import React, { useState, useEffect, useCallback, createContext, useContext, useRef, useMemo, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
// SignatureCanvas eliminado en v1.0.1 - firma ya no requerida
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Truck, Package, Users, Building2, LogOut, Info, Menu, X, 
  Plus, Eye, UserCheck, DollarSign, Clock, CheckCircle, User,
  MapPin, BarChart3, AlertCircle, Camera as CameraIcon, CreditCard,
  TrendingUp, Calendar, Receipt, Euro, Check, XCircle, Play, Navigation,
  Snowflake, Thermometer, ArrowUp, Send, Inbox, FileText, Phone, Bell,
  RefreshCw, Pause, ChevronLeft, ChevronRight, Loader2, Mail, StopCircle,
  Activity, Search
} from "lucide-react";
import { Camera, CameraResultType, CameraSource, CameraDirection } from '@capacitor/camera';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

// Initialize StatusBar for Android (prevents notch/punch-hole overlap)
const initializeStatusBar = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: '#171717' });
      await StatusBar.setStyle({ style: Style.Light });
    } catch (e) {
      console.log('StatusBar configuration not available:', e);
    }
  }
};
initializeStatusBar();

// ==================== API CONFIGURATION ====================
// Use relative URL if BACKEND_URL is same origin, otherwise use full URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// ==================== AXIOS GLOBAL CONFIG ====================
// Create axios instance with proper configuration
axios.defaults.baseURL = BACKEND_URL;
axios.defaults.timeout = 15000; // 15 second timeout
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Generate unique request ID
const generateRequestId = () => Math.random().toString(36).substring(2, 10);

// Request interceptor
axios.interceptors.request.use(
  (config) => {
    const requestId = generateRequestId();
    config.metadata = { startTime: Date.now(), requestId };
    config.headers['X-Request-ID'] = requestId;
    
    // Add auth token if available
    const token = localStorage.getItem("kargaexpress_token");
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    console.log(`[API:${requestId}] ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('[API] Request setup error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
axios.interceptors.response.use(
  (response) => {
    const { startTime, requestId } = response.config.metadata || {};
    const duration = Date.now() - (startTime || Date.now());
    console.log(`[API:${requestId}] ${response.config.method?.toUpperCase()} ${response.config.url} -> ${response.status} (${duration}ms)`);
    return response;
  },
  (error) => {
    const { startTime, requestId } = error.config?.metadata || {};
    const duration = Date.now() - (startTime || Date.now());
    const status = error.response?.status || 'NETWORK_ERROR';
    const url = error.config?.url || 'unknown';
    
    console.error(`[API:${requestId}] ${error.config?.method?.toUpperCase()} ${url} -> ${status} (${duration}ms)`, error.message);
    
    // Categorize errors for user-friendly messages
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      error.isTimeout = true;
      error.userMessage = 'La solicitud tardó demasiado. Intenta de nuevo.';
    } else if (!error.response) {
      error.isNetworkError = true;
      error.userMessage = 'Error de conexión. Verifica tu internet.';
    } else if (error.response.status === 401) {
      error.userMessage = 'Sesión expirada. Por favor, inicia sesión de nuevo.';
      // Clear invalid session
      localStorage.removeItem("kargaexpress_token");
      localStorage.removeItem("karga_user");
    } else if (error.response.status === 504) {
      error.userMessage = 'El servidor tardó demasiado en responder. Intenta de nuevo.';
    } else if (error.response.status >= 500) {
      error.userMessage = 'Error del servidor. Intenta de nuevo en unos momentos.';
    } else {
      error.userMessage = error.response?.data?.detail || 'Error desconocido';
    }
    
    return Promise.reject(error);
  }
);

// ==================== ERROR BOUNDARY ====================
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-800 mb-2">Algo salió mal</h1>
            <p className="text-slate-600 mb-4">Ha ocurrido un error inesperado. Por favor, recarga la página.</p>
            <Button 
              onClick={() => window.location.reload()} 
              style={{ backgroundColor: '#B4A798' }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Recargar página
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ==================== INITIALIZE AUTH HEADER ====================
// Set auth header from localStorage on app load (before any components mount)
const savedToken = localStorage.getItem("kargaexpress_token");
if (savedToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
  console.log('[Init] Auth header set from localStorage');
}

// ==================== PAGINATION COMPONENT ====================
const Pagination = React.memo(({ page, pages, total, onPageChange, loading }) => {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <span className="text-sm text-slate-500">{total} resultados</span>
      <div className="flex items-center gap-2">
        <Button 
          size="sm" 
          variant="outline" 
          onClick={() => onPageChange(page - 1)} 
          disabled={page <= 1 || loading}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm">Página {page} de {pages}</span>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={() => onPageChange(page + 1)} 
          disabled={page >= pages || loading}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
});

// ==================== LOADING BUTTON ====================
const LoadingButton = React.memo(({ loading, children, disabled, ...props }) => (
  <Button {...props} disabled={disabled || loading}>
    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
    {children}
  </Button>
));

// ==================== CONTEXTS ====================
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true); // Start true - always validate on mount
  const [authError, setAuthError] = useState(null);
  const [sessionValidated, setSessionValidated] = useState(false);

  // Clear all auth state completely
  const clearAuth = useCallback(() => {
    console.log('[Auth] Clearing all auth state');
    localStorage.removeItem("kargaexpress_token");
    localStorage.removeItem("karga_user");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setAuthError(null);
    setSessionValidated(false);
  }, []);

  // Validate session on mount - ALWAYS call /auth/me to verify
  useEffect(() => {
    const validateSession = async () => {
      const savedToken = localStorage.getItem("kargaexpress_token");
      
      if (!savedToken) {
        console.log('[Auth] No saved token, session clear');
        setLoading(false);
        setSessionValidated(true);
        return;
      }
      
      console.log('[Auth] Validating saved session...');
      axios.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
      
      try {
        const r = await axios.get(`${API}/auth/me`);
        console.log('[Auth] Session valid for:', r.data.email);
        setToken(savedToken);
        setUser(r.data);
        localStorage.setItem("karga_user", JSON.stringify(r.data));
        setAuthError(null);
      } catch (err) {
        console.error('[Auth] Session invalid:', err.message);
        // Clear invalid session
        clearAuth();
        if (err.response?.status === 401) {
          setAuthError('Tu sesión ha expirado. Por favor, inicia sesión de nuevo.');
        }
      } finally {
        setLoading(false);
        setSessionValidated(true);
      }
    };
    
    validateSession();
  }, [clearAuth]);

  const login = async (email, password) => {
    console.log('[Auth] Login attempt for:', email);
    const r = await axios.post(`${API}/auth/login`, { email, password });
    console.log('[Auth] Login success for:', r.data.user.email);
    
    // Store credentials
    const newToken = r.data.access_token;
    localStorage.setItem("kargaexpress_token", newToken);
    localStorage.setItem("karga_user", JSON.stringify(r.data.user));
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    
    setToken(newToken);
    setUser(r.data.user);
    setAuthError(null);
    setSessionValidated(true);
    return { user: r.data.user, driver_approval_status: r.data.driver_approval_status };
  };

  const register = async (email, password, name, role, acceptPrivacy) => {
    console.log('[Auth] Register attempt for:', email);
    const r = await axios.post(`${API}/auth/register`, { email, password, name, role, accept_privacy: acceptPrivacy });
    console.log('[Auth] Register success for:', r.data.user.email);
    localStorage.setItem("kargaexpress_token", r.data.access_token);
    localStorage.setItem("karga_user", JSON.stringify(r.data.user));
    axios.defaults.headers.common["Authorization"] = `Bearer ${r.data.access_token}`;
    setToken(r.data.access_token);
    setUser(r.data.user);
    setAuthError(null);
    setSessionValidated(true);
    return r.data.user;
  };

  const logout = useCallback(() => {
    console.log('[Auth] User logout');
    clearAuth();
    // Force redirect to login
    window.location.href = '/login';
  }, [clearAuth]);

  // Show loading spinner while validating session
  if (loading || !sessionValidated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-karga-dark mb-4" />
          <p className="text-slate-600">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={{ user, token, loading, authError, login, register, logout, clearAuth }}>{children}</AuthContext.Provider>;
};

// ==================== TOAST SYSTEM ====================
const ToastContext = createContext(null);
const useToast = () => useContext(ToastContext);

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const addToast = (message, type = "info") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  };
  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="fixed bottom-4 right-4 space-y-2 z-50">
        {toasts.map(t => (
          <div key={t.id} className={`px-4 py-3 rounded-lg shadow-lg text-white ${t.type === "error" ? "bg-red-500" : t.type === "success" ? "bg-green-500" : "bg-blue-500"}`}>
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// ==================== HELPERS ====================
const StatusPill = ({ status, role = "COMPANY" }) => {
  const colors = {
    REQUESTED: "bg-yellow-100 text-yellow-800",
    AUTO_CREATED: "bg-purple-100 text-purple-800",
    PRICED: "bg-blue-100 text-blue-800",
    COMPANY_ACCEPTED: "bg-green-100 text-green-800",
    COMPANY_REJECTED: "bg-red-100 text-red-800",
    OFFER_PUBLISHED: "bg-purple-100 text-purple-800",
    DRIVER_ASSIGNED: "bg-indigo-100 text-indigo-800",
    PICKED_UP: "bg-cyan-100 text-cyan-800",
    IN_TRANSIT: "bg-orange-100 text-orange-800",
    DELIVERED: "bg-emerald-100 text-emerald-800",
    CANCELLED: "bg-gray-100 text-gray-800",
  };
  
  // Labels for COMPANY view
  const companyLabels = {
    REQUESTED: "Solicitud enviada",
    AUTO_CREATED: "Recurrente (pendiente precio)",
    PRICED: "Presupuesto pendiente de aceptación",
    COMPANY_ACCEPTED: "Presupuesto aceptado – Buscando conductor",
    COMPANY_REJECTED: "Rechazado",
    OFFER_PUBLISHED: "Buscando conductor",
    DRIVER_ASSIGNED: "Conductor asignado",
    PICKED_UP: "Mercancía recogida",
    IN_TRANSIT: "En tránsito",
    DELIVERED: "Envío entregado",
    CANCELLED: "Cancelado",
  };
  
  // Labels for ADMIN view
  const adminLabels = {
    REQUESTED: "Pendiente de cotizar",
    AUTO_CREATED: "Recurrente – Pendiente de precio",
    PRICED: "Presupuesto enviado al cliente",
    COMPANY_ACCEPTED: "Cliente aceptó – Publicable a conductores",
    COMPANY_REJECTED: "Rechazado por cliente",
    OFFER_PUBLISHED: "Publicado a conductores",
    DRIVER_ASSIGNED: "Conductor asignado",
    PICKED_UP: "Recogido",
    IN_TRANSIT: "En tránsito",
    DELIVERED: "Entregado",
    CANCELLED: "Cancelado",
  };
  
  // Labels for DRIVER view
  const driverLabels = {
    OFFER_PUBLISHED: "Disponible",
    DRIVER_ASSIGNED: "Asignado",
    PICKED_UP: "Recogido",
    IN_TRANSIT: "En tránsito",
    DELIVERED: "Entregado",
    CANCELLED: "Cancelado",
  };
  
  const labels = role === "ADMIN" ? adminLabels : role === "DRIVER" ? driverLabels : companyLabels;
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-gray-100"}`}>{labels[status] || status}</span>;
};

// Logo component - Uses official KargaExpress logos (light and dark variants)
const LOGO_LIGHT = "/kargaexpress-logo-black-transparent.png";           // Black logo with transparency for light backgrounds
const LOGO_DARK = "/kargaexpress-logo-horizontal-light.png";              // White logo for dark backgrounds (footer)
const LOGO_HORIZONTAL = "/kargaexpress-logo-horizontal-transparent.png";  // Horizontal with text for header

const KargaLogo = ({ size = "md", variant = "light", className = "" }) => {
  // Direct pixel heights - larger for better presence
  const sizes = {
    navbar: "56px",      // Header/navbar - more presence
    sidebar: "40px",     // Sidebar (icon only)
    md: "80px",          // Login/Register
    splash: "130px",     // Splash screen
    footer: "52px",      // Footer - horizontal logo
  };
  
  // Choose the right logo based on variant and size
  let logoSrc;
  if (variant === "dark") {
    logoSrc = LOGO_DARK;  // White logo for dark backgrounds
  } else if (size === "navbar") {
    logoSrc = LOGO_HORIZONTAL;  // Full horizontal logo for header
  } else {
    logoSrc = LOGO_LIGHT;  // Black logo for light backgrounds
  }
  
  const height = sizes[size] || sizes.md;
  
  return (
    <img 
      src={logoSrc} 
      alt="KargaExpress" 
      className={`object-contain ${className}`}
      style={{ 
        height, 
        width: "auto", 
        maxWidth: size === "footer" ? "200px" : "220px"
      }}
    />
  );
};

const PaymentPill = ({ status, type = "company" }) => {
  const colors = {
    UNPAID: "bg-yellow-100 text-yellow-800",
    PAID: "bg-green-100 text-green-800",
    OVERDUE: "bg-red-100 text-red-800",
    NOT_PAID: "bg-yellow-100 text-yellow-800",
  };
  const labels = {
    UNPAID: "Pendiente",
    PAID: "Pagado",
    OVERDUE: "Vencido",
    NOT_PAID: "Pendiente",
  };
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-gray-100"}`}>{labels[status] || status}</span>;
};

const VehicleBadge = ({ category }) => {
  const labels = { SMALL: "Pequeño", MEDIUM: "Mediano", LARGE: "Grande" };
  return <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs">{labels[category] || category}</span>;
};

const RequirementBadges = ({ isothermal, freezer, liftgate }) => (
  <div className="flex gap-1 flex-wrap">
    {isothermal && <span className="px-2 py-0.5 bg-blue-100 text-karga-carbon rounded text-xs flex items-center gap-1"><Thermometer className="h-3 w-3"/>Frío</span>}
    {freezer && <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs flex items-center gap-1"><Snowflake className="h-3 w-3"/>Congelado</span>}
    {liftgate && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs flex items-center gap-1"><ArrowUp className="h-3 w-3"/>Plataforma</span>}
  </div>
);

// Schedule Info component - shows date/time and recurring badge
const ScheduleInfo = ({ shipment, showRecurringPattern = false }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      const options = { weekday: 'short', day: 'numeric', month: 'short' };
      return date.toLocaleDateString('es-ES', options);
    } catch { return null; }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch { return null; }
  };

  // Check if recurring (has recurring_template_id or is_recurring flag)
  const isRecurring = shipment?.recurring_template_id || shipment?.is_recurring;
  
  // Get schedule date/time
  const scheduleDate = shipment?.schedule_datetime || shipment?.scheduled_date || shipment?.pickup_datetime;
  const scheduledTime = shipment?.scheduled_time || shipment?.time_window || (scheduleDate ? formatTime(scheduleDate) : null);
  
  // Exact pickup time (HH:MM format) - takes priority
  const exactPickupTime = shipment?.pickup_time;
  const exactDeliveryTime = shipment?.delivery_time;
  
  // For recurring templates with pattern
  const daysOfWeek = shipment?.days_of_week;
  const getDaysPattern = (days) => {
    if (!days || !Array.isArray(days)) return null;
    const sorted = [...days].sort((a,b) => a-b);
    // L-V = [0,1,2,3,4], L-S = [0,1,2,3,4,5]
    if (sorted.length === 5 && sorted.join(',') === '0,1,2,3,4') return 'L-V';
    if (sorted.length === 6 && sorted.join(',') === '0,1,2,3,4,5') return 'L-S';
    if (sorted.length === 7) return 'L-D';
    const dayNames = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
    return sorted.map(d => dayNames[d]).join('-');
  };

  const daysPattern = getDaysPattern(daysOfWeek);
  const formattedDate = formatDate(scheduleDate);

  if (!formattedDate && !isRecurring && !daysPattern && !exactPickupTime) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      {isRecurring && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
          🔁 Recurrente
        </span>
      )}
      {showRecurringPattern && daysPattern ? (
        <span className="inline-flex items-center gap-1 text-slate-600">
          <Calendar className="h-3.5 w-3.5"/>
          <span className="font-medium">{daysPattern}</span>
          {(exactPickupTime || scheduledTime) && <span>• {exactPickupTime || scheduledTime}</span>}
        </span>
      ) : formattedDate ? (
        <span className="inline-flex items-center gap-1 text-slate-600">
          <Calendar className="h-3.5 w-3.5"/>
          <span>{formattedDate}</span>
          {exactPickupTime && (
            <span className="inline-flex items-center gap-1 text-karga-dark font-medium">
              • ⏰ {exactPickupTime}
            </span>
          )}
          {!exactPickupTime && scheduledTime && <span>• {scheduledTime}</span>}
        </span>
      ) : exactPickupTime ? (
        <span className="inline-flex items-center gap-1 text-karga-dark">
          ⏰ <span className="font-medium">{exactPickupTime}</span>
          {exactDeliveryTime && <span className="text-slate-500">→ {exactDeliveryTime}</span>}
        </span>
      ) : scheduledTime ? (
        <span className="inline-flex items-center gap-1 text-slate-600">
          <Clock className="h-3.5 w-3.5"/>
          <span>{scheduledTime}</span>
        </span>
      ) : null}
    </div>
  );
};

// ==================== LANDING PAGE ====================
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "ADMIN" ? "/admin" : user.role === "COMPANY" ? "/company" : "/driver");
  }, [user, navigate]);

  const images = {
    hero: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80",
    warehouse: "https://images.unsplash.com/photo-1553413077-190dd305871c?w=800&q=80",
    van1: "https://images.pexels.com/photos/6169668/pexels-photo-6169668.jpeg?auto=compress&cs=tinysrgb&w=600",
    van2: "https://images.pexels.com/photos/6169659/pexels-photo-6169659.jpeg?auto=compress&cs=tinysrgb&w=600",
    van3: "https://images.pexels.com/photos/4391478/pexels-photo-4391478.jpeg?auto=compress&cs=tinysrgb&w=600",
    van4: "https://images.pexels.com/photos/4391486/pexels-photo-4391486.jpeg?auto=compress&cs=tinysrgb&w=600",
    loading: "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=800&q=80",
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F2ED' }}>
      {/* Navigation - with safe-area padding for Android notch/status bar/punch-hole camera */}
      <nav className="bg-white border-b" style={{ 
        borderColor: '#E5E7EB',
        paddingTop: 'max(env(safe-area-inset-top, 0px), 24px)'
      }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
              <KargaLogo size="navbar" variant="light" />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#servicios" className="text-sm font-medium hover:opacity-80" style={{ color: '#746A60' }}>Servicios</a>
              <a href="#nosotros" className="text-sm font-medium hover:opacity-80" style={{ color: '#746A60' }}>Nosotros</a>
              <a href="#flota" className="text-sm font-medium hover:opacity-80" style={{ color: '#746A60' }}>Flota</a>
              <a href="#contacto" className="text-sm font-medium hover:opacity-80" style={{ color: '#746A60' }}>Contacto</a>
              <Button onClick={() => navigate("/login")} style={{ backgroundColor: '#171717', color: '#F5F2ED' }} className="hover:opacity-90">Entrar a la plataforma</Button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">
              {mobileMenuOpen ? <X className="h-6 w-6" style={{ color: '#171717' }} /> : <Menu className="h-6 w-6" style={{ color: '#171717' }} />}
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t" style={{ borderColor: '#E5E7EB' }}>
              <div className="flex flex-col gap-4">
                <a href="#servicios" className="text-sm font-medium" style={{ color: '#746A60' }}>Servicios</a>
                <a href="#nosotros" className="text-sm font-medium" style={{ color: '#746A60' }}>Nosotros</a>
                <a href="#flota" className="text-sm font-medium" style={{ color: '#746A60' }}>Flota</a>
                <a href="#contacto" className="text-sm font-medium" style={{ color: '#746A60' }}>Contacto</a>
                <Button onClick={() => navigate("/login")} className="w-full" style={{ backgroundColor: '#171717', color: '#F5F2ED' }}>Entrar a la plataforma</Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#F5F2ED' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 text-sm font-medium rounded-full mb-6" style={{ backgroundColor: '#B4A798', color: '#FFFFFF' }}>Logística en Galicia</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ color: '#171717' }}>
                Tu carga, <br/><span style={{ color: '#B4A798' }}>nuestra prioridad</span>
              </h1>
              <p className="text-lg mb-8" style={{ color: '#746A60' }}>
                Más de 25 años conectando empresas gallegas con soluciones de transporte fiables, rápidas y con precios justos. Sin algoritmos, con personas.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={() => navigate("/login")} style={{ backgroundColor: '#171717', color: '#F5F2ED' }} className="px-8 hover:opacity-90">Solicitar Presupuesto</Button>
                <Button size="lg" variant="outline" onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })} 
                  style={{ borderColor: '#B4A798', color: '#171717', backgroundColor: 'transparent' }} className="hover:bg-white/50">Hablar con Nosotros</Button>
              </div>
            </div>
            <div className="relative">
              <img src={images.hero} alt="Furgoneta KargaExpress" className="rounded-2xl shadow-xl w-full h-auto object-cover" style={{ maxHeight: '500px' }} />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 hidden lg:block" style={{ border: '1px solid #E5E7EB' }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#DCFCE7' }}>
                    <CheckCircle className="h-6 w-6" style={{ color: '#16A34A' }} />
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: '#171717' }}>+2,500 envíos</p>
                    <p className="text-sm" style={{ color: '#746A60' }}>completados este año</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conectando Galicia */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#171717' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">Conectando toda <span style={{ color: '#B4A798' }}>Galicia</span></h2>
            <p className="text-lg mb-12 max-w-2xl mx-auto" style={{ color: '#94A3B8' }}>
              Desde A Coruña hasta Ourense, de Lugo a Pontevedra. Cubrimos cada rincón de Galicia con una red de conductores locales.
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-3xl mx-auto">
              <div className="bg-white/10 rounded-xl p-4"><p className="text-3xl font-bold text-white">4</p><p style={{ color: '#94A3B8' }}>Provincias</p></div>
              <div className="bg-white/10 rounded-xl p-4"><p className="text-3xl font-bold text-white">24h</p><p style={{ color: '#94A3B8' }}>Express</p></div>
              <div className="bg-white/10 rounded-xl p-4"><p className="text-3xl font-bold text-white">50+</p><p style={{ color: '#94A3B8' }}>Conductores</p></div>
              <div className="bg-white/10 rounded-xl p-4"><p className="text-3xl font-bold text-white">98%</p><p style={{ color: '#94A3B8' }}>A tiempo</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* Qué hacemos */}
      <section id="servicios" className="py-16 lg:py-24" style={{ backgroundColor: '#F5F2ED' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#171717' }}>Qué hacemos en <span style={{ color: '#B4A798' }}>KargaExpress</span></h2>
            <p className="text-lg max-w-3xl mx-auto mb-6" style={{ color: '#746A60' }}>
              Resolvemos envíos puntuales y recurrentes para empresas que necesitan mover mercancía, pero no tiene sentido disponer de una furgoneta propia.
            </p>
            <div className="max-w-3xl mx-auto p-4 rounded-xl" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }}>
              <p className="text-sm font-medium" style={{ color: '#92400E' }}>
                KargaExpress no busca rutas fijas ni volúmenes estándar. No somos un servicio de paletería ni de envíos masivos.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 hover:shadow-lg transition-shadow" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: '#F5F2ED' }}>
                <Package className="h-7 w-7" style={{ color: '#171717' }} />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#171717' }}>Envíos Puntuales</h3>
              <p style={{ color: '#746A60' }}>Un envío hoy, otro la semana que viene. Sin compromisos de volumen ni frecuencia mínima.</p>
            </Card>
            <Card className="p-6 hover:shadow-lg transition-shadow" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: '#DCFCE7' }}>
                <Calendar className="h-7 w-7" style={{ color: '#16A34A' }} />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#171717' }}>Recurrentes Programados</h3>
              <p style={{ color: '#746A60' }}>Entregas cada martes, recogidas semanales. Organizamos tu logística sin que contrates conductor.</p>
            </Card>
            <Card className="p-6 hover:shadow-lg transition-shadow" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: '#FEF3C7' }}>
                <Clock className="h-7 w-7" style={{ color: '#D97706' }} />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#171717' }}>Urgentes Same-Day</h3>
              <p style={{ color: '#746A60' }}>Entregas en el mismo día para situaciones críticas. Disponibilidad inmediata.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Quiénes somos */}
      <section id="nosotros" className="py-16 lg:py-24" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img src={images.warehouse} alt="Almacén KargaExpress" className="rounded-2xl shadow-lg w-full h-auto object-cover" style={{ maxHeight: '450px' }} />
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block px-3 py-1 text-sm font-medium rounded-full mb-6" style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}>Desde 1999</span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: '#171717' }}>25 años moviendo <span style={{ color: '#B4A798' }}>Galicia</span></h2>
              <p className="text-lg mb-6" style={{ color: '#746A60' }}>
                KargaExpress nació de la experiencia real en logística. No somos una startup tecnológica. Somos transportistas que han abrazado la tecnología.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3"><CheckCircle className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: '#16A34A' }} /><span style={{ color: '#746A60' }}>Conocimiento de las rutas de Galicia</span></li>
                <li className="flex items-start gap-3"><CheckCircle className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: '#16A34A' }} /><span style={{ color: '#746A60' }}>Red de conductores profesionales</span></li>
                <li className="flex items-start gap-3"><CheckCircle className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: '#16A34A' }} /><span style={{ color: '#746A60' }}>Atención personalizada, siempre con personas</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Flota */}
      <section id="flota" className="py-16 lg:py-24" style={{ backgroundColor: '#F5F2ED' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#171717' }}>Furgones para <span style={{ color: '#B4A798' }}>cada necesidad</span></h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#746A60' }}>Flota exclusiva de furgones adaptados a diferentes tipos de carga y temperatura</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="overflow-hidden hover:shadow-lg transition-shadow" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
              <img src={images.van1} alt="Furgón estándar" className="w-full h-40 object-cover" />
              <div className="p-5">
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#171717' }}>Furgones Estándar</h3>
                <p className="text-sm mb-3" style={{ color: '#B4A798' }}>Pequeño · Mediano · Grande</p>
                <p className="text-sm" style={{ color: '#746A60' }}>Desde 3m³ hasta 20m³. Ideales para mercancía seca.</p>
              </div>
            </Card>
            <Card className="overflow-hidden hover:shadow-lg transition-shadow" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
              <img src={images.van2} alt="Furgón con frío" className="w-full h-40 object-cover" />
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold" style={{ color: '#171717' }}>Furgones con Frío</h3>
                  <span className="px-2 py-0.5 text-xs font-medium rounded" style={{ backgroundColor: '#DBEAFE', color: '#1D4ED8' }}>+2°C a +8°C</span>
                </div>
                <p className="text-sm" style={{ color: '#746A60' }}>Isotermos con equipo de frío para temperatura controlada.</p>
              </div>
            </Card>
            <Card className="overflow-hidden hover:shadow-lg transition-shadow" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
              <img src={images.van3} alt="Furgón congelación" className="w-full h-40 object-cover" />
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold" style={{ color: '#171717' }}>Congelación</h3>
                  <span className="px-2 py-0.5 text-xs font-medium rounded" style={{ backgroundColor: '#E0E7FF', color: '#4338CA' }}>-18°C a -25°C</span>
                </div>
                <p className="text-sm" style={{ color: '#746A60' }}>Furgones para congelados. Cadena de frío garantizada.</p>
              </div>
            </Card>
            <Card className="overflow-hidden hover:shadow-lg transition-shadow" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
              <img src={images.van4} alt="Furgón con plataforma" className="w-full h-40 object-cover" />
              <div className="p-5">
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#171717' }}>Plataforma Elevadora</h3>
                <p className="text-sm mb-3" style={{ color: '#16A34A' }}>Carga/descarga fácil</p>
                <p className="text-sm" style={{ color: '#746A60' }}>Con plataforma hidráulica para palets y mercancía pesada.</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Así de sencillo */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#171717' }}>Así de <span style={{ color: '#B4A798' }}>sencillo</span></h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#746A60' }}>Tres pasos para mover tu mercancía sin complicaciones</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center text-2xl font-bold" style={{ backgroundColor: '#F5F2ED', color: '#746A60' }}>1</div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#171717' }}>Solicita</h3>
              <p style={{ color: '#746A60' }}>Cuéntanos qué necesitas mover. Por WhatsApp, llamada o desde la plataforma.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center text-2xl font-bold" style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}>2</div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#171717' }}>Confirmamos</h3>
              <p style={{ color: '#746A60' }}>Te damos precio en menos de 1 hora. Sin sorpresas, precio cerrado.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center text-2xl font-bold" style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}>3</div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#171717' }}>Entregamos</h3>
              <p style={{ color: '#746A60' }}>Recogemos y entregamos. Te avisamos en cada paso con prueba fotográfica.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bloque diferencial */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#171717' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">Precios <span style={{ color: '#B4A798' }}>justos</span>, no algoritmos</h2>
              <p className="text-lg mb-8" style={{ color: '#94A3B8' }}>
                En KargaExpress no usamos algoritmos que inflan precios. Cada presupuesto lo calcula una persona que entiende tu necesidad.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4 bg-white/10 rounded-xl p-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/20"><Euro className="h-6 w-6 text-white" /></div>
                  <div><p className="font-semibold text-white">Precio fijo garantizado</p><p className="text-sm" style={{ color: '#94A3B8' }}>Lo que te decimos es lo que pagas</p></div>
                </div>
                <div className="flex items-center gap-4 bg-white/10 rounded-xl p-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/20"><Users className="h-6 w-6 text-white" /></div>
                  <div><p className="font-semibold text-white">Atención humana</p><p className="text-sm" style={{ color: '#94A3B8' }}>Hablas con personas, no con bots</p></div>
                </div>
                <div className="flex items-center gap-4 bg-white/10 rounded-xl p-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/20"><Clock className="h-6 w-6 text-white" /></div>
                  <div><p className="font-semibold text-white">Respuesta rápida</p><p className="text-sm" style={{ color: '#94A3B8' }}>Presupuesto en menos de 1 hora</p></div>
                </div>
              </div>
            </div>
            <div><img src={images.loading} alt="Muelle de carga" className="rounded-2xl shadow-xl w-full h-auto object-cover" style={{ maxHeight: '450px' }} /></div>
          </div>
        </div>
      </section>

      {/* Trabaja con nosotros - Drivers */}
      <section id="trabaja" className="py-16 lg:py-24" style={{ backgroundColor: '#EFF6FF' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#171717' }}>¿Eres transportista autónomo?</h2>
            <p className="text-lg mb-8" style={{ color: '#746A60' }}>
              Únete a nuestra red de conductores y accede a nuevas oportunidades de trabajo en Galicia. 
              Sin cuotas mensuales, solo ganas cuando trabajas.
            </p>
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: '#DCFCE7' }}>
                  <Euro className="h-6 w-6" style={{ color: '#16A34A' }}/>
                </div>
                <p className="font-medium" style={{ color: '#171717' }}>Pagos puntuales</p>
                <p className="text-sm text-slate-500">Liquidación mensual</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F5F2ED' }}>
                  <Calendar className="h-6 w-6" style={{ color: '#B4A798' }}/>
                </div>
                <p className="font-medium" style={{ color: '#171717' }}>Flexibilidad total</p>
                <p className="text-sm text-slate-500">Elige tus horarios</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
                  <Truck className="h-6 w-6" style={{ color: '#D97706' }}/>
                </div>
                <p className="font-medium" style={{ color: '#171717' }}>Tu vehículo</p>
                <p className="text-sm text-slate-500">Trabajas con tu furgoneta</p>
              </div>
            </div>
            <Button size="lg" onClick={() => navigate("/register/driver")} style={{ backgroundColor: '#B4A798', color: '#FFFFFF' }} className="px-8">
              Regístrate como conductor
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F5F2ED' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: '#171717' }}>¿Listo para mover tu mercancía?</h2>
          <p className="text-lg mb-8" style={{ color: '#746A60' }}>Accede a la plataforma y solicita presupuesto sin compromiso</p>
          <Button size="lg" onClick={() => navigate("/login")} className="px-10 py-4 text-lg" style={{ backgroundColor: '#B4A798', color: '#FFFFFF' }}>Acceder a la Plataforma</Button>
          <p className="mt-6 text-sm" style={{ color: '#64748B' }}>¿Prefieres hablar con nosotros? Contáctanos por WhatsApp en el footer</p>
        </div>
      </section>

      {/* Footer */}
      <footer id="contacto" style={{ backgroundColor: '#171717' }} className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="mb-4">
                {/* Logo horizontal real para fondo oscuro */}
                <KargaLogo size="footer" variant="dark" />
              </div>
              <p className="mb-4" style={{ color: '#94A3B8' }}>Soluciones de transporte para empresas en Galicia. Más de 25 años de experiencia.</p>
              <div className="space-y-2" style={{ color: '#94A3B8' }}>
                <a href="mailto:info@kargaexpress.app" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Mail className="h-4 w-4" />
                  <span>info@kargaexpress.app</span>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Contacto Directo</h4>
              <div className="space-y-3">
                <a href="https://wa.me/34673356802" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:opacity-80 transition-opacity" style={{ color: '#94A3B8' }}>
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="#25D366">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>Juan: +34 673 35 68 02</span>
                </a>
                <a href="https://wa.me/34680701206" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:opacity-80 transition-opacity" style={{ color: '#94A3B8' }}>
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="#25D366">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>Alejandro: +34 680 70 12 06</span>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Servicios</h4>
              <ul className="space-y-2" style={{ color: '#94A3B8' }}>
                <li>Envíos Puntuales</li><li>Recurrentes Programados</li><li>Urgentes Same-Day</li><li>Temperatura Controlada</li>
              </ul>
            </div>
          </div>
          <div className="border-t pt-8" style={{ borderColor: '#1E293B' }}>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm" style={{ color: '#64748B' }}>© {new Date().getFullYear()} KargaExpress. Todos los derechos reservados.</p>
              <div className="flex flex-wrap gap-4 justify-center">
                <button onClick={() => navigate("/login")} className="text-sm hover:opacity-80" style={{ color: '#64748B' }}>Entrar a la plataforma</button>
                <button onClick={() => navigate("/privacy-policy")} className="text-sm hover:opacity-80" style={{ color: '#64748B' }}>Privacidad</button>
                <button onClick={() => navigate("/terms")} className="text-sm hover:opacity-80" style={{ color: '#64748B' }}>Términos</button>
                <button onClick={() => navigate("/legal")} className="text-sm hover:opacity-80" style={{ color: '#64748B' }}>Aviso Legal</button>
                <button onClick={() => navigate("/cookies")} className="text-sm hover:opacity-80" style={{ color: '#64748B' }}>Cookies</button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ==================== SPLASH SCREEN ====================
const SplashScreen = () => {
  return (
    <div 
      className="min-h-screen flex items-center justify-center" 
      style={{ backgroundColor: '#171717' }}
      data-testid="splash-screen"
    >
      <KargaLogo size="splash" variant="dark" />
    </div>
  );
};

// ==================== LEGAL PAGES ====================
const LegalPageLayout = ({ title, children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F2ED' }}>
      <nav className="bg-white border-b sticky top-0 z-50" style={{ borderColor: '#E5E7EB' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="cursor-pointer" onClick={() => navigate("/")}>
            <KargaLogo size="navbar" variant="light" />
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/")}>Volver al inicio</Button>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-8" style={{ color: '#171717' }}>{title}</h1>
        <div className="prose prose-slate max-w-none">{children}</div>
      </div>
      <footer className="border-t py-8 mt-12" style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}>
        <div className="max-w-4xl mx-auto px-4 flex flex-wrap gap-6 justify-center text-sm" style={{ color: '#64748B' }}>
          <button onClick={() => navigate("/privacy-policy")} className="hover:underline">Política de Privacidad</button>
          <button onClick={() => navigate("/terms")} className="hover:underline">Términos y Condiciones</button>
          <button onClick={() => navigate("/legal")} className="hover:underline">Aviso Legal</button>
          <button onClick={() => navigate("/cookies")} className="hover:underline">Política de Cookies</button>
        </div>
      </footer>
    </div>
  );
};

const PrivacyPolicyPage = () => (
  <LegalPageLayout title="Política de Privacidad">
    <div className="space-y-6 text-slate-700">
      <p className="text-sm text-slate-500">Última actualización: Enero 2025</p>
      
      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Responsable del tratamiento</h2>
        <p>KargaExpress es responsable del tratamiento de los datos personales recogidos a través de esta plataforma de logística y transporte.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Datos que recogemos</h2>
        <p>Recogemos los siguientes datos según el tipo de usuario:</p>
        <h3 className="font-medium mt-4 mb-2">Para empresas (clientes):</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Razón social y datos fiscales (NIF/CIF)</li>
          <li>Dirección fiscal y de facturación</li>
          <li>Datos de contacto (nombre, email, teléfono)</li>
          <li>Direcciones de recogida y entrega de mercancías</li>
        </ul>
        <h3 className="font-medium mt-4 mb-2">Para conductores (autónomos):</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Nombre completo y datos fiscales (NIF)</li>
          <li>Dirección fiscal</li>
          <li>Datos de contacto (email, teléfono)</li>
          <li>Información del vehículo (categoría, matrícula, equipamiento)</li>
          <li>Fotografías de prueba de entrega (POD)</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">3. Finalidad del tratamiento</h2>
        <p>Utilizamos tus datos para:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li><strong>Gestión operativa:</strong> Coordinar recogidas y entregas, asignar conductores, gestionar rutas y comunicaciones relacionadas con los envíos.</li>
          <li><strong>Facturación y pagos:</strong> Emitir facturas, gestionar cobros a empresas y pagos a conductores.</li>
          <li><strong>Prueba de entrega (POD):</strong> Las fotografías de recogida y entrega se utilizan como justificante del servicio realizado.</li>
          <li><strong>Comunicaciones:</strong> Notificaciones sobre el estado de envíos, presupuestos y actualizaciones del servicio.</li>
          <li><strong>Mejora del servicio:</strong> Análisis interno para mejorar la plataforma.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Base legal</h2>
        <p>El tratamiento de datos se basa en:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>La ejecución del contrato de servicios de transporte</li>
          <li>El cumplimiento de obligaciones legales (fiscales, mercantiles)</li>
          <li>Tu consentimiento expreso al registrarte</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Conservación de datos</h2>
        <p>Conservamos tus datos durante la relación comercial y posteriormente según las obligaciones legales:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Datos fiscales y facturas: 5 años</li>
          <li>Pruebas de entrega (fotos): 2 años</li>
          <li>Datos de cuenta: hasta que solicites la baja</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Tus derechos</h2>
        <p>Puedes ejercer tus derechos de acceso, rectificación, supresión, portabilidad y limitación del tratamiento contactando con nosotros. También puedes presentar una reclamación ante la Agencia Española de Protección de Datos.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Contacto</h2>
        <p>Para cualquier consulta sobre privacidad, contacta con nosotros:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Email: <a href="mailto:info@kargaexpress.app" className="text-karga-dark hover:underline">info@kargaexpress.app</a></li>
          <li>Web: <a href="https://kargaexpress.app" className="text-karga-dark hover:underline">https://kargaexpress.app</a></li>
        </ul>
      </section>
    </div>
  </LegalPageLayout>
);

const TermsPage = () => (
  <LegalPageLayout title="Términos y Condiciones">
    <div className="space-y-6 text-slate-700">
      <p className="text-sm text-slate-500">Última actualización: Enero 2025</p>
      
      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Objeto</h2>
        <p>Estos términos regulan el uso de la plataforma KargaExpress, un servicio de intermediación logística que conecta empresas con necesidades de transporte con conductores autónomos.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Usuarios</h2>
        <h3 className="font-medium mt-4 mb-2">Empresas (clientes):</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Pueden solicitar servicios de transporte a través de la plataforma</li>
          <li>Reciben presupuestos que deben aceptar o rechazar</li>
          <li>Se comprometen a proporcionar información veraz sobre la mercancía</li>
          <li>Aceptan las condiciones de pago establecidas</li>
        </ul>
        <h3 className="font-medium mt-4 mb-2">Conductores (autónomos):</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Deben estar dados de alta como autónomos</li>
          <li>Son responsables de su vehículo, seguros y permisos</li>
          <li>Se comprometen a realizar las entregas aceptadas</li>
          <li>Deben proporcionar prueba de entrega (fotografías)</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">3. Funcionamiento del servicio</h2>
        <ol className="list-decimal pl-6 space-y-1">
          <li>La empresa solicita un envío indicando origen, destino y características</li>
          <li>KargaExpress envía un presupuesto con el precio del servicio</li>
          <li>Si la empresa acepta, se publica el trabajo a los conductores disponibles</li>
          <li>Un conductor acepta el trabajo y realiza el servicio</li>
          <li>El conductor proporciona prueba de entrega</li>
          <li>Se procede a la facturación y pago</li>
        </ol>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Precios y pagos</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Los precios indicados son sin IVA</li>
          <li>Las empresas reciben factura mensual con los servicios realizados</li>
          <li>Los conductores reciben liquidación mensual de sus trabajos</li>
          <li>KargaExpress actúa como intermediario, no como transportista</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Responsabilidades</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>El conductor es responsable de la mercancía durante el transporte</li>
          <li>La empresa es responsable de la información proporcionada sobre la mercancía</li>
          <li>KargaExpress facilita la conexión pero no es parte del contrato de transporte</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Prueba de entrega</h2>
        <p>El conductor debe proporcionar fotografías de:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Recogida de la mercancía</li>
          <li>Entrega de la mercancía</li>
          <li>Foto de entrega</li>
          <li>Foto del albarán</li>
        </ul>
        <p className="mt-2">Estas fotografías constituyen prueba del servicio realizado.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Cancelaciones</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Las empresas pueden rechazar presupuestos sin coste</li>
          <li>Una vez aceptado, la cancelación puede conllevar gastos</li>
          <li>Los conductores que no completen un trabajo aceptado pueden ser penalizados</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Modificaciones</h2>
        <p>KargaExpress puede modificar estos términos notificando a los usuarios con antelación razonable.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Contacto</h2>
        <p>Para cualquier consulta sobre estos términos:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Email: <a href="mailto:info@kargaexpress.app" className="text-karga-dark hover:underline">info@kargaexpress.app</a></li>
          <li>Web: <a href="https://kargaexpress.app" className="text-karga-dark hover:underline">https://kargaexpress.app</a></li>
        </ul>
      </section>
    </div>
  </LegalPageLayout>
);

const LegalNoticePage = () => (
  <LegalPageLayout title="Aviso Legal">
    <div className="space-y-6 text-slate-700">
      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Identificación</h2>
        <p>En cumplimiento del artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico, se informa:</p>
        <ul className="list-none mt-4 space-y-2">
          <li><strong>Denominación:</strong> KargaExpress</li>
          <li><strong>Dominio:</strong> kargaexpress.app</li>
          <li><strong>Actividad:</strong> Plataforma de intermediación logística</li>
          <li><strong>Email:</strong> <a href="mailto:info@kargaexpress.app" className="text-karga-dark hover:underline">info@kargaexpress.app</a></li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Objeto</h2>
        <p>Esta plataforma tiene como objeto facilitar la conexión entre empresas con necesidades de transporte y conductores autónomos disponibles para realizar dichos servicios.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">3. Condiciones de uso</h2>
        <p>El acceso y uso de esta plataforma implica la aceptación de las presentes condiciones. El usuario se compromete a:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Proporcionar información veraz y actualizada</li>
          <li>No utilizar la plataforma para fines ilícitos</li>
          <li>Mantener la confidencialidad de sus credenciales de acceso</li>
          <li>Respetar los derechos de otros usuarios</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Propiedad intelectual</h2>
        <p>Todos los contenidos de la plataforma (textos, imágenes, código, diseño) son propiedad de KargaExpress o de sus licenciantes y están protegidos por las leyes de propiedad intelectual.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Limitación de responsabilidad</h2>
        <p>KargaExpress actúa como intermediario tecnológico. No somos parte del contrato de transporte entre empresa y conductor. No nos hacemos responsables de:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Daños o pérdidas de mercancía durante el transporte</li>
          <li>Incumplimientos de los usuarios</li>
          <li>Interrupciones técnicas de la plataforma</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Legislación aplicable</h2>
        <p>Este aviso legal se rige por la legislación española. Para cualquier controversia, las partes se someten a los juzgados y tribunales de la ciudad donde KargaExpress tenga su domicilio social.</p>
      </section>
    </div>
  </LegalPageLayout>
);

const CookiePolicyPage = () => (
  <LegalPageLayout title="Política de Cookies">
    <div className="space-y-6 text-slate-700">
      <p className="text-sm text-slate-500">Última actualización: Enero 2025</p>
      
      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">1. ¿Qué son las cookies?</h2>
        <p>Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando visitas nuestra plataforma. Nos ayudan a recordar tus preferencias y mejorar tu experiencia.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Cookies que utilizamos</h2>
        
        <h3 className="font-medium mt-4 mb-2">Cookies técnicas (necesarias):</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Sesión de usuario:</strong> Mantienen tu sesión iniciada mientras usas la plataforma</li>
          <li><strong>Preferencias:</strong> Recuerdan tus ajustes (idioma, configuración)</li>
          <li><strong>Seguridad:</strong> Ayudan a proteger tu cuenta</li>
        </ul>

        <h3 className="font-medium mt-4 mb-2">Cookies de análisis (opcionales):</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Nos ayudan a entender cómo se usa la plataforma</li>
          <li>Permiten mejorar el servicio basándonos en datos agregados</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">3. Gestión de cookies</h2>
        <p>Puedes gestionar las cookies desde la configuración de tu navegador:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li><strong>Chrome:</strong> Configuración → Privacidad y seguridad → Cookies</li>
          <li><strong>Firefox:</strong> Opciones → Privacidad y seguridad → Cookies</li>
          <li><strong>Safari:</strong> Preferencias → Privacidad → Cookies</li>
          <li><strong>Edge:</strong> Configuración → Privacidad → Cookies</li>
        </ul>
        <p className="mt-4">Ten en cuenta que bloquear las cookies técnicas puede afectar al funcionamiento de la plataforma.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Almacenamiento local</h2>
        <p>Además de cookies, utilizamos el almacenamiento local del navegador (localStorage) para:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Mantener tu sesión iniciada</li>
          <li>Guardar preferencias de la aplicación</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Actualizaciones</h2>
        <p>Esta política puede actualizarse. Te recomendamos revisarla periódicamente.</p>
      </section>
    </div>
  </LegalPageLayout>
);

// ==================== LOGIN PAGE ====================
const LoginPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { addToast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      console.log('[Login] User already logged in, redirecting to:', user.role);
      navigate(user.role === "ADMIN" ? "/admin" : user.role === "COMPANY" ? "/company" : "/driver");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    console.log('[Login] Submitting login for:', email);
    
    // Safety timeout - reset loading state after 20s no matter what
    const safetyTimeout = setTimeout(() => {
      console.log('[Login] Safety timeout - resetting loading state');
      setLoading(false);
      setError("La solicitud tardó demasiado. Por favor, intenta de nuevo.");
    }, 20000);
    
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      clearTimeout(safetyTimeout);
      console.log('[Login] Response received:', r.data.user?.email);
      
      // Check if driver is pending approval BEFORE setting user state
      if (r.data.user.role === "DRIVER" && r.data.driver_approval_status === "PENDING_APPROVAL") {
        setError("Tu cuenta está pendiente de validación. Te avisaremos cuando esté activa.");
        setLoading(false);
        return;
      }
      
      // Check if driver is rejected
      if (r.data.user.role === "DRIVER" && r.data.driver_approval_status === "REJECTED") {
        setError("Tu solicitud de registro ha sido rechazada. Contacta con nosotros para más información.");
        setLoading(false);
        return;
      }
      
      // Store credentials
      console.log('[Login] Storing token and user');
      localStorage.setItem("kargaexpress_token", r.data.access_token);
      localStorage.setItem("karga_user", JSON.stringify(r.data.user));
      axios.defaults.headers.common["Authorization"] = `Bearer ${r.data.access_token}`;
      
      // Navigate to dashboard
      const dest = r.data.user.role === "ADMIN" ? "/admin" : r.data.user.role === "COMPANY" ? "/company" : "/driver";
      console.log('[Login] Navigating to:', dest);
      addToast(`Bienvenido, ${r.data.user.name}`, "success");
      
      // Use window.location for clean navigation
      window.location.href = dest;
    } catch (err) {
      clearTimeout(safetyTimeout);
      console.error('[Login] Error:', err.message);
      const errorMsg = err.userMessage || err.response?.data?.detail || "Error de autenticación. Intenta de nuevo.";
      setError(errorMsg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F2ED' }}>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <KargaLogo size="md" variant="light" />
          </div>
          <CardTitle>Iniciar Sesión</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
            </div>
            <div className="text-right">
              <button 
                type="button" 
                onClick={() => navigate("/forgot-password")} 
                className="text-sm hover:underline" 
                style={{ color: '#B4A798' }}
                data-testid="forgot-password-link"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
            <Button type="submit" className="w-full" disabled={loading} style={{ backgroundColor: '#B4A798' }} data-testid="login-submit">
              {loading ? "Accediendo..." : "Acceder"}
            </Button>
          </form>
          <div className="mt-6 pt-6 border-t" style={{ borderColor: '#E5E7EB' }}>
            <p className="text-sm text-center text-slate-600 mb-3">¿No tienes cuenta?</p>
            <div className="space-y-2">
              <Button variant="outline" className="w-full" onClick={() => navigate("/register/company")}>
                ¿Eres empresa? Regístrate
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate("/register/driver")}>
                ¿Eres autónomo? Regístrate aquí
              </Button>
            </div>
          </div>
          <div className="mt-4 text-center">
            <button onClick={() => navigate("/")} className="text-sm hover:underline" style={{ color: '#B4A798' }}>← Volver al inicio</button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== COMPANY REGISTRATION ====================
const CompanyRegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    company_name: "", legal_name: "", tax_id: "", billing_address: "",
    billing_email: "", billing_phone: "", contact_name: "", contact_phone: "",
    contact_email: "", password: "", password_confirm: "", main_city: "", notes: "",
    accept_privacy: false,
    accept_terms: false  // v1.1: Separate checkbox for billing terms
  });

  const handleChange = (field, value) => setForm({ ...form, [field]: value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.accept_privacy) {
      alert("Debe aceptar la política de privacidad");
      return;
    }
    if (!form.accept_terms) {
      alert("Debe aceptar las condiciones de uso y facturación");
      return;
    }
    if (form.password !== form.password_confirm) {
      alert("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register/company`, form);
      setSuccess(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Error al registrar");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F1F5F9' }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#DCFCE7' }}>
              <CheckCircle className="h-8 w-8" style={{ color: '#16A34A' }}/>
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#171717' }}>Cuenta creada correctamente</h2>
            <p className="text-slate-600 mb-6">Ya puedes solicitar envíos y recibir presupuestos desde la plataforma.</p>
            <Button onClick={() => navigate("/login")} style={{ backgroundColor: '#B4A798' }} className="w-full">
              Iniciar sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F1F5F9' }}>
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-6">
          <KargaLogo size="md" variant="light" />
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Registro de Empresa</CardTitle>
            <p className="text-sm text-slate-500">Complete los datos para crear su cuenta de empresa</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Details */}
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900 flex items-center gap-2"><Building2 className="h-4 w-4"/>Datos de la empresa</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Razón social *</Label>
                    <Input value={form.company_name} onChange={e => handleChange("company_name", e.target.value)} required/>
                  </div>
                  <div>
                    <Label>Razón social fiscal *</Label>
                    <Input value={form.legal_name} onChange={e => handleChange("legal_name", e.target.value)} required/>
                  </div>
                  <div>
                    <Label>NIF / CIF / VAT *</Label>
                    <Input value={form.tax_id} onChange={e => handleChange("tax_id", e.target.value)} required/>
                  </div>
                  <div>
                    <Label>Teléfono de facturación *</Label>
                    <Input value={form.billing_phone} onChange={e => handleChange("billing_phone", e.target.value)} required/>
                  </div>
                </div>
                <div>
                  <Label>Dirección fiscal completa *</Label>
                  <Textarea value={form.billing_address} onChange={e => handleChange("billing_address", e.target.value)} required rows={2}/>
                </div>
                <div>
                  <Label>Email de facturación *</Label>
                  <Input type="email" value={form.billing_email} onChange={e => handleChange("billing_email", e.target.value)} required/>
                </div>
              </div>

              {/* Contact Person */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-slate-900 flex items-center gap-2"><User className="h-4 w-4"/>Persona de contacto</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre completo *</Label>
                    <Input value={form.contact_name} onChange={e => handleChange("contact_name", e.target.value)} required/>
                  </div>
                  <div>
                    <Label>Teléfono *</Label>
                    <Input value={form.contact_phone} onChange={e => handleChange("contact_phone", e.target.value)} required/>
                  </div>
                  <div>
                    <Label>Email de acceso *</Label>
                    <Input type="email" value={form.contact_email} onChange={e => handleChange("contact_email", e.target.value)} required/>
                  </div>
                  <div>
                    <Label>Contraseña *</Label>
                    <Input type="password" value={form.password} onChange={e => handleChange("password", e.target.value)} required minLength={6}/>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Confirmar contraseña *</Label>
                    <Input type="password" value={form.password_confirm} onChange={e => handleChange("password_confirm", e.target.value)} required/>
                  </div>
                </div>
              </div>

              {/* Operational Info */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-slate-900 flex items-center gap-2"><MapPin className="h-4 w-4"/>Información operativa</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Ciudad / Zona principal *</Label>
                    <Input value={form.main_city} onChange={e => handleChange("main_city", e.target.value)} required placeholder="Ej: A Coruña"/>
                  </div>
                  <div>
                    <Label>Notas (opcional)</Label>
                    <Input value={form.notes} onChange={e => handleChange("notes", e.target.value)} placeholder="Información adicional"/>
                  </div>
                </div>
              </div>

              {/* Legal Consent */}
              <div className="pt-4 border-t space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={form.accept_privacy} onCheckedChange={v => handleChange("accept_privacy", v)} data-testid="accept-privacy-checkbox"/>
                  <span className="text-sm text-slate-600">
                    He leído y acepto la <a href="/privacy-policy" target="_blank" className="text-karga-dark underline">política de privacidad</a> y los <a href="/terms" target="_blank" className="text-karga-dark underline">términos y condiciones</a>, y autorizo el uso de mis datos personales para la gestión operativa de los servicios solicitados a través de KargaExpress.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={form.accept_terms} onCheckedChange={v => handleChange("accept_terms", v)} data-testid="accept-terms-checkbox"/>
                  <span className="text-sm text-slate-600">
                    Acepto las <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('showBillingTerms'))} className="text-karga-dark underline">Condiciones de Uso y Facturación</button>. Comprendo que las facturas de transporte deben ser abonadas antes del día 5 del mes siguiente a su emisión.
                  </span>
                </label>
              </div>

              <Button type="submit" className="w-full" disabled={loading || !form.accept_privacy || !form.accept_terms} style={{ backgroundColor: '#B4A798' }} data-testid="register-company-submit">
                {loading ? "Creando cuenta..." : "Crear cuenta de empresa"}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">¿Ya tienes cuenta? <button onClick={() => navigate("/login")} className="text-karga-dark hover:underline">Iniciar sesión</button></p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== DRIVER REGISTRATION ====================
const DriverRegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", password: "", password_confirm: "", city: "",
    legal_name: "", tax_id: "", fiscal_address: "",
    vehicle_category: "MEDIUM", is_isothermal: false, is_freezer: false, has_liftgate: false, license_plate: "",
    accept_privacy: false
  });

  const handleChange = (field, value) => setForm({ ...form, [field]: value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.accept_privacy) {
      alert("Debe aceptar la política de privacidad");
      return;
    }
    if (form.password !== form.password_confirm) {
      alert("Las contraseñas no coinciden");
      return;
    }
    if (!form.license_plate || form.license_plate.trim().length < 4) {
      alert("La matrícula del vehículo es obligatoria");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register/driver`, form);
      setSuccess(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Error al registrar");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F1F5F9' }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
              <Clock className="h-8 w-8" style={{ color: '#D97706' }}/>
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#171717' }}>Solicitud enviada</h2>
            <p className="text-slate-600 mb-6">Hemos recibido tu solicitud. Nuestro equipo revisará los datos y te avisaremos cuando tu cuenta esté activa.</p>
            <Button onClick={() => navigate("/")} variant="outline" className="w-full">
              Volver al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F1F5F9' }}>
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-6">
          <KargaLogo size="md" variant="light" />
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Registro de Autónomo / Conductor</CardTitle>
            <p className="text-sm text-slate-500">Complete los datos para solicitar acceso a la plataforma</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info */}
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900 flex items-center gap-2"><User className="h-4 w-4"/>Datos personales</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre completo *</Label>
                    <Input value={form.full_name} onChange={e => handleChange("full_name", e.target.value)} required/>
                  </div>
                  <div>
                    <Label>Teléfono *</Label>
                    <Input value={form.phone} onChange={e => handleChange("phone", e.target.value)} required/>
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" value={form.email} onChange={e => handleChange("email", e.target.value)} required/>
                  </div>
                  <div>
                    <Label>Ciudad / Zona *</Label>
                    <Input value={form.city} onChange={e => handleChange("city", e.target.value)} required placeholder="Ej: A Coruña"/>
                  </div>
                  <div>
                    <Label>Contraseña *</Label>
                    <Input type="password" value={form.password} onChange={e => handleChange("password", e.target.value)} required minLength={6}/>
                  </div>
                  <div>
                    <Label>Confirmar contraseña *</Label>
                    <Input type="password" value={form.password_confirm} onChange={e => handleChange("password_confirm", e.target.value)} required/>
                  </div>
                </div>
              </div>

              {/* Fiscal Info */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-slate-900 flex items-center gap-2"><FileText className="h-4 w-4"/>Datos fiscales</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre / Razón social fiscal *</Label>
                    <Input value={form.legal_name} onChange={e => handleChange("legal_name", e.target.value)} required/>
                  </div>
                  <div>
                    <Label>NIF / CIF *</Label>
                    <Input value={form.tax_id} onChange={e => handleChange("tax_id", e.target.value)} required/>
                  </div>
                </div>
                <div>
                  <Label>Dirección fiscal *</Label>
                  <Textarea value={form.fiscal_address} onChange={e => handleChange("fiscal_address", e.target.value)} required rows={2}/>
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-slate-900 flex items-center gap-2"><Truck className="h-4 w-4"/>Información del vehículo</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Categoría del vehículo *</Label>
                    <Select value={form.vehicle_category} onValueChange={v => handleChange("vehicle_category", v)}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SMALL">Furgoneta pequeña</SelectItem>
                        <SelectItem value="MEDIUM">Furgón mediano</SelectItem>
                        <SelectItem value="LARGE">Furgón grande</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Matrícula del vehículo *</Label>
                    <Input value={form.license_plate} onChange={e => handleChange("license_plate", e.target.value)} placeholder="0000 XXX" required/>
                  </div>
                </div>
                <div>
                  <Label className="mb-3 block">Equipamiento especial</Label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.is_isothermal} onCheckedChange={v => handleChange("is_isothermal", v)}/>
                      <Thermometer className="h-4 w-4 text-blue-500"/>Frío positivo (isotérmico)
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.is_freezer} onCheckedChange={v => handleChange("is_freezer", v)}/>
                      <Snowflake className="h-4 w-4 text-indigo-500"/>Congelación
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.has_liftgate} onCheckedChange={v => handleChange("has_liftgate", v)}/>
                      <ArrowUp className="h-4 w-4 text-amber-500"/>Plataforma elevadora
                    </label>
                  </div>
                </div>
              </div>

              {/* Legal Consent */}
              <div className="pt-4 border-t">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={form.accept_privacy} onCheckedChange={v => handleChange("accept_privacy", v)}/>
                  <span className="text-sm text-slate-600">
                    He leído y acepto la <a href="/privacy-policy" target="_blank" className="text-karga-dark underline">política de privacidad</a>, los <a href="/terms" target="_blank" className="text-karga-dark underline">términos y condiciones</a>, y autorizo el uso de mis datos personales para la gestión operativa, administrativa y de pago de los servicios realizados a través de KargaExpress.
                  </span>
                </label>
              </div>

              <Button type="submit" className="w-full" disabled={loading || !form.accept_privacy} style={{ backgroundColor: '#B4A798' }}>
                {loading ? "Enviando solicitud..." : "Enviar solicitud"}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">¿Ya tienes cuenta? <button onClick={() => navigate("/login")} className="text-karga-dark hover:underline">Iniciar sesión</button></p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== NOTIFICATION CENTER ====================
const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);
  const [lastNotificationId, setLastNotificationId] = useState(null);
  const [pendingJobCount, setPendingJobCount] = useState(0);
  const pendingJobTimeoutRef = useRef(null);
  const audioContextRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if sound is enabled (from localStorage)
  const isSoundEnabled = () => {
    if (user?.role !== 'DRIVER') return false;
    return localStorage.getItem('karga_sound_enabled') !== 'false';
  };

  // Play notification sound using Web Audio API (short, clean beep)
  const playNotificationSound = useCallback((type = 'default') => {
    if (!isSoundEnabled()) return;
    
    try {
      // Create audio context on demand
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      
      // Resume context if suspended (browser autoplay policy)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // Different sounds for different notification types
      if (type === 'job_available') {
        // Pleasant two-tone chime for new jobs
        oscillator.frequency.setValueAtTime(880, ctx.currentTime); // A5
        oscillator.frequency.setValueAtTime(1174.66, ctx.currentTime + 0.1); // D6
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
      } else if (type === 'job_assigned') {
        // Success sound - ascending
        oscillator.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
        oscillator.frequency.setValueAtTime(659.25, ctx.currentTime + 0.1); // E5
        oscillator.frequency.setValueAtTime(783.99, ctx.currentTime + 0.2); // G5
        gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.35);
      } else if (type === 'alert') {
        // Alert sound for cancellations/incidents - descending
        oscillator.frequency.setValueAtTime(880, ctx.currentTime);
        oscillator.frequency.setValueAtTime(659.25, ctx.currentTime + 0.15);
        gainNode.gain.setValueAtTime(0.35, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
      }
    } catch (e) {
      console.log('Audio not supported:', e);
    }
  }, [user?.role]);

  // Handle new notification with sound (for drivers)
  const handleNewNotification = useCallback((newNotif) => {
    if (user?.role !== 'DRIVER') return;
    
    const soundTypes = {
      'NEW_JOB_AVAILABLE': 'job_available',
      'JOB_ASSIGNED': 'job_assigned',
      'DRIVER_ASSIGNED': 'job_assigned',
      'JOB_CANCELLED': 'alert',
      'INCIDENCE_DRIVER': 'alert',
    };
    
    const soundType = soundTypes[newNotif.notification_type];
    if (!soundType) return; // No sound for minor events
    
    // Group job_available notifications (debounce)
    if (soundType === 'job_available') {
      setPendingJobCount(prev => prev + 1);
      
      // Clear existing timeout
      if (pendingJobTimeoutRef.current) {
        clearTimeout(pendingJobTimeoutRef.current);
      }
      
      // Play sound after 2 seconds of no new jobs (grouping)
      pendingJobTimeoutRef.current = setTimeout(() => {
        playNotificationSound('job_available');
        setPendingJobCount(0);
      }, 2000);
    } else {
      // Play immediately for assignments and alerts
      playNotificationSound(soundType);
    }
  }, [user?.role, playNotificationSound]);

  // Poll for new notifications (for drivers)
  useEffect(() => {
    if (user?.role !== 'DRIVER') return;
    
    const pollInterval = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/notifications?limit=5`);
        const newNotifs = r.data || [];
        
        if (newNotifs.length > 0) {
          const latestId = newNotifs[0]?.id;
          
          // Check for new notifications
          if (lastNotificationId && latestId !== lastNotificationId) {
            // Find new notifications
            const newOnes = newNotifs.filter(n => 
              !n.is_read && 
              notifications.findIndex(existing => existing.id === n.id) === -1
            );
            
            // Handle each new notification
            newOnes.forEach(n => handleNewNotification(n));
          }
          
          setLastNotificationId(latestId);
        }
        
        // Update count
        const countRes = await axios.get(`${API}/notifications/count`);
        setUnreadCount(countRes.data.unread_count);
      } catch {}
    }, 15000); // Poll every 15 seconds
    
    return () => {
      clearInterval(pollInterval);
      if (pendingJobTimeoutRef.current) clearTimeout(pendingJobTimeoutRef.current);
    };
  }, [user?.role, lastNotificationId, notifications, handleNewNotification]);

  // Load only count on mount (fast)
  useEffect(() => {
    axios.get(`${API}/notifications/count`)
      .then(r => setUnreadCount(r.data.unread_count))
      .catch(() => {});
    
    // Initialize lastNotificationId for drivers
    if (user?.role === 'DRIVER') {
      axios.get(`${API}/notifications?limit=1`)
        .then(r => {
          if (r.data?.[0]) setLastNotificationId(r.data[0].id);
        })
        .catch(() => {});
    }
  }, [user?.role]);

  // Load full notifications only when dropdown opens
  const loadNotifications = useCallback(async () => {
    if (notificationsLoaded) return;
    try {
      const r = await axios.get(`${API}/notifications`);
      setNotifications(r.data);
      setNotificationsLoaded(true);
    } catch (err) {
      console.error("Error loading notifications", err);
    }
  }, [notificationsLoaded]);

  // Load notifications when opening dropdown
  const handleOpen = useCallback(() => {
    setIsOpen(true);
    loadNotifications();
  }, [loadNotifications]);
  
  // Manual refresh handler
  const handleRefresh = useCallback(() => {
    setNotificationsLoaded(false);
    axios.get(`${API}/notifications/count`).then(r => setUnreadCount(r.data.unread_count));
    axios.get(`${API}/notifications`).then(r => { setNotifications(r.data); setNotificationsLoaded(true); });
  }, []);

  const markAsRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`);
      // Optimistic update instead of full reload
      setNotifications(prev => prev.map(n => n.id === id ? {...n, is_read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllAsRead = async () => {
    // Immediately update UI (optimistic update)
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    try {
      await axios.post(`${API}/notifications/read-all`);
    } catch {
      // Reload if failed
      loadNotifications();
    }
  };

  const handleNotificationClick = (notif) => {
    markAsRead(notif.id);
    setIsOpen(false);
    if (notif.shipment_id) {
      // Navigate directly to the shipment based on role
      const user = JSON.parse(localStorage.getItem("karga_user") || "{}");
      if (user.role === "COMPANY") {
        navigate(`/company/shipments/${notif.shipment_id}`);
      } else if (user.role === "ADMIN") {
        navigate(`/admin/shipments/${notif.shipment_id}`);
      } else if (user.role === "DRIVER") {
        navigate(`/driver/jobs/${notif.shipment_id}`);
      }
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      // Company notifications
      QUOTE_RECEIVED: Euro,
      QUOTE_ACCEPTED: Check,
      PICKUP_COMPLETED: Package,
      DELIVERY_COMPLETED: CheckCircle,
      DRIVER_ASSIGNED_TO_COMPANY: UserCheck,
      INCIDENCE_COMPANY: AlertCircle,
      RECURRING_SHIPMENT_CREATED: RefreshCw,
      // Admin notifications
      NEW_REQUEST: Inbox,
      COMPANY_ACCEPTED: Check,
      OFFER_PUBLISHED: Send,
      DRIVER_ASSIGNED: UserCheck,
      SHIPMENT_DELIVERED: CheckCircle,
      NEW_DRIVER_PENDING: User,
      PICKUP_ADMIN: Package,
      INCIDENCE_ADMIN: AlertCircle,
      DRIVER_APPLIED: Users,
      // Driver notifications
      NEW_JOB_AVAILABLE: Truck,
      JOB_ASSIGNED: UserCheck,
      ACCOUNT_APPROVED: CheckCircle,
      ACCOUNT_REJECTED: XCircle,
      JOB_CANCELLED: XCircle,
      INCIDENCE_DRIVER: AlertCircle,
    };
    return icons[type] || Bell;
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (mins < 1) return "Ahora";
    if (mins < 60) return `Hace ${mins}m`;
    if (hours < 24) return `Hace ${hours}h`;
    return `Hace ${days}d`;
  };

  // Get notification color based on type
  const getNotificationColor = (type) => {
    const colors = {
      // Green - success/completion
      DELIVERY_COMPLETED: 'bg-green-100 text-green-600',
      SHIPMENT_DELIVERED: 'bg-green-100 text-green-600',
      ACCOUNT_APPROVED: 'bg-green-100 text-green-600',
      QUOTE_ACCEPTED: 'bg-green-100 text-green-600',
      COMPANY_ACCEPTED: 'bg-green-100 text-green-600',
      // Blue - info/assignments
      DRIVER_ASSIGNED: 'bg-blue-100 text-karga-dark',
      DRIVER_ASSIGNED_TO_COMPANY: 'bg-blue-100 text-karga-dark',
      JOB_ASSIGNED: 'bg-blue-100 text-karga-dark',
      PICKUP_COMPLETED: 'bg-blue-100 text-karga-dark',
      PICKUP_ADMIN: 'bg-blue-100 text-karga-dark',
      // Yellow - pending/new
      NEW_REQUEST: 'bg-amber-100 text-amber-600',
      NEW_JOB_AVAILABLE: 'bg-amber-100 text-amber-600',
      NEW_DRIVER_PENDING: 'bg-amber-100 text-amber-600',
      DRIVER_APPLIED: 'bg-amber-100 text-amber-600',
      QUOTE_RECEIVED: 'bg-amber-100 text-amber-600',
      // Purple - special
      OFFER_PUBLISHED: 'bg-purple-100 text-purple-600',
      RECURRING_SHIPMENT_CREATED: 'bg-purple-100 text-purple-600',
      // Red - alerts/cancellations
      INCIDENCE_COMPANY: 'bg-red-100 text-red-600',
      INCIDENCE_ADMIN: 'bg-red-100 text-red-600',
      INCIDENCE_DRIVER: 'bg-red-100 text-red-600',
      JOB_CANCELLED: 'bg-red-100 text-red-600',
      ACCOUNT_REJECTED: 'bg-red-100 text-red-600',
    };
    return colors[type] || 'bg-slate-100 text-slate-600';
  };

  // Limit to last 15 notifications
  const displayedNotifications = notifications.slice(0, 15);

  return (
    <div className="relative">
      <button 
        onClick={() => isOpen ? setIsOpen(false) : handleOpen()} 
        className="relative p-3 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label="Notificaciones"
      >
        <Bell className="h-5 w-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-12 w-96 bg-white rounded-lg shadow-xl border z-50 overflow-hidden">
            <div className="p-3 border-b flex justify-between items-center bg-slate-50">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-slate-500"/>
                <span className="font-semibold text-sm">Notificaciones</span>
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">{unreadCount}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleRefresh} className="p-1 hover:bg-slate-200 rounded" title="Actualizar">
                  <RefreshCw className="h-4 w-4 text-slate-500"/>
                </button>
                {unreadCount > 0 && (
                  <button onClick={markAllAsRead} className="text-xs text-karga-dark hover:underline">
                    Marcar leídas
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-y-auto max-h-96">
              {displayedNotifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="h-8 w-8 text-slate-300 mx-auto mb-2"/>
                  <p className="text-slate-500 text-sm">No hay notificaciones</p>
                </div>
              ) : (
                displayedNotifications.map(notif => {
                  const Icon = getNotificationIcon(notif.notification_type);
                  const colorClass = getNotificationColor(notif.notification_type);
                  return (
                    <div 
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`p-3 border-b cursor-pointer hover:bg-slate-50 transition-all ${!notif.is_read ? "bg-blue-50/50 border-l-4 border-l-blue-500" : "border-l-4 border-l-transparent"}`}
                    >
                      <div className="flex gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm leading-tight ${!notif.is_read ? "font-semibold text-slate-800" : "text-slate-700"}`}>{notif.title}</p>
                            <span className="text-xs text-slate-400 whitespace-nowrap">{formatTime(notif.created_at)}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                          {notif.shipment_id && (
                            <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                              <ChevronRight className="h-3 w-3"/>Ver envío
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ==================== SIDEBAR LAYOUT ====================
const DashboardLayout = ({ children, role }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = {
    ADMIN: [
      { icon: BarChart3, label: "Dashboard", path: "/admin" },
      { icon: Activity, label: "Operaciones", path: "/admin/operations" },
      { icon: Inbox, label: "Solicitudes", path: "/admin/requests" },
      { icon: Package, label: "Envíos", path: "/admin/shipments" },
      { icon: RefreshCw, label: "Recurrentes", path: "/admin/recurring" },
      { icon: Building2, label: "Empresas", path: "/admin/companies" },
      { icon: Users, label: "Conductores", path: "/admin/drivers" },
      { icon: Receipt, label: "Facturación", path: "/admin/billing" },
      { icon: Euro, label: "Finanzas", path: "/admin/finance" },
    ],
    COMPANY: [
      { icon: BarChart3, label: "Dashboard", path: "/company" },
      { icon: Plus, label: "Nuevo Envío", path: "/company/new" },
      { icon: RefreshCw, label: "Recurrentes", path: "/company/recurring" },
      { icon: Inbox, label: "Presupuestos", path: "/company/quotes" },
      { icon: Package, label: "Mis Envíos", path: "/company/shipments" },
      { icon: Receipt, label: "Facturación", path: "/company/billing" },
      { icon: Building2, label: "Mi Empresa", path: "/company/profile" },
    ],
    DRIVER: [
      { icon: BarChart3, label: "Dashboard", path: "/driver" },
      { icon: Inbox, label: "Trabajos Disponibles", path: "/driver/available" },
      { icon: Package, label: "Mis Trabajos", path: "/driver/jobs" },
      { icon: Calendar, label: "Mi Calendario", path: "/driver/calendar" },
      { icon: Euro, label: "Ganancias", path: "/driver/earnings" },
      { icon: UserCheck, label: "Mi Perfil", path: "/driver/profile" },
    ],
  };

  const items = menuItems[role] || [];

  return (
    <div className="flex min-h-screen">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      
      {/* Sidebar - with safe area padding for notch/punch-hole camera */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform lg:transform-none ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`} style={{ 
        backgroundColor: '#171717',
        paddingTop: 'env(safe-area-inset-top, 0px)'
      }}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-center p-4 border-b border-slate-700 cursor-pointer" style={{
            paddingTop: 'max(16px, env(safe-area-inset-top, 0px))'
          }} onClick={() => navigate(role === "ADMIN" ? "/admin" : role === "COMPANY" ? "/company" : "/driver")}>
            <KargaLogo size="sidebar" variant="dark" />
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {items.map((item) => (
              <button key={item.path} onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors">
                <item.icon className="h-5 w-5" />{item.label}
              </button>
            ))}
          </nav>
          <div className="p-4 border-t border-slate-700">
            <div className="text-sm text-slate-400 mb-2">{user?.name}</div>
            <button onClick={logout} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white">
              <LogOut className="h-4 w-4" />Cerrar sesión
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header with notifications - visible on all screen sizes */}
        {/* Safe area padding for mobile notch/status bar/punch-hole camera */}
        <header className="app-header bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-30" style={{
          paddingTop: 'max(env(safe-area-inset-top, 0px), 24px)'
        }}>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(true)} 
              className="lg:hidden touch-target p-2 -ml-2 rounded-lg hover:bg-slate-100 active:bg-slate-200"
              aria-label="Abrir menú"
            >
              <Menu className="h-6 w-6" />
            </button>
            <span className="font-semibold lg:hidden">KargaExpress</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="touch-target">
              <NotificationCenter />
            </div>
            <span className="text-sm text-slate-500 hidden lg:block">{user?.name}</span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-auto" style={{ backgroundColor: '#F5F2ED' }}>{children}</main>
      </div>
    </div>
  );
};

// ==================== COMPANY DASHBOARD ====================
const CompanyDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [recentShipments, setRecentShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        const [summaryRes, shipmentsRes] = await Promise.all([
          axios.get(`${API}/companies/me/summary`),
          axios.get(`${API}/shipments?page=1&limit=5`) // Only fetch 5 for dashboard
        ]);
        setSummary(summaryRes.data);
        setRecentShipments(shipmentsRes.data.items || shipmentsRes.data);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <DashboardLayout role="COMPANY">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Panel de Empresa</h1>
          <Button onClick={() => navigate("/company/new")} style={{ backgroundColor: '#B4A798' }}><Plus className="h-4 w-4 mr-2"/>Nuevo Envío</Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-karga-cream"><Plus className="h-5 w-5 text-karga-dark"/></div>
                <div><p className="text-sm text-slate-500">Creados Hoy</p><p className="text-2xl font-bold">{summary?.today_created || 0}</p></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-yellow-100"><Inbox className="h-5 w-5 text-yellow-600"/></div>
                <div><p className="text-sm text-slate-500">Presupuestos</p><p className="text-2xl font-bold">{summary?.pending_quotes || 0}</p></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-100"><Truck className="h-5 w-5 text-orange-600"/></div>
                <div><p className="text-sm text-slate-500">En Curso</p><p className="text-2xl font-bold">{summary?.in_progress || 0}</p></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-100"><Euro className="h-5 w-5 text-red-600"/></div>
                <div><p className="text-sm text-slate-500">Pendiente</p><p className="text-2xl font-bold">€{(summary?.unpaid_total || 0).toFixed(0)}</p></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Shipments */}
        <Card>
          <CardHeader><CardTitle>Envíos Recientes</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Origen</TableHead>
                  <TableHead>Destino</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Precio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentShipments.map(s => (
                  <TableRow key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/company/shipments/${s.id}`)}>
                    <TableCell className="text-sm">{s.pickup_address?.substring(0, 30)}...</TableCell>
                    <TableCell className="text-sm">{s.dropoff_address?.substring(0, 30)}...</TableCell>
                    <TableCell><StatusPill status={s.status} role="COMPANY"/></TableCell>
                    <TableCell>{s.customer_total ? `€${s.customer_total.toFixed(2)}` : "-"}</TableCell>
                  </TableRow>
                ))}
                {recentShipments.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500">No hay envíos</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

// ==================== COMPANY NEW SHIPMENT ====================
const CompanyNewShipment = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    pickup_address: "", pickup_contact_name: "", pickup_phone: "",
    dropoff_address: "", dropoff_contact_name: "", dropoff_phone: "",
    cargo_description: "", notes: "",
    service_type: "SCHEDULED", schedule_datetime: "",
    vehicle_category: "MEDIUM", allow_bigger_vehicle: true,
    requires_isothermal: false, requires_freezer: false, requires_liftgate: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/shipments`, form);
      addToast("Solicitud de envío creada", "success");
      navigate("/company/shipments");
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al crear envío", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout role="COMPANY">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-6" style={{ color: '#171717' }}>Nueva Solicitud de Envío</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Pickup */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-green-600"/>Recogida</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Dirección de recogida *</Label><Input value={form.pickup_address} onChange={e => setForm({...form, pickup_address: e.target.value})} required placeholder="Calle, número, ciudad"/></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Persona de contacto</Label><Input value={form.pickup_contact_name} onChange={e => setForm({...form, pickup_contact_name: e.target.value})} placeholder="Nombre"/></div>
                <div><Label>Teléfono</Label><Input value={form.pickup_phone} onChange={e => setForm({...form, pickup_phone: e.target.value})} placeholder="+34 600 000 000"/></div>
              </div>
            </CardContent>
          </Card>

          {/* Dropoff */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-red-600"/>Entrega</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Dirección de entrega *</Label><Input value={form.dropoff_address} onChange={e => setForm({...form, dropoff_address: e.target.value})} required placeholder="Calle, número, ciudad"/></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Persona de contacto</Label><Input value={form.dropoff_contact_name} onChange={e => setForm({...form, dropoff_contact_name: e.target.value})} placeholder="Nombre"/></div>
                <div><Label>Teléfono</Label><Input value={form.dropoff_phone} onChange={e => setForm({...form, dropoff_phone: e.target.value})} placeholder="+34 600 000 000"/></div>
              </div>
            </CardContent>
          </Card>

          {/* Cargo & Schedule */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Package className="h-5 w-5"/>Mercancía y Programación</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Descripción de la carga</Label><Textarea value={form.cargo_description} onChange={e => setForm({...form, cargo_description: e.target.value})} placeholder="Ej: 3 palets de material de oficina, 500kg total"/></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de servicio</Label>
                  <Select value={form.service_type} onValueChange={v => setForm({...form, service_type: v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SCHEDULED">Programado</SelectItem>
                      <SelectItem value="URGENT">Urgente (mismo día)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha/hora preferida</Label>
                  <Input type="datetime-local" value={form.schedule_datetime} onChange={e => setForm({...form, schedule_datetime: e.target.value})}/>
                </div>
              </div>
              <div><Label>Notas adicionales</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Instrucciones especiales, horarios, accesos..."/></div>
            </CardContent>
          </Card>

          {/* Vehicle Requirements */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Truck className="h-5 w-5"/>Requisitos del Vehículo</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Tamaño mínimo de furgón</Label>
                <Select value={form.vehicle_category} onValueChange={v => setForm({...form, vehicle_category: v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SMALL">Pequeño (hasta 3m³)</SelectItem>
                    <SelectItem value="MEDIUM">Mediano (hasta 10m³)</SelectItem>
                    <SelectItem value="LARGE">Grande (hasta 20m³)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="bigger" checked={form.allow_bigger_vehicle} onCheckedChange={c => setForm({...form, allow_bigger_vehicle: c})}/>
                <Label htmlFor="bigger" className="text-sm">Permitir vehículo más grande si no hay disponible del tamaño solicitado</Label>
              </div>
              <div className="border-t pt-4">
                <Label className="mb-3 block">Requisitos especiales</Label>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox id="iso" checked={form.requires_isothermal} onCheckedChange={c => setForm({...form, requires_isothermal: c})}/>
                    <Label htmlFor="iso" className="text-sm flex items-center gap-1"><Thermometer className="h-4 w-4 text-blue-500"/>Frío (+2°C a +8°C)</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="freeze" checked={form.requires_freezer} onCheckedChange={c => setForm({...form, requires_freezer: c})}/>
                    <Label htmlFor="freeze" className="text-sm flex items-center gap-1"><Snowflake className="h-4 w-4 text-indigo-500"/>Congelado (-18°C)</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="lift" checked={form.requires_liftgate} onCheckedChange={c => setForm({...form, requires_liftgate: c})}/>
                    <Label htmlFor="lift" className="text-sm flex items-center gap-1"><ArrowUp className="h-4 w-4 text-amber-500"/>Plataforma elevadora</Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancelar</Button>
            <Button type="submit" disabled={loading} style={{ backgroundColor: '#B4A798' }}>{loading ? "Enviando..." : "Solicitar Presupuesto"}</Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

// ==================== COMPANY QUOTES INBOX ====================
const CompanyQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/shipments?status=PRICED`);
      // Handle both paginated {items:[]} and direct array responses
      const data = r.data?.items || r.data || [];
      setQuotes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[CompanyQuotes] Error loading:', err);
      setError(err.userMessage || 'Error al cargar los presupuestos');
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadQuotes(); }, [loadQuotes]);

  const handleAccept = async (id) => {
    try {
      await axios.post(`${API}/shipments/${id}/company-response`, { accept: true });
      addToast("Presupuesto aceptado\nHemos recibido tu confirmación. Estamos buscando el conductor adecuado para tu envío.", "success");
      loadQuotes();
    } catch (err) {
      addToast("Error al procesar respuesta", "error");
    }
  };

  const handleReject = async () => {
    try {
      await axios.post(`${API}/shipments/${selectedQuoteId}/company-response`, { accept: false, reason: rejectReason });
      addToast("Presupuesto rechazado\nEl presupuesto ha sido rechazado. El envío ha sido cancelado.", "info");
      setShowRejectModal(false);
      setRejectReason("");
      setSelectedQuoteId(null);
      loadQuotes();
    } catch (err) {
      addToast("Error al procesar respuesta", "error");
    }
  };

  const openRejectModal = (id) => {
    setSelectedQuoteId(id);
    setShowRejectModal(true);
  };

  return (
    <DashboardLayout role="COMPANY">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Presupuestos pendientes</h1>
          <Button size="sm" variant="outline" onClick={loadQuotes} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {/* Loading State */}
        {loading && quotes.length === 0 && (
          <Card><CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400 mb-2" />
            <p className="text-slate-500">Cargando presupuestos...</p>
          </CardContent></Card>
        )}
        
        {/* Error State */}
        {error && (
          <Card className="border-red-200"><CardContent className="p-6 text-center">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-700 mb-4">{error}</p>
            <Button onClick={loadQuotes} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />Reintentar
            </Button>
          </CardContent></Card>
        )}
        
        {/* Empty State */}
        {!loading && !error && quotes.length === 0 && (
          <Card><CardContent className="p-8 text-center text-slate-500">No tienes presupuestos pendientes en este momento.</CardContent></Card>
        )}
        
        {/* Data List */}
        {!error && quotes.length > 0 && (
          <div className="space-y-4">
            {quotes.map(q => (
              <Card key={q.id} className="border-l-4" style={{ borderLeftColor: '#B4A798' }}>
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">Presupuesto</span>
                        {q.service_type === "URGENT" && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">URGENTE</span>}
                      </div>
                      <p className="font-medium text-slate-900">{q.pickup_address}</p>
                      <p className="text-sm text-slate-500">→ {q.dropoff_address}</p>
                      {q.cargo_description && <p className="text-sm text-slate-600 mt-2">{q.cargo_description}</p>}
                      <RequirementBadges isothermal={q.requires_isothermal} freezer={q.requires_freezer} liftgate={q.requires_liftgate}/>
                    </div>
                    <div className="flex flex-col items-end gap-3 min-w-[200px]">
                      <div className="text-right bg-slate-50 p-4 rounded-lg w-full">
                        <p className="text-sm text-slate-600 font-medium">Precio del servicio</p>
                        <p className="text-3xl font-bold" style={{ color: '#B4A798' }}>€{(q.client_total_vat_incl || q.customer_total || q.precio_total)?.toFixed(2)}</p>
                        <p className="text-xs text-slate-500">(sin IVA)</p>
                      </div>
                      <p className="text-xs text-slate-500 text-right">Este es el precio final del servicio. Una vez aceptado, comenzaremos a gestionarlo.</p>
                      <div className="flex gap-2 w-full">
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => openRejectModal(q.id)}>Rechazar</Button>
                        <Button size="sm" className="flex-1" onClick={() => handleAccept(q.id)} style={{ backgroundColor: '#16A34A' }}>
                          <Check className="h-4 w-4 mr-1"/>Aceptar presupuesto
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar presupuesto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea 
              placeholder="Motivo del rechazo (opcional)" 
              value={rejectReason} 
              onChange={e => setRejectReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancelar</Button>
            <Button onClick={handleReject} variant="destructive">Confirmar rechazo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// ==================== COMPANY SHIPMENTS LIST ====================
const CompanyShipments = () => {
  const [shipments, setShipments] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const loadShipments = useCallback(async (pageNum = 1) => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/shipments?page=${pageNum}&limit=20`);
      const data = r.data?.items || r.data || [];
      setShipments(Array.isArray(data) ? data : []);
      setPagination({ total: r.data?.total || 0, pages: r.data?.pages || 1 });
    } catch (err) {
      console.error('[CompanyShipments] Error:', err);
      setError(err.userMessage || 'Error al cargar los envíos');
      setShipments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadShipments(1); }, [loadShipments]);
  
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    loadShipments(newPage);
  }, [loadShipments]);

  const shipmentRows = useMemo(() => (Array.isArray(shipments) ? shipments : []).map(s => (
    <TableRow key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/company/shipments/${s.id}`)}>
      <TableCell className="text-sm">{s.pickup_address?.substring(0, 25)}...</TableCell>
      <TableCell className="text-sm">{s.dropoff_address?.substring(0, 25)}...</TableCell>
      <TableCell><StatusPill status={s.status} role="COMPANY"/></TableCell>
      <TableCell>
        {s.driver_name ? (
          <span className="text-sm font-medium text-orange-600">{s.driver_name}</span>
        ) : (
          <span className="text-sm text-slate-400">-</span>
        )}
      </TableCell>
      <TableCell>{s.precio_total ? `€${s.precio_total.toFixed(2)}` : s.customer_total ? `€${s.customer_total.toFixed(2)}` : "-"}</TableCell>
    </TableRow>
  )), [shipments, navigate]);

  return (
    <DashboardLayout role="COMPANY">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Mis Envíos</h1>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => loadShipments(page)} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={() => navigate("/company/new")} style={{ backgroundColor: '#B4A798' }}><Plus className="h-4 w-4 mr-2"/>Nuevo</Button>
          </div>
        </div>
        <Card>
          <CardContent className="p-0">
            {loading && shipments.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Origen</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Conductor</TableHead>
                    <TableHead>Precio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shipmentRows}
                </TableBody>
              </Table>
            )}
          </CardContent>
          <Pagination page={page} pages={pagination.pages} total={pagination.total} onPageChange={handlePageChange} loading={loading} />
        </Card>
      </div>
    </DashboardLayout>
  );
};

// ==================== SHIPMENT DETAIL (with POD) ====================
const ShipmentDetail = ({ role }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [assigningDriver, setAssigningDriver] = useState(null);
  const { addToast } = useToast();

  const loadShipment = useCallback(async () => {
    try {
      // Use role-specific endpoints (backend enforced data filtering)
      let shipmentEndpoint;
      if (role === "ADMIN") {
        shipmentEndpoint = `${API}/admin/shipments/${id}`;
      } else if (role === "COMPANY") {
        shipmentEndpoint = `${API}/company/shipments/${id}`;
      } else if (role === "DRIVER") {
        shipmentEndpoint = `${API}/driver/shipments/${id}`;
      } else {
        shipmentEndpoint = `${API}/shipments/${id}`;
      }
      
      const [shipmentRes, proofsRes] = await Promise.all([
        axios.get(shipmentEndpoint),
        axios.get(`${API}/shipments/${id}/proofs`)
      ]);
      setShipment(shipmentRes.data);
      setProofs(proofsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, role]);

  const loadApplicants = useCallback(async () => {
    if (role !== "ADMIN") return;
    setLoadingApplicants(true);
    try {
      const r = await axios.get(`${API}/shipments/${id}/applicants`);
      setApplicants(r.data.applicants || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingApplicants(false);
    }
  }, [id, role]);

  useEffect(() => {
    loadShipment();
  }, [loadShipment]);

  useEffect(() => {
    if (shipment?.status === "OFFER_PUBLISHED" && role === "ADMIN") {
      loadApplicants();
    }
  }, [shipment?.status, role, loadApplicants]);

  const assignDriver = async (driverId) => {
    setAssigningDriver(driverId);
    try {
      await axios.post(`${API}/shipments/${id}/assign-driver`, { driver_id: driverId });
      addToast("Conductor asignado correctamente", "success");
      loadShipment(); // Refresh shipment status
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al asignar", "error");
    } finally {
      setAssigningDriver(null);
    }
  };

  if (loading) return <DashboardLayout role={role}><div className="p-8 text-center">Cargando...</div></DashboardLayout>;
  if (!shipment) return <DashboardLayout role={role}><div className="p-8 text-center text-red-500">Envío no encontrado</div></DashboardLayout>;

  const pickupPhoto = proofs.find(p => p.proof_type === "PICKUP_PHOTO");
  const deliveryPhoto = proofs.find(p => p.proof_type === "DELIVERY_PHOTO");
  const deliveryNote = proofs.find(p => p.proof_type === "DELIVERY_NOTE");
  const podComplete = pickupPhoto && deliveryPhoto && deliveryNote && shipment.receiver_name;

  return (
    <DashboardLayout role={role}>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-sm text-karga-dark hover:underline">← Volver</button>
          <StatusPill status={shipment.status} role={role}/>
        </div>

        {/* Main Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5"/>
              Detalles del Envío
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Recogida</p>
                <p className="font-medium">{shipment.pickup_address}</p>
                {shipment.pickup_contact_name && <p className="text-sm text-slate-600">{shipment.pickup_contact_name} - {shipment.pickup_phone}</p>}
              </div>
              <div>
                <p className="text-sm text-slate-500">Entrega</p>
                <p className="font-medium">{shipment.dropoff_address}</p>
                {shipment.dropoff_contact_name && <p className="text-sm text-slate-600">{shipment.dropoff_contact_name} - {shipment.dropoff_phone}</p>}
              </div>
            </div>
            {shipment.cargo_description && (
              <div>
                <p className="text-sm text-slate-500">Mercancía</p>
                <p>{shipment.cargo_description}</p>
              </div>
            )}
            <div className="flex gap-4 flex-wrap">
              <div>
                <p className="text-sm text-slate-500">Vehículo</p>
                <VehicleBadge category={shipment.vehicle_category}/>
              </div>
              <div>
                <p className="text-sm text-slate-500">Requisitos</p>
                <RequirementBadges isothermal={shipment.requires_isothermal} freezer={shipment.requires_freezer} liftgate={shipment.requires_liftgate}/>
              </div>
            </div>
            {/* Fecha y franja horaria */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500 mb-1">Fecha programada</p>
              <ScheduleInfo shipment={shipment}/>
              {shipment.pickup_time && (
                <p className="text-sm mt-1"><span className="text-slate-500">Hora de recogida:</span> <span className="font-medium text-karga-dark">⏰ {shipment.pickup_time}</span></p>
              )}
              {shipment.delivery_time && (
                <p className="text-sm"><span className="text-slate-500">Hora estimada entrega:</span> <span className="font-medium">{shipment.delivery_time}</span></p>
              )}
            </div>
            {/* Assigned Driver - COMPANY sees driver name when assigned */}
            {role === "COMPANY" && shipment.driver_name && (
              <div className="p-4 bg-orange-50 rounded-lg flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Truck className="h-5 w-5 text-orange-600"/>
                </div>
                <div>
                  <p className="text-sm text-orange-700">Conductor asignado</p>
                  <p className="font-bold text-orange-800">{shipment.driver_name}</p>
                </div>
              </div>
            )}
            {/* Pricing - COMPANY sees only precio_total - filtered by backend */}
            {role === "COMPANY" && shipment.precio_total && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-karga-carbon mb-1">Precio total</p>
                <p className="text-2xl font-bold text-karga-carbon">€{shipment.precio_total.toFixed(2)}</p>
                <p className="text-xs text-karga-dark mt-1">(sin IVA)</p>
              </div>
            )}
            {/* Pricing - ADMIN sees all: totals + margin */}
            {role === "ADMIN" && (
              <div className="space-y-4">
                {/* Client pricing with breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-karga-carbon">Cliente Total (sin IVA)</p>
                    <p className="text-xl font-bold text-karga-carbon">€{shipment.client_total_vat_incl?.toFixed(2) || shipment.customer_total?.toFixed(2) || "-"}</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="text-sm text-orange-700">Conductor Total (sin IVA)</p>
                    <p className="text-xl font-bold text-orange-700">€{shipment.driver_total_vat_incl?.toFixed(2) || shipment.driver_payout?.toFixed(2) || "-"}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-700">Margen Karga</p>
                    <p className="text-xl font-bold text-green-700">€{shipment.margin_total?.toFixed(2) || shipment.karga_margin?.toFixed(2) || "-"}</p>
                  </div>
                </div>
              </div>
            )}
            {/* Pricing - DRIVER sees only their payout */}
            {role === "DRIVER" && shipment.mi_pago_total && (
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700 mb-1">Mi pago</p>
                <p className="text-2xl font-bold text-green-700">€{shipment.mi_pago_total.toFixed(2)}</p>
                <p className="text-xs text-green-600 mt-1">(sin IVA)</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Applicants Section - Admin only, when status is OFFER_PUBLISHED */}
        {role === "ADMIN" && shipment.status === "OFFER_PUBLISHED" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5"/>
                Aplicantes
                {applicants.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-karga-carbon rounded-full text-xs font-medium">
                    {applicants.length}
                  </span>
                )}
                <Button size="sm" variant="ghost" onClick={loadApplicants} disabled={loadingApplicants} className="ml-auto">
                  <RefreshCw className={`h-4 w-4 ${loadingApplicants ? 'animate-spin' : ''}`}/>
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingApplicants ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400"/>
                </div>
              ) : applicants.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-2 text-slate-300"/>
                  <p>Ningún conductor ha aplicado todavía</p>
                  <p className="text-sm mt-1">Los conductores verán este trabajo en su lista de disponibles</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {applicants.map(app => (
                    <div key={app.driver_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50" data-testid={`applicant-${app.driver_id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-slate-500"/>
                        </div>
                        <div>
                          <p className="font-medium">{app.driver_name}</p>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <VehicleBadge category={app.vehicle_category}/>
                            <RequirementBadges 
                              isothermal={app.is_isothermal} 
                              freezer={app.is_freezer} 
                              liftgate={app.has_liftgate}
                            />
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            Aplicó: {new Date(app.applied_at).toLocaleString('es-ES')}
                            {app.phone && <span className="ml-2">📞 {app.phone}</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!app.has_payment_info && (
                          <span className="text-xs text-amber-600 flex items-center gap-1" title="Sin datos de pago">
                            <AlertCircle className="h-3 w-3"/>
                          </span>
                        )}
                        <LoadingButton
                          size="sm"
                          onClick={() => assignDriver(app.driver_id)}
                          loading={assigningDriver === app.driver_id}
                          disabled={assigningDriver !== null}
                          style={{ backgroundColor: '#16A34A' }}
                          data-testid={`assign-btn-${app.driver_id}`}
                        >
                          <UserCheck className="h-4 w-4 mr-1"/>
                          Asignar
                        </LoadingButton>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Driver Assigned Info - Admin only */}
        {role === "ADMIN" && shipment.driver_id && shipment.driver_name && (
          <Card className="border-green-200 bg-green-50/30">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <UserCheck className="h-5 w-5 text-green-600"/>
              </div>
              <div>
                <p className="font-medium text-green-800">Conductor Asignado</p>
                <p className="text-sm text-green-700">{shipment.driver_name}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* POD Section */}
        {shipment.status === "DELIVERED" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CameraIcon className="h-5 w-5"/>
                Prueba de Entrega (POD)
                {podComplete && (
                  <span className="ml-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                    <CheckCircle className="h-3 w-3"/>
                    Completada
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {/* Pickup Photo */}
                <div className="border rounded-lg p-3">
                  <p className="text-sm font-medium text-slate-700 mb-2">📦 Foto Recogida</p>
                  {pickupPhoto ? (
                    <a href={`${BACKEND_URL}${pickupPhoto.file_url}`} target="_blank" rel="noopener noreferrer" className="block">
                      <img 
                        src={`${BACKEND_URL}${pickupPhoto.thumbnail_url || pickupPhoto.file_url}`} 
                        alt="Foto recogida" 
                        className="w-full h-40 object-cover rounded-lg hover:opacity-90 transition-opacity cursor-zoom-in"
                        loading="lazy"
                      />
                      <p className="text-xs text-slate-400 mt-1 text-center">Click para ampliar</p>
                    </a>
                  ) : (
                    <div className="w-full h-40 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400">
                      <CameraIcon className="h-8 w-8"/>
                    </div>
                  )}
                </div>

                {/* Delivery Photo */}
                <div className="border rounded-lg p-3">
                  <p className="text-sm font-medium text-slate-700 mb-2">🚚 Foto Entrega</p>
                  {deliveryPhoto ? (
                    <>
                      <a href={`${BACKEND_URL}${deliveryPhoto.file_url}`} target="_blank" rel="noopener noreferrer" className="block">
                        <img 
                          src={`${BACKEND_URL}${deliveryPhoto.thumbnail_url || deliveryPhoto.file_url}`} 
                          alt="Foto entrega" 
                          className="w-full h-40 object-cover rounded-lg hover:opacity-90 transition-opacity cursor-zoom-in"
                          loading="lazy"
                        />
                        <p className="text-xs text-slate-400 mt-1 text-center">Click para ampliar</p>
                      </a>
                      {shipment.receiver_name && (
                        <p className="mt-2 text-sm text-slate-600">
                          <span className="font-medium">Receptor:</span> {shipment.receiver_name}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-40 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400">
                      <CameraIcon className="h-8 w-8"/>
                    </div>
                  )}
                </div>

                {/* Delivery Note / Albarán */}
                <div className="border rounded-lg p-3">
                  <p className="text-sm font-medium text-slate-700 mb-2">📋 Foto Albarán</p>
                  {deliveryNote ? (
                    <a href={`${BACKEND_URL}${deliveryNote.file_url}`} target="_blank" rel="noopener noreferrer" className="block">
                      <img 
                        src={`${BACKEND_URL}${deliveryNote.thumbnail_url || deliveryNote.file_url}`} 
                        alt="Foto albarán" 
                        className="w-full h-40 object-cover rounded-lg hover:opacity-90 transition-opacity cursor-zoom-in"
                        loading="lazy"
                      />
                      <p className="text-xs text-slate-400 mt-1 text-center">Click para ampliar</p>
                    </a>
                  ) : (
                    <div className="w-full h-40 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400">
                      <FileText className="h-8 w-8"/>
                    </div>
                  )}
                </div>
              </div>

              {/* Firma Digital eliminada en v1.0.1 */}

              {/* Delivery timestamp */}
              {shipment.delivered_at && (
                <p className="mt-4 text-sm text-slate-500">
                  Entregado el {new Date(shipment.delivered_at).toLocaleString('es-ES')}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Full Timestamp Timeline (Admin view) */}
        {role === "ADMIN" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5"/>
                Seguimiento de Tiempos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {shipment.created_at && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Creado</span>
                    <span className="font-medium">{new Date(shipment.created_at).toLocaleString('es-ES')}</span>
                  </div>
                )}
                {shipment.quoted_at && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Presupuestado</span>
                    <span className="font-medium">{new Date(shipment.quoted_at).toLocaleString('es-ES')}</span>
                  </div>
                )}
                {shipment.company_accepted_at && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Aceptado por cliente</span>
                    <span className="font-medium text-green-600">{new Date(shipment.company_accepted_at).toLocaleString('es-ES')}</span>
                  </div>
                )}
                {shipment.company_rejected_at && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Rechazado por cliente</span>
                    <span className="font-medium text-red-600">{new Date(shipment.company_rejected_at).toLocaleString('es-ES')}</span>
                  </div>
                )}
                {shipment.offer_published_at && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Publicado a conductores</span>
                    <span className="font-medium">{new Date(shipment.offer_published_at).toLocaleString('es-ES')}</span>
                  </div>
                )}
                {shipment.driver_assigned_at && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Conductor asignado</span>
                    <span className="font-medium">{new Date(shipment.driver_assigned_at).toLocaleString('es-ES')}</span>
                  </div>
                )}
                {shipment.pickup_scheduled_datetime && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Recogida programada</span>
                    <span className="font-medium text-karga-dark">{new Date(shipment.pickup_scheduled_datetime).toLocaleString('es-ES')}</span>
                  </div>
                )}
                {shipment.pickup_completed_at && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Recogida realizada</span>
                    <span className="font-medium text-green-600">{new Date(shipment.pickup_completed_at).toLocaleString('es-ES')}</span>
                  </div>
                )}
                {shipment.delivery_scheduled_datetime && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Entrega programada</span>
                    <span className="font-medium text-karga-dark">{new Date(shipment.delivery_scheduled_datetime).toLocaleString('es-ES')}</span>
                  </div>
                )}
                {shipment.delivered_at && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-slate-600">Entrega realizada</span>
                    <span className="font-medium text-green-600">{new Date(shipment.delivered_at).toLocaleString('es-ES')}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Incidence Section */}
        {shipment.has_incidence && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertCircle className="h-5 w-5"/>
                Incidencia Reportada
                {shipment.incidence_resolved_at && (
                  <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Resuelta</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-red-600 font-medium">Tipo de incidencia</p>
                  <p className="font-medium text-red-800">
                    {{
                      "DELAY": "⏰ Retraso",
                      "NO_ONE_AT_PICKUP": "🚫 No hay nadie en recogida",
                      "NO_ONE_AT_DELIVERY": "🚫 No hay nadie en entrega",
                      "CARGO_ISSUE": "📦 Problema con mercancía",
                      "WRONG_ADDRESS": "📍 Dirección incorrecta",
                      "OTHER": "❓ Otra incidencia"
                    }[shipment.incidence_type] || shipment.incidence_type}
                  </p>
                </div>
                {shipment.incidence_notes && (
                  <div>
                    <p className="text-sm text-red-600 font-medium">Descripción</p>
                    <p className="text-red-800">{shipment.incidence_notes}</p>
                  </div>
                )}
                <div className="flex gap-4 text-sm text-red-600">
                  {shipment.incidence_created_at && (
                    <span>Reportada: {new Date(shipment.incidence_created_at).toLocaleString('es-ES')}</span>
                  )}
                  {shipment.incidence_resolved_at && (
                    <span>Resuelta: {new Date(shipment.incidence_resolved_at).toLocaleString('es-ES')}</span>
                  )}
                </div>
                {shipment.incidence_photos && shipment.incidence_photos.length > 0 && (
                  <div>
                    <p className="text-sm text-red-600 font-medium mb-2">Fotos de incidencia</p>
                    <div className="flex gap-2 flex-wrap">
                      {shipment.incidence_photos.map((photo, idx) => (
                        <img key={idx} src={`${BACKEND_URL}/api/uploads/${photo}`} alt={`Incidencia ${idx+1}`} className="w-24 h-24 object-cover rounded border"/>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Events Timeline - Already filtered by backend per role */}
        {shipment.events && shipment.events.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Historial</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(Array.isArray(shipment.events) ? shipment.events : []).map((e, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-2"></div>
                    <div>
                      <p className="font-medium">{e.notes}</p>
                      <p className="text-slate-500">
                        {new Date(e.created_at).toLocaleString('es-ES')}
                        {/* Only show user_name for ADMIN */}
                        {role === "ADMIN" && e.user_name && ` - ${e.user_name}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

// ==================== COMPANY BILLING ====================
const CompanyBilling = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [showConditions, setShowConditions] = useState(false);  // v1.1: Modal for billing conditions

  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

  const loadBilling = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/companies/me/monthly-activity?year=${selectedYear}&month=${selectedMonth}`);
      setData(r.data || {});
    } catch (err) {
      console.error('[CompanyBilling] Error:', err);
      setError(err.userMessage || 'Error al cargar la facturación');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => { loadBilling(); }, [loadBilling]);

  const statusLabels = {
    'REQUESTED': 'Solicitado',
    'PRICED': 'Cotizado',
    'COMPANY_ACCEPTED': 'Aceptado',
    'COMPANY_REJECTED': 'Rechazado',
    'OFFER_PUBLISHED': 'Publicado',
    'DRIVER_ASSIGNED': 'Asignado',
    'PICKED_UP': 'Recogido',
    'IN_TRANSIT': 'En tránsito',
    'DELIVERED': 'Entregado',
    'CANCELLED': 'Cancelado'
  };

  const statusColors = {
    'DELIVERED': 'bg-green-100 text-green-700',
    'CANCELLED': 'bg-red-100 text-red-700',
    'COMPANY_REJECTED': 'bg-red-100 text-red-700',
    'IN_TRANSIT': 'bg-blue-100 text-karga-carbon',
    'PICKED_UP': 'bg-blue-100 text-karga-carbon',
    'DRIVER_ASSIGNED': 'bg-purple-100 text-purple-700'
  };

  if (loading) {
    return (
      <DashboardLayout role="COMPANY">
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout role="COMPANY">
        <Card className="border-red-200">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-700 mb-4">{error}</p>
            <Button onClick={loadBilling} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />Reintentar
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const shipments = data?.shipments || [];

  return (
    <DashboardLayout role="COMPANY">
      <div className="space-y-6">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Actividad Mensual</h1>
            <button onClick={() => setShowConditions(true)} className="text-sm text-karga-dark hover:underline" data-testid="company-billing-conditions-link">
              Ver condiciones de facturación
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Select value={selectedMonth.toString()} onValueChange={v => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-32">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                {monthNames.map((m, i) => (
                  <SelectItem key={i} value={(i+1).toString()}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input 
              type="number" 
              className="w-24"
              value={selectedYear} 
              onChange={e => setSelectedYear(parseInt(e.target.value))}
              min={2024}
              max={2030}
            />
            <Button variant="outline" size="sm" onClick={loadBilling} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Summary Card */}
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-karga-dark font-medium">{monthNames[selectedMonth - 1]} {selectedYear}</p>
                <p className="text-3xl font-bold text-blue-800">€{data?.billing_total_vat_incl?.toFixed(2) || "0.00"}</p>
                <p className="text-sm text-karga-dark">Total facturado (sin IVA)</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">{data?.delivered_count || 0} envíos entregados</p>
                <p className="text-sm text-slate-500">{data?.total_shipments || 0} total del mes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Shipments List */}
        <Card>
          <CardHeader>
            <CardTitle>Detalle de Envíos</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {shipments.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Receipt className="h-12 w-12 mx-auto mb-4 text-slate-300"/>
                <p>No hay envíos en este período</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Referencia</TableHead>
                    <TableHead>Ruta</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Precio (sin IVA)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shipments.map(s => (
                    <TableRow key={s.id}>
                      <TableCell className="text-sm">
                        <div>{s.date}</div>
                        {(s.scheduled_time || s.time_window) && (
                          <span className="text-xs text-slate-500">{s.scheduled_time || s.time_window}</span>
                        )}
                        {s.recurring_template_id && (
                          <span className="inline-block mt-1 text-xs text-purple-600">🔁</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{s.reference}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="truncate max-w-[200px]">{s.pickup_address}</p>
                          <p className="text-slate-500 truncate max-w-[200px]">→ {s.dropoff_address}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${statusColors[s.status] || 'bg-slate-100 text-slate-700'}`}>
                          {statusLabels[s.status] || s.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {s.status === 'DELIVERED' ? (
                          <span className="text-green-600">€{(s.price_vat_incl || 0).toFixed(2)}</span>
                        ) : (
                          <span className="text-slate-400">€{(s.price_vat_incl || 0).toFixed(2)}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* v1.1: Billing Conditions Modal */}
        <Dialog open={showConditions} onOpenChange={setShowConditions}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Condiciones de Facturación</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm text-slate-700">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-blue-800 mb-2">Reglas Generales</p>
                <ul className="list-disc list-inside space-y-1 text-karga-carbon">
                  <li>Facturación mensual basada únicamente en entregas completadas (DELIVERED).</li>
                  <li>Todos los precios mostrados son sin IVA.</li>
                </ul>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="font-medium text-green-800 mb-2">Plazos de Pago</p>
                <ul className="list-disc list-inside space-y-1 text-green-700">
                  <li>El total del mes debe abonarse antes del <strong>día 5 del mes siguiente</strong>.</li>
                  <li>Ejemplo: facturación de enero se paga antes del 5 de febrero.</li>
                </ul>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="font-medium text-red-800 mb-2">Impago</p>
                <ul className="list-disc list-inside space-y-1 text-red-700">
                  <li>En caso de impago, KargaExpress podrá suspender el servicio.</li>
                </ul>
              </div>
              <p className="text-xs text-slate-500 text-center">Legislación y jurisdicción española.</p>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowConditions(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// ==================== COMPANY PROFILE ====================
const CompanyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const { addToast } = useToast();

  useEffect(() => {
    axios.get(`${API}/companies/me/profile`).then(r => { setProfile(r.data); setForm(r.data || {}); });
  }, []);

  const handleSave = async () => {
    try {
      const r = await axios.patch(`${API}/companies/me/profile`, form);
      setProfile(r.data);
      setEditing(false);
      addToast("Perfil actualizado", "success");
    } catch { addToast("Error al guardar", "error"); }
  };

  return (
    <DashboardLayout role="COMPANY">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Datos de Empresa</h1>
          {!editing && <Button onClick={() => setEditing(true)} variant="outline">Editar</Button>}
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            {editing ? (
              <>
                <div><Label>Nombre comercial</Label><Input value={form.company_name || ""} onChange={e => setForm({...form, company_name: e.target.value})}/></div>
                <div><Label>Razón social</Label><Input value={form.legal_name || ""} onChange={e => setForm({...form, legal_name: e.target.value})}/></div>
                <div><Label>NIF/CIF</Label><Input value={form.tax_id || ""} onChange={e => setForm({...form, tax_id: e.target.value})}/></div>
                <div><Label>Dirección de facturación</Label><Input value={form.billing_address || ""} onChange={e => setForm({...form, billing_address: e.target.value})}/></div>
                <div><Label>Email de facturación</Label><Input value={form.billing_email || ""} onChange={e => setForm({...form, billing_email: e.target.value})}/></div>
                <div><Label>Teléfono</Label><Input value={form.phone || ""} onChange={e => setForm({...form, phone: e.target.value})}/></div>
                <div><Label>Persona de contacto</Label><Input value={form.contact_name || ""} onChange={e => setForm({...form, contact_name: e.target.value})}/></div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
                  <Button onClick={handleSave} style={{ backgroundColor: '#B4A798' }}>Guardar</Button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <div><span className="text-sm text-slate-500">Nombre comercial:</span><p className="font-medium">{profile?.company_name || "-"}</p></div>
                <div><span className="text-sm text-slate-500">Razón social:</span><p className="font-medium">{profile?.legal_name || "-"}</p></div>
                <div><span className="text-sm text-slate-500">NIF/CIF:</span><p className="font-medium">{profile?.tax_id || "-"}</p></div>
                <div><span className="text-sm text-slate-500">Dirección:</span><p className="font-medium">{profile?.billing_address || "-"}</p></div>
                <div><span className="text-sm text-slate-500">Email:</span><p className="font-medium">{profile?.billing_email || "-"}</p></div>
                <div><span className="text-sm text-slate-500">Teléfono:</span><p className="font-medium">{profile?.phone || "-"}</p></div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Push Notifications Settings */}
        <PushNotificationSettings />
      </div>
    </DashboardLayout>
  );
};

// ==================== ADMIN DASHBOARD ====================
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState(null);
  const [enhanced, setEnhanced] = useState({ kpis: {}, timeline: [], alerts: [] });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [monthlyStats, setMonthlyStats] = useState({ 
    company_receivables: 0, company_receivables_count: 0,
    driver_payables: 0, driver_payables_count: 0,
    total_margin: 0 
  });
  const [showCreateShipment, setShowCreateShipment] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [shipmentForm, setShipmentForm] = useState({
    pickup_address: "", pickup_contact_name: "", pickup_phone: "",
    dropoff_address: "", dropoff_contact_name: "", dropoff_phone: "",
    cargo_description: "", notes: "", vehicle_category: "MEDIUM",
    requires_isothermal: false, requires_freezer: false, requires_liftgate: false,
    schedule_datetime: "", pickup_time: "", delivery_time: ""
  });
  const [newCompanyForm, setNewCompanyForm] = useState({ name: "", email: "", tax_id: "", phone: "" });
  const [createdCompanyInfo, setCreatedCompanyInfo] = useState(null);
  const { addToast } = useToast();
  const navigate = useNavigate();
  
  // Get current month name in Spanish
  const currentMonthYear = useMemo(() => {
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const now = new Date();
    return `${months[now.getMonth()]} ${now.getFullYear()}`;
  }, []);

  // Load stats and calculate monthly totals
  useEffect(() => {
    axios.get(`${API}/admin/stats`).then(r => setStats(r.data));
    axios.get(`${API}/admin/daily-summary`).then(r => setDaily(r.data));
    axios.get(`${API}/admin/dashboard-enhanced`).then(r => setEnhanced(r.data)).catch(() => {});
    
    // Load DELIVERED shipments and filter by current month
    const loadMonthlyData = async () => {
      try {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        
        // Get all delivered shipments
        const res = await axios.get(`${API}/admin/shipments?status=DELIVERED&limit=500`);
        const shipments = res.data?.items || res.data || [];
        
        // Filter by current month (using delivered_at or updated_at)
        const monthlyShipments = shipments.filter(s => {
          const deliveryDate = new Date(s.delivered_at || s.updated_at || s.created_at);
          return deliveryDate >= firstDay && deliveryDate <= lastDay;
        });
        
        // Calculate totals
        let companyReceivables = 0;
        let companyCount = 0;
        let driverPayables = 0;
        let driverCount = 0;
        let totalMargin = 0;
        
        monthlyShipments.forEach(s => {
          // Por cobrar: DELIVERED + company_payment_status !== PAID
          if (s.company_payment_status !== 'PAID') {
            companyReceivables += (s.client_total_vat_incl || 0);
            companyCount++;
          }
          // Por pagar: DELIVERED + driver_payout_status !== PAID
          if (s.driver_payout_status !== 'PAID') {
            driverPayables += (s.driver_total_vat_incl || 0);
            driverCount++;
          }
          // Margen: de todos los DELIVERED del mes
          totalMargin += (s.margin_total || 0);
        });
        
        setMonthlyStats({
          company_receivables: companyReceivables,
          company_receivables_count: companyCount,
          driver_payables: driverPayables,
          driver_payables_count: driverCount,
          total_margin: totalMargin
        });
      } catch (err) {
        console.error('Error loading monthly stats:', err);
      }
    };
    
    loadMonthlyData();
  }, []);

  const loadCompanies = async () => {
    try {
      const res = await axios.get(`${API}/admin/companies/list`);
      setCompanies(res.data);
    } catch {}
  };

  // Global search
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/search?q=${encodeURIComponent(query)}`);
      setSearchResults(res.data.results || []);
    } catch {
      setSearchResults([]);
    }
  };

  // Format timeline event
  const formatTimelineEvent = (event) => {
    const time = new Date(event.time).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const eventLabels = {
      'REQUESTED': 'Nuevo envío creado',
      'PRICED': 'Presupuesto enviado',
      'COMPANY_ACCEPTED': 'Presupuesto aceptado',
      'OFFER_PUBLISHED': 'Publicado a conductores',
      'DRIVER_ASSIGNED': 'Conductor asignado',
      'PICKED_UP': 'Recogida confirmada',
      'IN_TRANSIT': 'En tránsito',
      'DELIVERED': 'Entrega completada',
      'DRIVER_UNASSIGNED': 'Conductor desasignado',
      'POD_UPLOADED': 'Foto subida'
    };
    let message = eventLabels[event.event_type] || event.event_type;
    if (event.driver_name && event.event_type === 'DRIVER_ASSIGNED') {
      message = `${event.driver_name} asignado`;
    }
    if (event.company_name) {
      message += ` - ${event.company_name}`;
    }
    return { time, message, type: event.event_type };
  };

  const openCreateShipmentModal = () => {
    loadCompanies();
    setShowCreateShipment(true);
  };

  const handleCreateCompany = async () => {
    try {
      const res = await axios.post(`${API}/admin/companies`, newCompanyForm);
      addToast("Empresa creada correctamente", "success");
      setCreatedCompanyInfo(res.data);
      setShowCreateCompany(false);
      setNewCompanyForm({ name: "", email: "", tax_id: "", phone: "" });
      loadCompanies();
      setSelectedCompanyId(res.data.id);
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al crear empresa", "error");
    }
  };

  const handleCreateShipment = async () => {
    if (!selectedCompanyId) { addToast("Selecciona una empresa", "error"); return; }
    if (!shipmentForm.pickup_address || !shipmentForm.dropoff_address) { 
      addToast("Dirección de recogida y entrega son obligatorios", "error"); return; 
    }
    try {
      await axios.post(`${API}/admin/shipments`, { ...shipmentForm, company_id: selectedCompanyId });
      addToast("Envío creado correctamente", "success");
      setShowCreateShipment(false);
      setSelectedCompanyId("");
      setShipmentForm({
        pickup_address: "", pickup_contact_name: "", pickup_phone: "",
        dropoff_address: "", dropoff_contact_name: "", dropoff_phone: "",
        cargo_description: "", notes: "", vehicle_category: "MEDIUM",
        requires_isothermal: false, requires_freezer: false, requires_liftgate: false,
        schedule_datetime: "", pickup_time: "", delivery_time: ""
      });
      setCreatedCompanyInfo(null);
      // Refresh stats
      axios.get(`${API}/admin/stats`).then(r => setStats(r.data));
      navigate("/admin/requests");
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al crear envío", "error");
    }
  };

  return (
    <DashboardLayout role="ADMIN">
      <div className="space-y-6">
        {/* Header with Search */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <h1 className="text-2xl font-bold text-slate-800">Panel de Control</h1>
          <div className="flex items-center gap-3 w-full lg:w-auto">
            {/* Global Search */}
            <div className="relative flex-1 lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"/>
              <Input 
                placeholder="Buscar envío, empresa, conductor..." 
                className="pl-10 pr-4"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => setSearchOpen(true)}
                data-testid="global-search"
              />
              {searchOpen && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                  {searchResults.map((r, i) => (
                    <div 
                      key={i} 
                      className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                      onClick={() => { navigate(r.link); setSearchOpen(false); setSearchQuery(""); }}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          r.type === 'shipment' ? 'bg-blue-100 text-karga-carbon' :
                          r.type === 'company' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          {r.type === 'shipment' ? 'ENV' : r.type === 'company' ? 'EMP' : 'DRV'}
                        </span>
                        <span className="font-medium text-sm">{r.title}</span>
                        {r.status && <StatusPill status={r.status} role="ADMIN"/>}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{r.subtitle}</p>
                      {r.extra && <p className="text-xs text-slate-400">{r.extra}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={openCreateShipmentModal} className="whitespace-nowrap bg-karga-carbon hover:bg-karga-dark">
              <Phone className="h-4 w-4 mr-2"/>Nuevo Envío
            </Button>
          </div>
        </div>

        {/* Alerts Banner */}
        {enhanced.alerts?.length > 0 && (
          <div className="space-y-2" data-testid="alerts-banner">
            {enhanced.alerts.map((alert, i) => (
              <div 
                key={i}
                onClick={() => navigate(alert.link)}
                className={`p-3 rounded-lg flex items-center gap-3 cursor-pointer transition-all hover:shadow-md ${
                  alert.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
                  alert.type === 'warning' ? 'bg-amber-50 border border-amber-200 text-amber-800' :
                  'bg-blue-50 border border-blue-200 text-blue-800'
                }`}
              >
                {alert.icon === 'users' && <Users className="h-5 w-5"/>}
                {alert.icon === 'alert' && <AlertCircle className="h-5 w-5"/>}
                {alert.icon === 'clock' && <Clock className="h-5 w-5"/>}
                {alert.icon === 'inbox' && <Inbox className="h-5 w-5"/>}
                <span className="font-medium">{alert.message}</span>
                <ChevronRight className="h-4 w-4 ml-auto"/>
              </div>
            ))}
          </div>
        )}

        {/* KPI Cards - Estado en 5 segundos */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Card className="cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5" onClick={() => navigate("/admin/shipments")} data-testid="kpi-today">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center bg-slate-100 mb-2">
                <Package className="h-5 w-5 text-slate-600"/>
              </div>
              <p className="text-3xl font-bold text-slate-800">{enhanced.kpis?.today_shipments || daily?.today_created || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Envíos hoy</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5 border-amber-200 bg-amber-50/30" onClick={() => navigate("/admin/shipments?status=OFFER_PUBLISHED")} data-testid="kpi-pending">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center bg-amber-100 mb-2">
                <Users className="h-5 w-5 text-amber-600"/>
              </div>
              <p className="text-3xl font-bold text-amber-600">{enhanced.kpis?.pending_assign || stats?.offer_published || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Sin asignar</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5 border-blue-200 bg-blue-50/30" onClick={() => navigate("/admin/shipments?status=IN_TRANSIT")} data-testid="kpi-transit">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center bg-blue-100 mb-2">
                <Truck className="h-5 w-5 text-karga-dark"/>
              </div>
              <p className="text-3xl font-bold text-karga-dark">{enhanced.kpis?.in_transit || stats?.in_progress || 0}</p>
              <p className="text-xs text-slate-500 mt-1">En tránsito</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5 border-green-200 bg-green-50/30" onClick={() => navigate("/admin/shipments?status=DELIVERED")} data-testid="kpi-delivered">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center bg-green-100 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600"/>
              </div>
              <p className="text-3xl font-bold text-green-600">{enhanced.kpis?.delivered_today || daily?.today_delivered || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Entregados hoy</p>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5 ${(enhanced.kpis?.incidences || 0) > 0 ? 'border-red-200 bg-red-50/30' : ''}`} data-testid="kpi-incidents">
            <CardContent className="p-4 text-center">
              <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${(enhanced.kpis?.incidences || 0) > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                <AlertCircle className={`h-5 w-5 ${(enhanced.kpis?.incidences || 0) > 0 ? 'text-red-600' : 'text-slate-400'}`}/>
              </div>
              <p className={`text-3xl font-bold ${(enhanced.kpis?.incidences || 0) > 0 ? 'text-red-600' : 'text-slate-400'}`}>{enhanced.kpis?.incidences || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Incidencias</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Grid: Timeline + Finance */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Activity Timeline */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="h-5 w-5 text-slate-400"/>
                Actividad Reciente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {enhanced.timeline?.length > 0 ? enhanced.timeline.slice(0, 12).map((event, i) => {
                  const { time, message, type } = formatTimelineEvent(event);
                  return (
                    <div 
                      key={i} 
                      className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 px-2 rounded cursor-pointer"
                      onClick={() => event.shipment_id && navigate(`/admin/shipments/${event.shipment_id}`)}
                    >
                      <span className="text-xs text-slate-400 font-mono w-12 flex-shrink-0">{time}</span>
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        type === 'DELIVERED' ? 'bg-green-500' :
                        type === 'DRIVER_ASSIGNED' ? 'bg-blue-500' :
                        type === 'IN_TRANSIT' || type === 'PICKED_UP' ? 'bg-indigo-500' :
                        type === 'REQUESTED' ? 'bg-amber-500' : 'bg-slate-300'
                      }`}/>
                      <p className="text-sm text-slate-600 flex-1">{message}</p>
                    </div>
                  );
                }) : (
                  <p className="text-sm text-slate-400 text-center py-8">Sin actividad reciente</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Finance Summary */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Euro className="h-5 w-5 text-slate-400"/>
                  {currentMonthYear}
                  <span className="text-xs font-normal text-slate-400 ml-auto">sin IVA</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                  <span className="text-sm text-slate-600">Por Cobrar</span>
                  <div className="text-right">
                    <p className="font-bold text-orange-600">€{(monthlyStats.company_receivables || 0).toFixed(0)}</p>
                    <p className="text-xs text-slate-400">{monthlyStats.company_receivables_count || 0} envíos</p>
                  </div>
                </div>
                <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                  <span className="text-sm text-slate-600">Por Pagar</span>
                  <div className="text-right">
                    <p className="font-bold text-red-600">€{(monthlyStats.driver_payables || 0).toFixed(0)}</p>
                    <p className="text-xs text-slate-400">{monthlyStats.driver_payables_count || 0} envíos</p>
                  </div>
                </div>
                <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                  <span className="text-sm text-slate-600">Margen</span>
                  <p className="font-bold text-green-600">€{(monthlyStats.total_margin || 0).toFixed(0)}</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Quick Stats */}
            <Card>
              <CardContent className="p-4 grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-karga-dark">€{(daily?.today_revenue || 0).toFixed(0)}</p>
                  <p className="text-xs text-slate-500">Ingresos hoy</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">€{(daily?.today_margin || 0).toFixed(0)}</p>
                  <p className="text-xs text-slate-500">Margen hoy</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Pipeline Overview */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Pipeline de Envíos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-50 cursor-pointer hover:bg-yellow-100 transition-colors" onClick={() => navigate("/admin/requests")}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-yellow-100"><Inbox className="h-5 w-5 text-yellow-600"/></div>
                <div><p className="text-sm text-slate-500">Solicitudes</p><p className="text-xl font-bold">{stats?.requested || 0}</p></div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => navigate("/admin/shipments?status=PRICED")}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100"><Send className="h-5 w-5 text-karga-dark"/></div>
                <div><p className="text-sm text-slate-500">Presupuestados</p><p className="text-xl font-bold">{stats?.priced || 0}</p></div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-purple-50 cursor-pointer hover:bg-purple-100 transition-colors" onClick={() => navigate("/admin/shipments?status=DRIVER_ASSIGNED")}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-100"><UserCheck className="h-5 w-5 text-purple-600"/></div>
                <div><p className="text-sm text-slate-500">Asignados</p><p className="text-xl font-bold">{stats?.driver_assigned || 0}</p></div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 cursor-pointer hover:bg-green-100 transition-colors" onClick={() => navigate("/admin/shipments?status=DELIVERED")}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-100"><CheckCircle className="h-5 w-5 text-green-600"/></div>
                <div><p className="text-sm text-slate-500">Total Entregados</p><p className="text-xl font-bold">{stats?.delivered || 0}</p></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Shipment Modal */}
      <Dialog open={showCreateShipment} onOpenChange={setShowCreateShipment}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Phone className="h-5 w-5"/>Crear Envío (Pedido Telefónico)</DialogTitle>
          </DialogHeader>
          
          {/* Company Selection */}
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium">Seleccionar Empresa</Label>
              <div className="flex gap-2 mt-2">
                <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Selecciona una empresa"/></SelectTrigger>
                  <SelectContent>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => setShowCreateCompany(true)}>
                  <Plus className="h-4 w-4 mr-1"/>Nueva
                </Button>
              </div>
              {createdCompanyInfo && (
                <div className="mt-2 p-2 bg-green-50 text-green-800 rounded text-sm">
                  Empresa creada: {createdCompanyInfo.name}. Contraseña temporal: <code className="bg-green-100 px-1 rounded">{createdCompanyInfo.temp_password}</code>
                </div>
              )}
            </div>

            {/* Shipment Form */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-slate-700">Recogida</h4>
                <Input placeholder="Dirección de recogida *" value={shipmentForm.pickup_address} 
                  onChange={e => setShipmentForm({...shipmentForm, pickup_address: e.target.value})}/>
                <Input placeholder="Nombre contacto" value={shipmentForm.pickup_contact_name} 
                  onChange={e => setShipmentForm({...shipmentForm, pickup_contact_name: e.target.value})}/>
                <Input placeholder="Teléfono" value={shipmentForm.pickup_phone} 
                  onChange={e => setShipmentForm({...shipmentForm, pickup_phone: e.target.value})}/>
              </div>
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-slate-700">Entrega</h4>
                <Input placeholder="Dirección de entrega *" value={shipmentForm.dropoff_address} 
                  onChange={e => setShipmentForm({...shipmentForm, dropoff_address: e.target.value})}/>
                <Input placeholder="Nombre contacto" value={shipmentForm.dropoff_contact_name} 
                  onChange={e => setShipmentForm({...shipmentForm, dropoff_contact_name: e.target.value})}/>
                <Input placeholder="Teléfono" value={shipmentForm.dropoff_phone} 
                  onChange={e => setShipmentForm({...shipmentForm, dropoff_phone: e.target.value})}/>
              </div>
            </div>

            <div>
              <Label>Descripción de la carga</Label>
              <Textarea placeholder="Descripción de la mercancía..." value={shipmentForm.cargo_description}
                onChange={e => setShipmentForm({...shipmentForm, cargo_description: e.target.value})}/>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Tipo de vehículo</Label>
                <Select value={shipmentForm.vehicle_category} onValueChange={v => setShipmentForm({...shipmentForm, vehicle_category: v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SMALL">Furgoneta pequeña</SelectItem>
                    <SelectItem value="MEDIUM">Furgón mediano</SelectItem>
                    <SelectItem value="LARGE">Furgón grande</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Fecha y hora de recogida */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha de recogida</Label>
                  <Input type="date" value={shipmentForm.schedule_datetime?.split('T')[0] || ''} 
                    onChange={e => setShipmentForm({...shipmentForm, schedule_datetime: e.target.value ? `${e.target.value}T09:00:00` : ''})}/>
                </div>
                <div>
                  <Label>Hora de recogida (opcional)</Label>
                  <Input type="time" value={shipmentForm.pickup_time} placeholder="HH:MM"
                    onChange={e => setShipmentForm({...shipmentForm, pickup_time: e.target.value})}/>
                </div>
              </div>
              <div>
                <Label>Hora estimada de entrega (opcional)</Label>
                <Input type="time" value={shipmentForm.delivery_time} placeholder="HH:MM"
                  onChange={e => setShipmentForm({...shipmentForm, delivery_time: e.target.value})}/>
              </div>
              
              <div className="space-y-2">
                <Label>Requisitos especiales</Label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={shipmentForm.requires_isothermal} onCheckedChange={v => setShipmentForm({...shipmentForm, requires_isothermal: v})}/>
                    <Thermometer className="h-4 w-4 text-blue-500"/>Frío positivo
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={shipmentForm.requires_freezer} onCheckedChange={v => setShipmentForm({...shipmentForm, requires_freezer: v})}/>
                    <Snowflake className="h-4 w-4 text-indigo-500"/>Congelación
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={shipmentForm.requires_liftgate} onCheckedChange={v => setShipmentForm({...shipmentForm, requires_liftgate: v})}/>
                    <ArrowUp className="h-4 w-4 text-amber-500"/>Plataforma elevadora
                  </label>
                </div>
              </div>
            </div>

            <div>
              <Label>Notas adicionales</Label>
              <Input placeholder="Notas..." value={shipmentForm.notes} 
                onChange={e => setShipmentForm({...shipmentForm, notes: e.target.value})}/>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateShipment(false)}>Cancelar</Button>
            <Button onClick={handleCreateShipment} style={{ backgroundColor: '#B4A798' }}>
              <Plus className="h-4 w-4 mr-1"/>Crear Envío
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Company Modal */}
      <Dialog open={showCreateCompany} onOpenChange={setShowCreateCompany}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nueva Empresa</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre de la empresa *</Label>
              <Input value={newCompanyForm.name} onChange={e => setNewCompanyForm({...newCompanyForm, name: e.target.value})}/>
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={newCompanyForm.email} onChange={e => setNewCompanyForm({...newCompanyForm, email: e.target.value})}/>
            </div>
            <div>
              <Label>NIF/CIF</Label>
              <Input value={newCompanyForm.tax_id} onChange={e => setNewCompanyForm({...newCompanyForm, tax_id: e.target.value})}/>
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input value={newCompanyForm.phone} onChange={e => setNewCompanyForm({...newCompanyForm, phone: e.target.value})}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCompany(false)}>Cancelar</Button>
            <Button onClick={handleCreateCompany} disabled={!newCompanyForm.name || !newCompanyForm.email} style={{ backgroundColor: '#B4A798' }}>
              Crear Empresa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// ==================== ADMIN REQUESTS (INBOX) ====================
const AdminRequests = () => {
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [quoteForm, setQuoteForm] = useState({ client_total_vat_incl: "", driver_total_vat_incl: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addToast } = useToast();

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    console.log('[AdminRequests] Loading requests...');
    try {
      const r = await axios.get(`${API}/shipments?status=REQUESTED`);
      // Handle paginated response
      const data = r.data.items || r.data || [];
      console.log('[AdminRequests] Loaded', data.length, 'requests');
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[AdminRequests] Error loading:', err.message);
      setError(err.userMessage || 'Error al cargar las solicitudes');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const handleQuote = async () => {
    try {
      // Use new IVA fields
      await axios.post(`${API}/shipments/${selectedRequest.id}/quote`, {
        client_total_vat_incl: parseFloat(quoteForm.client_total_vat_incl),
        driver_total_vat_incl: parseFloat(quoteForm.driver_total_vat_incl),
      });
      addToast("Presupuesto enviado", "success");
      setSelectedRequest(null);
      setQuoteForm({ client_total_vat_incl: "", driver_total_vat_incl: "" });
      loadRequests();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    }
  };

  // Calculate margin for preview (sin IVA)
  const clientTotal = parseFloat(quoteForm.client_total_vat_incl) || 0;
  const driverTotal = parseFloat(quoteForm.driver_total_vat_incl) || 0;
  const margin = clientTotal > 0 && driverTotal > 0 ? (clientTotal - driverTotal).toFixed(2) : "0.00";

  return (
    <DashboardLayout role="ADMIN">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Solicitudes Nuevas</h1>
          <Button size="sm" variant="outline" onClick={loadRequests} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {/* Loading State */}
        {loading && requests.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400 mb-2" />
              <p className="text-slate-500">Cargando solicitudes...</p>
            </CardContent>
          </Card>
        )}
        
        {/* Error State */}
        {error && (
          <Card className="border-red-200">
            <CardContent className="p-6 text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-700 mb-4">{error}</p>
              <Button onClick={loadRequests} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Reintentar
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Empty State */}
        {!loading && !error && requests.length === 0 && (
          <Card><CardContent className="p-8 text-center text-slate-500">No hay solicitudes pendientes</CardContent></Card>
        )}
        
        {/* Data List */}
        {!error && requests.length > 0 && (
          <div className="space-y-4">
            {requests.map(r => (
              <Card key={r.id}>
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium">{r.company_name || 'Empresa'}</span>
                        {r.service_type === "URGENT" && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">URGENTE</span>}
                        <VehicleBadge category={r.vehicle_category}/>
                      </div>
                      <p className="text-sm"><MapPin className="h-4 w-4 inline mr-1 text-green-600"/>{r.pickup_address || 'Sin dirección'}</p>
                      <p className="text-sm"><MapPin className="h-4 w-4 inline mr-1 text-red-600"/>{r.dropoff_address || 'Sin dirección'}</p>
                      {r.cargo_description && <p className="text-sm text-slate-600 mt-2">{r.cargo_description}</p>}
                      <RequirementBadges isothermal={r.requires_isothermal} freezer={r.requires_freezer} liftgate={r.requires_liftgate}/>
                    </div>
                    <Button onClick={() => setSelectedRequest(r)} style={{ backgroundColor: '#B4A798' }}>
                      <Euro className="h-4 w-4 mr-1"/>Presupuestar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Quote Dialog */}
        <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Establecer Presupuesto</DialogTitle></DialogHeader>
            {selectedRequest && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <p><strong>{selectedRequest.company_name}</strong></p>
                  <p>{selectedRequest.pickup_address} → {selectedRequest.dropoff_address}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Total Cliente (€, sin IVA)</Label>
                    <Input type="number" step="0.01" value={quoteForm.client_total_vat_incl} 
                      onChange={e => setQuoteForm({...quoteForm, client_total_vat_incl: e.target.value})} placeholder="100.00"/>
                  </div>
                  <div>
                    <Label>Total Conductor (€, sin IVA)</Label>
                    <Input type="number" step="0.01" value={quoteForm.driver_total_vat_incl} 
                      onChange={e => setQuoteForm({...quoteForm, driver_total_vat_incl: e.target.value})} placeholder="70.00"/>
                  </div>
                </div>
                <p className="text-xs text-slate-500">Introduce los precios sin IVA. Margen = Cliente − Conductor.</p>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800">Margen KargaExpress (sin IVA): <strong>€{margin}</strong></p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedRequest(null)}>Cancelar</Button>
              <Button onClick={handleQuote} disabled={!quoteForm.client_total_vat_incl || !quoteForm.driver_total_vat_incl || driverTotal >= clientTotal} style={{ backgroundColor: '#B4A798' }}>
                <Send className="h-4 w-4 mr-1"/>Enviar Presupuesto
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// ==================== ADMIN SHIPMENTS ====================
const AdminShipments = () => {
  const [shipments, setShipments] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get('status') || "");
  const [dateFilter, setDateFilter] = useState("month"); // today, week, month, all
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [assignModal, setAssignModal] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState("");
  // Phase 1: Multi-select and tags
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAssignModal, setBulkAssignModal] = useState(false);
  const [tagsModal, setTagsModal] = useState(null);
  const [adminNotesModal, setAdminNotesModal] = useState(null);
  const [availableTags, setAvailableTags] = useState({});
  const { addToast } = useToast();
  const navigate = useNavigate();

  // Load approved drivers for assignment
  useEffect(() => {
    axios.get(`${API}/drivers?limit=100`).then(r => {
      const approved = (r.data.items || r.data).filter(d => 
        d.profile?.approval_status === 'ACTIVE' || d.profile?.approval_status === 'APPROVED'
      );
      setDrivers(approved);
    }).catch(() => {});
    // Load available tags
    axios.get(`${API}/shipments/tags`).then(r => setAvailableTags(r.data.tags || {})).catch(() => {});
  }, []);

  // Sync filter with URL params
  useEffect(() => {
    const urlStatus = searchParams.get('status');
    if (urlStatus && urlStatus !== filter) {
      setFilter(urlStatus);
    }
  }, [searchParams]);

  // Update URL when filter changes
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    if (newFilter) {
      setSearchParams({ status: newFilter });
    } else {
      setSearchParams({});
    }
  };

  // Date filter helper
  const getDateRange = () => {
    const now = new Date();
    if (dateFilter === "today") {
      return now.toISOString().split('T')[0];
    } else if (dateFilter === "week") {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return weekAgo.toISOString().split('T')[0];
    } else if (dateFilter === "month") {
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    }
    return null;
  };

  const loadShipments = useCallback(async (pageNum = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pageNum, limit: 20 });
      if (filter) params.append('status', filter);
      const dateFrom = getDateRange();
      if (dateFrom && dateFilter !== "all") params.append('date_from', dateFrom);
      const r = await axios.get(`${API}/shipments?${params}`);
      setShipments(r.data.items || r.data);
      setPagination({ total: r.data.total || 0, pages: r.data.pages || 1 });
    } catch (err) {
      addToast("Error cargando envíos", "error");
    } finally {
      setLoading(false);
    }
  }, [filter, page, dateFilter, addToast]);

  useEffect(() => { loadShipments(1); setPage(1); }, [filter, dateFilter]);
  
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    loadShipments(newPage);
  }, [loadShipments]);

  const handlePublish = useCallback(async (id) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/shipments/${id}/publish`);
      addToast("Publicado a conductores", "success");
      loadShipments(page);
    } catch (err) { addToast(err.response?.data?.detail || "Error", "error"); }
    finally { setActionLoading(null); }
  }, [addToast, loadShipments, page]);

  // Assign driver to shipment
  const handleAssign = async () => {
    if (!assignModal || !selectedDriver) return;
    setActionLoading(assignModal.id);
    try {
      await axios.post(`${API}/shipments/${assignModal.id}/assign-driver`, { driver_id: selectedDriver });
      addToast("Conductor asignado", "success");
      setAssignModal(null);
      setSelectedDriver("");
      loadShipments(page);
    } catch (err) { 
      addToast(err.response?.data?.detail || "Error al asignar", "error"); 
    } finally { 
      setActionLoading(null); 
    }
  };

  // Unassign driver from shipment
  const handleUnassign = async (shipmentId) => {
    if (!window.confirm("¿Desasignar el conductor? El envío volverá a disponibles.")) return;
    setActionLoading(shipmentId);
    try {
      await axios.post(`${API}/shipments/${shipmentId}/unassign`);
      addToast("Conductor desasignado", "success");
      loadShipments(page);
    } catch (err) { 
      addToast(err.response?.data?.detail || "Error al desasignar", "error"); 
    } finally { 
      setActionLoading(null); 
    }
  };

  // Delete shipment
  const handleDelete = async (shipmentId) => {
    if (!window.confirm("¿Eliminar este envío? Esta acción no se puede deshacer.")) return;
    setActionLoading(shipmentId);
    try {
      await axios.delete(`${API}/shipments/${shipmentId}`);
      addToast("Envío eliminado", "success");
      loadShipments(page);
    } catch (err) { 
      addToast(err.response?.data?.detail || "Error al eliminar", "error"); 
    } finally { 
      setActionLoading(null); 
    }
  };

  // === PHASE 1: New operations ===
  
  // Toggle selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  // Select all visible
  const toggleSelectAll = () => {
    if (selectedIds.length === shipments.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(shipments.map(s => s.id));
    }
  };

  // Bulk assign
  const handleBulkAssign = async () => {
    if (!selectedDriver || selectedIds.length === 0) return;
    setActionLoading("bulk");
    try {
      const res = await axios.post(`${API}/shipments/bulk-assign`, {
        shipment_ids: selectedIds,
        driver_id: selectedDriver
      });
      addToast(res.data.message, "success");
      setSelectedIds([]);
      setBulkAssignModal(false);
      setSelectedDriver("");
      loadShipments(page);
    } catch (err) {
      addToast(err.response?.data?.detail || "Error en asignación", "error");
    } finally {
      setActionLoading(null);
    }
  };

  // Duplicate shipment
  const handleDuplicate = async (shipmentId) => {
    setActionLoading(shipmentId);
    try {
      const res = await axios.post(`${API}/shipments/${shipmentId}/duplicate`);
      addToast(`Envío duplicado: ${res.data.reference}`, "success");
      loadShipments(page);
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al duplicar", "error");
    } finally {
      setActionLoading(null);
    }
  };

  // Update tags
  const handleUpdateTags = async (newTags) => {
    if (!tagsModal) return;
    try {
      await axios.patch(`${API}/shipments/${tagsModal.id}/tags`, { tags: newTags });
      addToast("Etiquetas actualizadas", "success");
      setTagsModal(null);
      loadShipments(page);
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    }
  };

  // Update admin notes
  const handleUpdateNotes = async (notes) => {
    if (!adminNotesModal) return;
    try {
      await axios.patch(`${API}/shipments/${adminNotesModal.id}/admin-notes`, { admin_notes: notes });
      addToast("Notas actualizadas", "success");
      setAdminNotesModal(null);
      loadShipments(page);
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    }
  };

  // Tag colors
  const tagColors = {
    URGENT: "bg-red-100 text-red-700 border-red-200",
    REFRIGERATED: "bg-blue-100 text-karga-carbon border-blue-200",
    FROZEN: "bg-cyan-100 text-cyan-700 border-cyan-200",
    DOCUMENTATION: "bg-purple-100 text-purple-700 border-purple-200",
    SANITARY: "bg-green-100 text-green-700 border-green-200",
    LIFTGATE: "bg-orange-100 text-orange-700 border-orange-200",
    FRAGILE: "bg-yellow-100 text-yellow-700 border-yellow-200"
  };

  // Format date helper
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const shipmentRows = useMemo(() => shipments.map(s => (
    <TableRow key={s.id} className={selectedIds.includes(s.id) ? "bg-blue-50" : ""}>
      {/* Checkbox for multi-select */}
      <TableCell className="w-10">
        <input 
          type="checkbox" 
          checked={selectedIds.includes(s.id)}
          onChange={() => toggleSelect(s.id)}
          className="rounded border-slate-300"
        />
      </TableCell>
      <TableCell>
        <button 
          className="font-medium text-left hover:text-karga-dark hover:underline cursor-pointer"
          onClick={() => s.company_id && navigate(`/admin/companies/${s.company_id}`)}
        >
          {s.company_name || 'N/A'}
        </button>
        <p className="text-xs text-slate-400">{s.reference || s.id?.substring(0, 8)}</p>
        {/* Tags display */}
        {s.tags?.length > 0 && (
          <div className="flex gap-1 mt-1 flex-wrap">
            {s.tags.map(tag => (
              <span key={tag} className={`text-xs px-1.5 py-0.5 rounded border ${tagColors[tag] || 'bg-slate-100'}`}>
                {availableTags[tag]?.label || tag}
              </span>
            ))}
          </div>
        )}
      </TableCell>
      <TableCell>
        {s.driver_name ? (
          <button 
            className="text-left hover:text-orange-600 hover:underline cursor-pointer font-medium"
            onClick={() => s.driver_id && navigate(`/admin/drivers/${s.driver_id}`)}
          >
            {s.driver_name}
          </button>
        ) : (
          <span className="text-slate-400 text-sm">Sin asignar</span>
        )}
      </TableCell>
      <TableCell className="text-sm max-w-[200px] truncate" title={`${s.pickup_address} → ${s.dropoff_address}`}>
        {s.pickup_address?.substring(0, 15)}... → {s.dropoff_address?.substring(0, 15)}...
      </TableCell>
      <TableCell><StatusPill status={s.status} role="ADMIN"/></TableCell>
      <TableCell className="text-xs text-slate-500">{formatDateTime(s.updated_at)}</TableCell>
      <TableCell>€{s.client_total_vat_incl?.toFixed(2) || "-"}</TableCell>
      <TableCell className="text-green-600 font-medium">€{s.margin_total?.toFixed(2) || "-"}</TableCell>
      <TableCell>
        <div className="flex gap-1 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => navigate(`/admin/shipments/${s.id}`)} title="Ver detalle">
            <Eye className="h-3 w-3"/>
          </Button>
          {/* Duplicate */}
          <Button size="sm" variant="outline" onClick={() => handleDuplicate(s.id)} title="Duplicar envío" disabled={actionLoading === s.id}>
            <Plus className="h-3 w-3"/>
          </Button>
          {/* Tags */}
          <Button size="sm" variant="outline" onClick={() => setTagsModal(s)} title="Etiquetas">
            <FileText className="h-3 w-3"/>
          </Button>
          {/* Assign - for published shipments without driver */}
          {['OFFER_PUBLISHED', 'COMPANY_ACCEPTED', 'PRICED'].includes(s.status) && !s.driver_id && (
            <Button size="sm" variant="outline" onClick={() => setAssignModal(s)} title="Asignar conductor" className="text-karga-dark">
              <UserCheck className="h-3 w-3"/>
            </Button>
          )}
          {/* Unassign - for assigned shipments not yet picked up */}
          {s.driver_id && ['DRIVER_ASSIGNED', 'OFFER_PUBLISHED'].includes(s.status) && (
            <Button size="sm" variant="outline" onClick={() => handleUnassign(s.id)} title="Desasignar" className="text-orange-600" disabled={actionLoading === s.id}>
              <XCircle className="h-3 w-3"/>
            </Button>
          )}
          {/* Publish - for accepted quotes */}
          {s.status === "COMPANY_ACCEPTED" && (
            <LoadingButton size="sm" onClick={() => handlePublish(s.id)} loading={actionLoading === s.id} style={{ backgroundColor: '#7C3AED' }} title="Publicar a conductores">
              <Play className="h-3 w-3"/>
            </LoadingButton>
          )}
          {/* Delete - only for non-delivered */}
          {!['DELIVERED', 'IN_TRANSIT', 'PICKED_UP'].includes(s.status) && (
            <Button size="sm" variant="ghost" onClick={() => handleDelete(s.id)} title="Eliminar" className="text-red-600" disabled={actionLoading === s.id}>
              <XCircle className="h-3 w-3"/>
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )), [shipments, navigate, handlePublish, handleUnassign, handleDelete, handleDuplicate, actionLoading, selectedIds, availableTags, tagColors]);

  return (
    <DashboardLayout role="ADMIN">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Todos los Envíos</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => loadShipments(page)} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {/* Date filter */}
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Hoy</SelectItem>
                <SelectItem value="week">Semana</SelectItem>
                <SelectItem value="month">Mes</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
            {/* Status filter */}
            <Select value={filter || "ALL"} onValueChange={v => handleFilterChange(v === "ALL" ? "" : v)}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Todos los estados"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos</SelectItem>
                <SelectItem value="REQUESTED">Solicitudes</SelectItem>
                <SelectItem value="PRICED">Presupuestados</SelectItem>
                <SelectItem value="COMPANY_ACCEPTED">Aceptados</SelectItem>
                <SelectItem value="OFFER_PUBLISHED">Disponibles</SelectItem>
                <SelectItem value="DRIVER_ASSIGNED">Asignados</SelectItem>
                <SelectItem value="IN_TRANSIT">En tránsito</SelectItem>
                <SelectItem value="DELIVERED">Entregados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {/* Bulk actions bar */}
            {selectedIds.length > 0 && (
              <div className="p-3 bg-blue-50 border-b flex items-center justify-between">
                <span className="text-sm text-karga-carbon font-medium">
                  {selectedIds.length} envío{selectedIds.length > 1 ? 's' : ''} seleccionado{selectedIds.length > 1 ? 's' : ''}
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setSelectedIds([])}>
                    Deseleccionar
                  </Button>
                  <Button size="sm" onClick={() => setBulkAssignModal(true)} className="bg-karga-carbon hover:bg-karga-dark text-white">
                    <UserCheck className="h-4 w-4 mr-1"/>Asignar conductor
                  </Button>
                </div>
              </div>
            )}
            {loading && shipments.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input 
                        type="checkbox" 
                        checked={selectedIds.length === shipments.length && shipments.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-slate-300"
                      />
                    </TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Repartidor</TableHead>
                    <TableHead>Ruta</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Actualizado</TableHead>
                    <TableHead>Precio</TableHead>
                    <TableHead>Margen</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shipmentRows}
                </TableBody>
              </Table>
            )}
          </CardContent>
          <Pagination page={page} pages={pagination.pages} total={pagination.total} onPageChange={handlePageChange} loading={loading} />
        </Card>
      </div>
      
      {/* Assign Driver Modal */}
      <Dialog open={!!assignModal} onOpenChange={() => setAssignModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignar Conductor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <p className="font-medium">{assignModal?.company_name}</p>
              <p className="text-slate-500">{assignModal?.pickup_address?.substring(0, 30)}... → {assignModal?.dropoff_address?.substring(0, 30)}...</p>
            </div>
            <div>
              <Label>Seleccionar Conductor</Label>
              <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                <SelectTrigger><SelectValue placeholder="Elegir conductor..."/></SelectTrigger>
                <SelectContent>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} - {d.profile?.vehicle_category || 'N/A'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModal(null)}>Cancelar</Button>
            <LoadingButton onClick={handleAssign} loading={actionLoading === assignModal?.id} disabled={!selectedDriver} style={{ backgroundColor: '#B4A798' }}>
              Asignar
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Modal */}
      <Dialog open={bulkAssignModal} onOpenChange={() => setBulkAssignModal(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignación Múltiple</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-blue-800">{selectedIds.length} envío{selectedIds.length > 1 ? 's' : ''} seleccionado{selectedIds.length > 1 ? 's' : ''}</p>
            </div>
            <div>
              <Label>Seleccionar conductor</Label>
              <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Elegir conductor..." /></SelectTrigger>
                <SelectContent>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} - {d.profile?.vehicle_category || 'N/A'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignModal(false)}>Cancelar</Button>
            <LoadingButton onClick={handleBulkAssign} loading={actionLoading === "bulk"} disabled={!selectedDriver} className="bg-karga-carbon hover:bg-karga-dark">
              Asignar a todos
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tags Modal */}
      <Dialog open={!!tagsModal} onOpenChange={() => setTagsModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Etiquetas del envío</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">{tagsModal?.company_name} - {tagsModal?.reference}</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(availableTags).map(([key, val]) => {
                const isSelected = tagsModal?.tags?.includes(key);
                return (
                  <button
                    key={key}
                    onClick={() => {
                      const currentTags = tagsModal?.tags || [];
                      const newTags = isSelected 
                        ? currentTags.filter(t => t !== key)
                        : [...currentTags, key];
                      handleUpdateTags(newTags);
                    }}
                    className={`p-2 rounded-lg border text-sm text-left transition-all ${
                      isSelected 
                        ? `${tagColors[key]} border-2` 
                        : 'bg-slate-50 hover:bg-slate-100 border-slate-200'
                    }`}
                  >
                    {val.label}
                  </button>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagsModal(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// ==================== ADMIN OPERATIONS (Jornadas + Quick Work) ====================
const AdminOperations = () => {
  const [activeView, setActiveView] = useState("today"); // today, jornadas, quick
  const [summary, setSummary] = useState(null);
  const [jornadas, setJornadas] = useState([]);
  const [selectedJornada, setSelectedJornada] = useState(null);
  const [jornadaShipments, setJornadaShipments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [availableTags, setAvailableTags] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  // Quick shipment form
  const [quickForm, setQuickForm] = useState({
    company_id: "",
    dropoff_address: "",
    dropoff_contact_name: "",
    dropoff_phone: "",
    notes: "",
    client_total_vat_incl: "",
    driver_total_vat_incl: "",
    schedule_date: new Date().toISOString().split('T')[0],
    tags: [],
    admin_notes: ""
  });
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAssignModal, setBulkAssignModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState("");
  
  const { addToast } = useToast();
  const navigate = useNavigate();

  // Load initial data
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/operations/companies-quick`),
      axios.get(`${API}/drivers?limit=100`),
      axios.get(`${API}/shipments/tags`)
    ]).then(([companiesRes, driversRes, tagsRes]) => {
      setCompanies(companiesRes.data);
      const approved = (driversRes.data.items || driversRes.data).filter(d => 
        d.profile?.approval_status === 'ACTIVE' || d.profile?.approval_status === 'APPROVED'
      );
      setDrivers(approved);
      setAvailableTags(tagsRes.data.tags || {});
    }).catch(err => console.error(err));
  }, []);

  // Load summary for selected date
  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/operations/summary?date=${selectedDate}`);
      setSummary(res.data);
    } catch (err) {
      addToast("Error cargando resumen", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, addToast]);

  // Load jornadas for date range
  const loadJornadas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/operations/jornadas?date=${selectedDate}`);
      setJornadas(res.data.jornadas || []);
    } catch (err) {
      addToast("Error cargando jornadas", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, addToast]);

  // Load jornada detail
  const loadJornadaDetail = useCallback(async (companyId, date) => {
    setActionLoading("jornada");
    try {
      const res = await axios.get(`${API}/operations/jornada/${companyId}/${date}`);
      setSelectedJornada(res.data);
      setJornadaShipments(res.data.shipments || []);
      setSelectedIds([]);
    } catch (err) {
      addToast("Error cargando jornada", "error");
    } finally {
      setActionLoading(null);
    }
  }, [addToast]);

  useEffect(() => {
    if (activeView === "today") {
      loadSummary();
      loadJornadas();
    } else if (activeView === "jornadas") {
      loadJornadas();
    }
  }, [activeView, selectedDate, loadSummary, loadJornadas]);

  // Quick shipment submission
  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    if (!quickForm.company_id || !quickForm.dropoff_address || !quickForm.client_total_vat_incl || !quickForm.driver_total_vat_incl) {
      addToast("Completa los campos obligatorios", "error");
      return;
    }
    setActionLoading("quick");
    try {
      const res = await axios.post(`${API}/operations/quick-shipment`, {
        ...quickForm,
        client_total_vat_incl: parseFloat(quickForm.client_total_vat_incl),
        driver_total_vat_incl: parseFloat(quickForm.driver_total_vat_incl)
      });
      addToast(`Envío ${res.data.reference} creado`, "success");
      // Reset form but keep company and date
      setQuickForm(prev => ({
        ...prev,
        dropoff_address: "",
        dropoff_contact_name: "",
        dropoff_phone: "",
        notes: "",
        client_total_vat_incl: "",
        driver_total_vat_incl: "",
        tags: [],
        admin_notes: ""
      }));
      loadSummary();
      loadJornadas();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error creando envío", "error");
    } finally {
      setActionLoading(null);
    }
  };

  // Shipment actions
  const handlePublish = async (id) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/shipments/${id}/publish`);
      addToast("Publicado a conductores", "success");
      if (selectedJornada) {
        loadJornadaDetail(selectedJornada.company_id, selectedJornada.date);
      }
      loadJornadas();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleAssignSingle = async (shipmentId, driverId) => {
    setActionLoading(shipmentId);
    try {
      await axios.post(`${API}/shipments/${shipmentId}/assign-driver`, { driver_id: driverId });
      addToast("Conductor asignado", "success");
      if (selectedJornada) {
        loadJornadaDetail(selectedJornada.company_id, selectedJornada.date);
      }
      loadJornadas();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedDriver || selectedIds.length === 0) return;
    setActionLoading("bulk");
    try {
      const res = await axios.post(`${API}/shipments/bulk-assign`, {
        shipment_ids: selectedIds,
        driver_id: selectedDriver
      });
      addToast(res.data.message, "success");
      setSelectedIds([]);
      setBulkAssignModal(false);
      setSelectedDriver("");
      if (selectedJornada) {
        loadJornadaDetail(selectedJornada.company_id, selectedJornada.date);
      }
      loadJornadas();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDuplicate = async (shipmentId) => {
    setActionLoading(shipmentId);
    try {
      const res = await axios.post(`${API}/shipments/${shipmentId}/duplicate`);
      addToast(`Duplicado: ${res.data.reference}`, "success");
      if (selectedJornada) {
        loadJornadaDetail(selectedJornada.company_id, selectedJornada.date);
      }
      loadJornadas();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    } finally {
      setActionLoading(null);
    }
  };

  // Tag colors
  const tagColors = {
    URGENT: "bg-red-100 text-red-700",
    REFRIGERATED: "bg-blue-100 text-karga-carbon",
    FROZEN: "bg-cyan-100 text-cyan-700",
    DOCUMENTATION: "bg-purple-100 text-purple-700",
    SANITARY: "bg-green-100 text-green-700",
    LIFTGATE: "bg-orange-100 text-orange-700",
    FRAGILE: "bg-yellow-100 text-yellow-700"
  };

  // Toggle selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  return (
    <DashboardLayout role="ADMIN">
      <div className="space-y-4">
        {/* Header with date picker and view tabs */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Operaciones</h1>
            <p className="text-sm text-slate-500">Gestión rápida del trabajo diario</p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40"
            />
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => {
                loadSummary();
                loadJornadas();
              }}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* View Tabs - Large touch-friendly buttons */}
        <div className="flex gap-2 border-b pb-2">
          <button
            onClick={() => { setActiveView("today"); setSelectedJornada(null); }}
            className={`px-4 py-3 rounded-lg font-medium transition-all ${
              activeView === "today" 
                ? "bg-karga-carbon text-white" 
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            <Activity className="h-5 w-5 inline mr-2"/>Resumen
          </button>
          <button
            onClick={() => { setActiveView("jornadas"); setSelectedJornada(null); }}
            className={`px-4 py-3 rounded-lg font-medium transition-all ${
              activeView === "jornadas" 
                ? "bg-karga-carbon text-white" 
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            <Calendar className="h-5 w-5 inline mr-2"/>Jornadas
          </button>
          <button
            onClick={() => setActiveView("quick")}
            className={`px-4 py-3 rounded-lg font-medium transition-all ${
              activeView === "quick" 
                ? "bg-green-600 text-white" 
                : "bg-green-50 text-green-700 hover:bg-green-100"
            }`}
          >
            <Plus className="h-5 w-5 inline mr-2"/>Crear Envío
          </button>
        </div>

        {/* SUMMARY VIEW */}
        {activeView === "today" && summary && (
          <div className="space-y-4">
            {/* Quick Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="p-4 bg-yellow-50 border-yellow-200">
                <div className="text-3xl font-bold text-yellow-700">{summary.today?.pending || 0}</div>
                <div className="text-sm text-yellow-600">Pendientes</div>
              </Card>
              <Card className="p-4 bg-blue-50 border-blue-200">
                <div className="text-3xl font-bold text-karga-carbon">{summary.today?.in_route || 0}</div>
                <div className="text-sm text-karga-dark">En ruta</div>
              </Card>
              <Card className="p-4 bg-green-50 border-green-200">
                <div className="text-3xl font-bold text-green-700">{summary.today?.delivered || 0}</div>
                <div className="text-sm text-green-600">Entregados</div>
              </Card>
              <Card className="p-4 bg-emerald-50 border-emerald-200">
                <div className="text-3xl font-bold text-emerald-700">€{summary.today?.margin || 0}</div>
                <div className="text-sm text-emerald-600">Margen día</div>
              </Card>
            </div>

            {/* Alerts Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {summary.pending_requests > 0 && (
                <Card 
                  className="p-4 bg-orange-50 border-orange-300 cursor-pointer hover:bg-orange-100 transition-colors"
                  onClick={() => navigate('/admin/requests')}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-200 rounded-lg">
                      <Inbox className="h-6 w-6 text-orange-700"/>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-700">{summary.pending_requests}</div>
                      <div className="text-sm text-orange-600">Solicitudes sin cotizar</div>
                    </div>
                  </div>
                </Card>
              )}
              {summary.pending_quotes > 0 && (
                <Card className="p-4 bg-purple-50 border-purple-300">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-200 rounded-lg">
                      <DollarSign className="h-6 w-6 text-purple-700"/>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-700">{summary.pending_quotes}</div>
                      <div className="text-sm text-purple-600">Presupuestos pendientes</div>
                    </div>
                  </div>
                </Card>
              )}
              <Card className="p-4 bg-slate-50 border-slate-300">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-200 rounded-lg">
                    <Users className="h-6 w-6 text-slate-700"/>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-700">{summary.available_drivers}</div>
                    <div className="text-sm text-slate-600">Conductores disponibles</div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Jornadas del día */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5"/>
                  Jornadas del {new Date(selectedDate).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {jornadas.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    <Package className="h-12 w-12 mx-auto mb-2 opacity-50"/>
                    <p>No hay envíos programados para esta fecha</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {jornadas.map((j, idx) => (
                      <div 
                        key={`${j.company_id}-${j.date}-${idx}`}
                        className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => loadJornadaDetail(j.company_id, j.date)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-semibold text-slate-900">{j.company_name}</div>
                            <div className="text-sm text-slate-500 flex gap-4 mt-1">
                              <span className="text-yellow-600">{j.pending} pend.</span>
                              <span className="text-karga-dark">{j.in_route} ruta</span>
                              <span className="text-green-600">{j.delivered} entregados</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-slate-900">€{j.total_client}</div>
                            <div className="text-sm text-green-600">+€{j.total_margin} margen</div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-slate-400 ml-2"/>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* JORNADAS VIEW */}
        {activeView === "jornadas" && !selectedJornada && (
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400"/>
              </div>
            ) : jornadas.length === 0 ? (
              <Card className="p-8 text-center">
                <Package className="h-12 w-12 mx-auto mb-2 text-slate-300"/>
                <p className="text-slate-500">No hay jornadas para esta fecha</p>
                <Button 
                  className="mt-4"
                  onClick={() => setActiveView("quick")}
                  style={{ backgroundColor: '#10B981' }}
                >
                  <Plus className="h-4 w-4 mr-2"/>Crear primer envío
                </Button>
              </Card>
            ) : (
              <div className="grid gap-3">
                {jornadas.map((j, idx) => (
                  <Card 
                    key={`${j.company_id}-${j.date}-${idx}`}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => loadJornadaDetail(j.company_id, j.date)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-bold text-lg text-slate-900">{j.company_name}</div>
                          <div className="flex gap-3 mt-2">
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-sm font-medium">
                              {j.pending} pendientes
                            </span>
                            <span className="px-2 py-1 bg-blue-100 text-karga-carbon rounded text-sm font-medium">
                              {j.in_route} en ruta
                            </span>
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-medium">
                              {j.delivered} entregados
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold">€{j.total_client}</div>
                          <div className="text-sm text-green-600 font-medium">Margen: €{j.total_margin}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* JORNADA DETAIL VIEW */}
        {activeView === "jornadas" && selectedJornada && (
          <div className="space-y-4">
            {/* Back button and header */}
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => setSelectedJornada(null)}>
                <ChevronLeft className="h-4 w-4 mr-1"/>Volver
              </Button>
              <div className="flex-1">
                <h2 className="text-xl font-bold">{selectedJornada.company_name}</h2>
                <p className="text-sm text-slate-500">
                  {new Date(selectedJornada.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                  {' • '}{jornadaShipments.length} envíos • €{selectedJornada.totals?.total_client}
                </p>
              </div>
            </div>

            {/* Bulk actions */}
            {selectedIds.length > 0 && (
              <Card className="p-3 bg-blue-50 border-blue-200">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-karga-carbon">
                    {selectedIds.length} seleccionado{selectedIds.length > 1 ? 's' : ''}
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setSelectedIds([])}>
                      Deseleccionar
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => setBulkAssignModal(true)}
                      className="bg-karga-carbon hover:bg-karga-dark text-white"
                    >
                      <UserCheck className="h-4 w-4 mr-1"/>Asignar
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Shipments list - operational style */}
            <div className="space-y-2">
              {jornadaShipments.map(s => (
                <Card key={s.id} className={`p-3 ${selectedIds.includes(s.id) ? 'ring-2 ring-blue-500' : ''}`}>
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(s.id)}
                      onChange={() => toggleSelect(s.id)}
                      className="mt-1 rounded border-slate-300"
                    />
                    
                    {/* Main content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-slate-500">{s.reference}</span>
                        <StatusPill status={s.status} role="ADMIN"/>
                        {s.tags?.map(tag => (
                          <span key={tag} className={`text-xs px-1.5 py-0.5 rounded ${tagColors[tag]}`}>
                            {availableTags[tag]?.label || tag}
                          </span>
                        ))}
                      </div>
                      <div className="mt-1">
                        <p className="text-sm font-medium truncate" title={s.dropoff_address}>
                          <MapPin className="h-3 w-3 inline mr-1 text-slate-400"/>
                          {s.dropoff_address}
                        </p>
                        {s.dropoff_contact_name && (
                          <p className="text-xs text-slate-500">
                            {s.dropoff_contact_name} {s.dropoff_phone && `• ${s.dropoff_phone}`}
                          </p>
                        )}
                      </div>
                      {s.driver_name && (
                        <p className="text-xs text-indigo-600 mt-1">
                          <Truck className="h-3 w-3 inline mr-1"/>{s.driver_name}
                        </p>
                      )}
                    </div>

                    {/* Price */}
                    <div className="text-right flex-shrink-0">
                      <div className="font-bold">€{s.client_total_vat_incl?.toFixed(2) || '-'}</div>
                      <div className="text-xs text-green-600">+€{s.margin_total?.toFixed(2) || '-'}</div>
                    </div>

                    {/* Quick actions */}
                    <div className="flex flex-col gap-1">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => navigate(`/admin/shipments/${s.id}`)}
                        className="h-8 w-8 p-0"
                      >
                        <Eye className="h-4 w-4"/>
                      </Button>
                      {['COMPANY_ACCEPTED', 'PRICED'].includes(s.status) && (
                        <Button 
                          size="sm"
                          onClick={() => handlePublish(s.id)}
                          disabled={actionLoading === s.id}
                          className="h-8 w-8 p-0 bg-purple-600 hover:bg-purple-700"
                        >
                          <Play className="h-4 w-4"/>
                        </Button>
                      )}
                      {['OFFER_PUBLISHED'].includes(s.status) && !s.driver_id && (
                        <Select
                          value=""
                          onValueChange={(driverId) => handleAssignSingle(s.id, driverId)}
                        >
                          <SelectTrigger className="h-8 w-8 p-0 border-blue-300">
                            <UserCheck className="h-4 w-4 text-karga-dark"/>
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map(d => (
                              <SelectItem key={d.id} value={d.id}>
                                {d.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDuplicate(s.id)}
                        disabled={actionLoading === s.id}
                        className="h-8 w-8 p-0"
                        title="Duplicar"
                      >
                        <Plus className="h-4 w-4"/>
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* QUICK SHIPMENT FORM */}
        {activeView === "quick" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-green-600"/>
                Creación Rápida de Envío
              </CardTitle>
              <p className="text-sm text-slate-500">Campos mínimos, precios manuales</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleQuickSubmit} className="space-y-4">
                {/* Row 1: Company + Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Empresa *</Label>
                    <Select 
                      value={quickForm.company_id} 
                      onValueChange={v => {
                        setQuickForm({...quickForm, company_id: v});
                        // Auto-fill pickup from company default
                        const company = companies.find(c => c.id === v);
                        if (company?.default_address && !quickForm.pickup_address) {
                          // We don't have pickup in quick form, but it's auto-set in backend
                        }
                      }}
                    >
                      <SelectTrigger className="h-12 text-base">
                        <SelectValue placeholder="Seleccionar empresa..."/>
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Fecha programada</Label>
                    <Input
                      type="date"
                      value={quickForm.schedule_date}
                      onChange={e => setQuickForm({...quickForm, schedule_date: e.target.value})}
                      className="h-12 text-base"
                    />
                  </div>
                </div>

                {/* Row 2: Destination */}
                <div>
                  <Label className="text-sm font-medium">Dirección de entrega *</Label>
                  <Input
                    value={quickForm.dropoff_address}
                    onChange={e => setQuickForm({...quickForm, dropoff_address: e.target.value})}
                    placeholder="Dirección completa de entrega"
                    className="h-12 text-base"
                    required
                  />
                </div>

                {/* Row 3: Contact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Contacto</Label>
                    <Input
                      value={quickForm.dropoff_contact_name}
                      onChange={e => setQuickForm({...quickForm, dropoff_contact_name: e.target.value})}
                      placeholder="Nombre del contacto"
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Teléfono</Label>
                    <Input
                      value={quickForm.dropoff_phone}
                      onChange={e => setQuickForm({...quickForm, dropoff_phone: e.target.value})}
                      placeholder="Teléfono de contacto"
                      className="h-11"
                    />
                  </div>
                </div>

                {/* Row 4: Prices - BIG inputs */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-sm font-medium text-karga-carbon">Precio Cliente (IVA inc.) *</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg">€</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={quickForm.client_total_vat_incl}
                        onChange={e => setQuickForm({...quickForm, client_total_vat_incl: e.target.value})}
                        placeholder="0.00"
                        className="h-14 text-xl font-bold pl-8"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-orange-700">Pago Conductor (IVA inc.) *</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg">€</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={quickForm.driver_total_vat_incl}
                        onChange={e => setQuickForm({...quickForm, driver_total_vat_incl: e.target.value})}
                        placeholder="0.00"
                        className="h-14 text-xl font-bold pl-8"
                        required
                      />
                    </div>
                  </div>
                  {quickForm.client_total_vat_incl && quickForm.driver_total_vat_incl && (
                    <div className="col-span-2 text-center p-2 bg-green-100 rounded-lg">
                      <span className="text-sm text-green-700">Margen: </span>
                      <span className="text-lg font-bold text-green-700">
                        €{(parseFloat(quickForm.client_total_vat_incl || 0) - parseFloat(quickForm.driver_total_vat_incl || 0)).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Row 5: Notes */}
                <div>
                  <Label className="text-sm font-medium">Notas (visibles para todos)</Label>
                  <Textarea
                    value={quickForm.notes}
                    onChange={e => setQuickForm({...quickForm, notes: e.target.value})}
                    placeholder="Instrucciones de entrega, descripción de mercancía..."
                    rows={2}
                  />
                </div>

                {/* Row 6: Tags */}
                <div>
                  <Label className="text-sm font-medium">Etiquetas</Label>
                  <div className="flex gap-2 flex-wrap mt-1">
                    {Object.entries(availableTags).map(([key, val]) => {
                      const isSelected = quickForm.tags.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            setQuickForm(prev => ({
                              ...prev,
                              tags: isSelected 
                                ? prev.tags.filter(t => t !== key)
                                : [...prev.tags, key]
                            }));
                          }}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                            isSelected
                              ? `${tagColors[key]} ring-2 ring-offset-1`
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {val.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-4">
                  <LoadingButton
                    type="submit"
                    loading={actionLoading === "quick"}
                    className="flex-1 h-14 text-lg"
                    style={{ backgroundColor: '#10B981' }}
                  >
                    <Plus className="h-5 w-5 mr-2"/>
                    Crear Envío
                  </LoadingButton>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bulk Assign Modal */}
      <Dialog open={bulkAssignModal} onOpenChange={() => setBulkAssignModal(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignación Múltiple</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="font-medium text-blue-800">
                {selectedIds.length} envío{selectedIds.length > 1 ? 's' : ''} seleccionado{selectedIds.length > 1 ? 's' : ''}
              </p>
            </div>
            <div>
              <Label>Seleccionar conductor</Label>
              <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Elegir conductor..."/>
                </SelectTrigger>
                <SelectContent>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} - {d.profile?.vehicle_category || 'N/A'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignModal(false)}>Cancelar</Button>
            <LoadingButton 
              onClick={handleBulkAssign} 
              loading={actionLoading === "bulk"} 
              disabled={!selectedDriver}
              className="bg-karga-carbon hover:bg-karga-dark"
            >
              Asignar a todos
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// ==================== ADMIN COMPANIES ====================
const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const { addToast } = useToast();

  useEffect(() => { 
    axios.get(`${API}/companies`).then(r => {
      setCompanies(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  
  // Frontend filtering by name, email, NIF, phone
  const filteredCompanies = useMemo(() => {
    if (!searchTerm.trim()) return companies;
    const term = searchTerm.toLowerCase().trim();
    return companies.filter(c => {
      const name = (c.name || '').toLowerCase();
      const email = (c.email || '').toLowerCase();
      const nif = (c.profile?.nif || c.profile?.tax_id || '').toLowerCase();
      const phone = (c.profile?.phone || '').toLowerCase();
      return name.includes(term) || email.includes(term) || nif.includes(term) || phone.includes(term);
    });
  }, [companies, searchTerm]);

  const openProfile = async (companyId) => {
    try {
      const r = await axios.get(`${API}/admin/companies/${companyId}`);
      setCompanyProfile(r.data);
      setEditForm({
        name: r.data.user?.name || '',
        email: r.data.user?.email || '',
        phone: r.data.profile?.phone || '',
        address: r.data.profile?.address || '',
        nif: r.data.profile?.nif || r.data.profile?.tax_id || ''
      });
      setSelectedCompany(companyId);
    } catch (err) {
      addToast(err.response?.data?.detail || 'Error al cargar perfil', 'error');
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/companies/${selectedCompany}`, editForm);
      addToast('Empresa actualizada', 'success');
      // Refresh list
      const r = await axios.get(`${API}/companies`);
      setCompanies(r.data);
      setSelectedCompany(null);
    } catch (err) {
      addToast(err.response?.data?.detail || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout role="ADMIN">
      <div className="space-y-6">
        <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Empresas</h1>
        
        {/* Search bar */}
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar empresa por nombre, email, NIF o teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="company-search-input"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4"/>
            </button>
          )}
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              {filteredCompanies.length === 0 && searchTerm ? (
                <div className="p-8 text-center text-slate-500">
                  No se encontraron empresas con "{searchTerm}"
                </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>NIF/CIF</TableHead>
                    <TableHead>Envíos</TableHead>
                    <TableHead>Pendiente</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCompanies.map((c, index) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <button 
                          onClick={() => openProfile(c.id)}
                          className="text-left hover:text-karga-dark transition-colors"
                        >
                          <p className="font-medium text-karga-dark hover:underline cursor-pointer">{c.name}</p>
                          <p className="text-xs text-slate-400">EMP-{String(index + 1).padStart(3, '0')}</p>
                          <p className="text-sm text-slate-500">{c.email}</p>
                        </button>
                      </TableCell>
                      <TableCell>{c.profile?.nif || c.profile?.tax_id || "-"}</TableCell>
                      <TableCell>{c.shipment_count}</TableCell>
                      <TableCell className="text-orange-600 font-medium">€{(c.unpaid_amount || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Company Profile Modal */}
        <Dialog open={!!selectedCompany} onOpenChange={() => setSelectedCompany(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Perfil de Empresa</DialogTitle>
            </DialogHeader>
            
            {companyProfile && (
              <div className="space-y-6">
                {/* Monthly Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-green-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-slate-500">Entregados (mes)</p>
                      <p className="text-2xl font-bold text-green-700">{companyProfile.monthly_summary?.delivered_count || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-slate-500">Facturable</p>
                      <p className="text-2xl font-bold text-karga-carbon">€{companyProfile.monthly_summary?.billing_total_vat_incl?.toFixed(2) || '0.00'}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-slate-500">Pendientes</p>
                      <p className="text-2xl font-bold text-orange-700">{companyProfile.monthly_summary?.pending_count || 0}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* v1.1: Legal Terms Status */}
                <Card className={companyProfile.terms_info?.accepted ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      {companyProfile.terms_info?.accepted ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-600" />
                      )}
                      <div>
                        <p className={`font-medium ${companyProfile.terms_info?.accepted ? 'text-green-800' : 'text-red-800'}`} data-testid="terms-status">
                          Términos aceptados: {companyProfile.terms_info?.accepted ? 'Sí' : 'No'}
                        </p>
                        {companyProfile.terms_info?.accepted && (
                          <p className="text-sm text-slate-600" data-testid="terms-details">
                            Fecha: {companyProfile.terms_info.accepted_at ? new Date(companyProfile.terms_info.accepted_at).toLocaleString('es-ES') : 'N/A'} · 
                            Versión: {companyProfile.terms_info.version || 'N/A'} · 
                            IP: {companyProfile.terms_info.ip || 'N/A'}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Edit Form */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre Empresa</Label>
                    <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} />
                  </div>
                  <div>
                    <Label>NIF/CIF</Label>
                    <Input value={editForm.nif} onChange={e => setEditForm({...editForm, nif: e.target.value})} />
                  </div>
                  <div className="col-span-2">
                    <Label>Dirección</Label>
                    <Input value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} />
                  </div>
                </div>

                {/* Shipments This Month */}
                {companyProfile.shipments?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Envíos del mes ({companyProfile.monthly_summary?.period})</h4>
                    <div className="max-h-48 overflow-y-auto border rounded">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Ref</TableHead>
                            <TableHead>Fecha</TableHead>
                            <TableHead>Estado</TableHead>
                            <TableHead className="text-right">Cliente</TableHead>
                            <TableHead className="text-right">Margen</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {companyProfile.shipments.slice(0, 10).map(s => (
                            <TableRow key={s.id}>
                              <TableCell className="font-mono text-xs">{s.reference}</TableCell>
                              <TableCell className="text-sm">{s.date}</TableCell>
                              <TableCell><span className="text-xs">{s.status}</span></TableCell>
                              <TableCell className="text-right text-sm">€{(s.client_total_vat_incl || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right text-sm text-green-600">€{(s.margin_total || 0).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedCompany(null)}>Cancelar</Button>
              <LoadingButton onClick={saveProfile} loading={saving} style={{ backgroundColor: '#B4A798' }}>
                Guardar Cambios
              </LoadingButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// ==================== ADMIN DRIVERS ====================
const AdminDrivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverProfile, setDriverProfile] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { addToast } = useToast();

  const loadDrivers = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/drivers?page=${pageNum}&limit=20`);
      setDrivers(r.data.items || r.data);
      setPagination({ total: r.data.total || 0, pages: r.data.pages || 1 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDrivers(1); }, [loadDrivers]);
  
  // Frontend filtering by name, email, DNI, phone, license plate
  const filteredDrivers = useMemo(() => {
    if (!searchTerm.trim()) return drivers;
    const term = searchTerm.toLowerCase().trim();
    return drivers.filter(d => {
      const name = (d.name || '').toLowerCase();
      const email = (d.email || '').toLowerCase();
      const dni = (d.profile?.dni || d.profile?.tax_id || d.profile?.nif || '').toLowerCase();
      const phone = (d.profile?.phone || '').toLowerCase();
      const plate = (d.profile?.license_plate || '').toLowerCase();
      return name.includes(term) || email.includes(term) || dni.includes(term) || phone.includes(term) || plate.includes(term);
    });
  }, [drivers, searchTerm]);
  
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    loadDrivers(newPage);
  }, [loadDrivers]);

  const handleActivate = async (driverId) => {
    setActionLoading(driverId);
    try {
      await axios.post(`${API}/admin/drivers/${driverId}/activate`);
      addToast('Conductor activado', 'success');
      loadDrivers(page);
    } catch (err) {
      addToast(err.response?.data?.detail || 'Error al activar', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBlock = async (driverId) => {
    setActionLoading(driverId);
    try {
      await axios.post(`${API}/admin/drivers/${driverId}/block`);
      addToast('Conductor bloqueado', 'success');
      loadDrivers(page);
    } catch (err) {
      addToast(err.response?.data?.detail || 'Error al bloquear', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const openProfile = async (driverId) => {
    try {
      const r = await axios.get(`${API}/admin/drivers/${driverId}`);
      setDriverProfile(r.data);
      setEditForm({
        name: r.data.user?.name || '',
        email: r.data.user?.email || '',
        phone: r.data.profile?.phone || '',
        address: r.data.profile?.address || r.data.profile?.fiscal_address || '',
        dni: r.data.profile?.dni || r.data.profile?.tax_id || '',
        nif: r.data.profile?.nif || '',
        iban: r.data.profile?.iban || '',
        account_holder: r.data.profile?.account_holder || '',
        license_plate: r.data.profile?.license_plate || ''
      });
      setSelectedDriver(driverId);
    } catch (err) {
      addToast(err.response?.data?.detail || 'Error al cargar perfil', 'error');
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/drivers/${selectedDriver}`, editForm);
      addToast('Conductor actualizado', 'success');
      loadDrivers(page);
      setSelectedDriver(null);
    } catch (err) {
      addToast(err.response?.data?.detail || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      'PENDING': 'bg-yellow-100 text-yellow-700',
      'ACTIVE': 'bg-green-100 text-green-700',
      'BLOCKED': 'bg-red-100 text-red-700',
      // Legacy values
      'PENDING_APPROVAL': 'bg-yellow-100 text-yellow-700',
      'APPROVED': 'bg-green-100 text-green-700',
      'REJECTED': 'bg-red-100 text-red-700'
    };
    const labels = {
      'PENDING': 'Pendiente',
      'ACTIVE': 'Activo',
      'BLOCKED': 'Bloqueado',
      'PENDING_APPROVAL': 'Pendiente',
      'APPROVED': 'Activo',
      'REJECTED': 'Bloqueado'
    };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-700'}`}>{labels[status] || status}</span>;
  };

  const driverRows = useMemo(() => filteredDrivers.map((d, index) => {
    const status = d.profile?.approval_status || 'PENDING';
    const isActive = status === 'ACTIVE' || status === 'APPROVED';
    const isPending = status === 'PENDING' || status === 'PENDING_APPROVAL';
    
    return (
      <TableRow key={d.id} data-testid={`driver-row-${d.id}`}>
        <TableCell>
          <button 
            onClick={() => openProfile(d.id)}
            className="text-left hover:text-karga-dark transition-colors"
          >
            <p className="font-medium text-karga-dark hover:underline cursor-pointer">{d.name}</p>
            <p className="text-xs text-slate-400">DRV-{String(index + 1).padStart(3, '0')}</p>
            <p className="text-sm text-slate-500">{d.email}</p>
            {d.profile?.license_plate && <p className="text-xs text-slate-400">{d.profile.license_plate}</p>}
          </button>
        </TableCell>
        <TableCell><VehicleBadge category={d.profile?.vehicle_category}/></TableCell>
        <TableCell>{statusBadge(status)}</TableCell>
        <TableCell>
          <div className="flex gap-1">
            {(isPending || status === 'BLOCKED' || status === 'REJECTED') && (
              <Button 
                size="sm" 
                variant="outline" 
                className="text-green-600 border-green-300 hover:bg-green-50"
                onClick={(e) => { e.stopPropagation(); handleActivate(d.id); }}
                disabled={actionLoading === d.id}
              >
                {actionLoading === d.id ? <Loader2 className="h-3 w-3 animate-spin"/> : <CheckCircle className="h-3 w-3"/>}
                <span className="ml-1 text-xs">Activar</span>
              </Button>
            )}
            {isActive && (
              <Button 
                size="sm" 
                variant="outline" 
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={(e) => { e.stopPropagation(); handleBlock(d.id); }}
                disabled={actionLoading === d.id}
              >
                {actionLoading === d.id ? <Loader2 className="h-3 w-3 animate-spin"/> : <XCircle className="h-3 w-3"/>}
                <span className="ml-1 text-xs">Bloquear</span>
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  }), [filteredDrivers, actionLoading]);

  return (
    <DashboardLayout role="ADMIN">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Conductores</h1>
          <Button size="sm" variant="outline" onClick={() => loadDrivers(page)} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {/* Search bar */}
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar conductor por nombre, email, DNI o teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="driver-search-input"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4"/>
            </button>
          )}
        </div>
        
        <Card>
          <CardContent className="p-0">
            {loading && drivers.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : filteredDrivers.length === 0 && searchTerm ? (
              <div className="p-8 text-center text-slate-500">
                No se encontraron conductores con "{searchTerm}"
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Conductor</TableHead>
                    <TableHead>Vehículo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {driverRows}
                </TableBody>
              </Table>
            )}
          </CardContent>
          <Pagination page={page} pages={pagination.pages} total={pagination.total} onPageChange={handlePageChange} loading={loading} />
        </Card>

        {/* Driver Profile Modal */}
        <Dialog open={!!selectedDriver} onOpenChange={() => setSelectedDriver(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Perfil de Conductor</DialogTitle>
            </DialogHeader>
            
            {driverProfile && (
              <div className="space-y-6">
                {/* Monthly Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-green-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-slate-500">Entregados (mes)</p>
                      <p className="text-2xl font-bold text-green-700">{driverProfile.monthly_summary?.delivered_count || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-slate-500">A Pagar</p>
                      <p className="text-2xl font-bold text-orange-700">€{driverProfile.monthly_summary?.payout_total_vat_incl?.toFixed(2) || '0.00'}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-slate-500">Total Trabajos</p>
                      <p className="text-2xl font-bold text-karga-carbon">{driverProfile.monthly_summary?.total_jobs || 0}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Edit Form */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre</Label>
                    <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} />
                  </div>
                  <div>
                    <Label>DNI/NIE</Label>
                    <Input value={editForm.dni} onChange={e => setEditForm({...editForm, dni: e.target.value})} />
                  </div>
                  <div>
                    <Label>Matrícula</Label>
                    <Input value={editForm.license_plate} onChange={e => setEditForm({...editForm, license_plate: e.target.value})} placeholder="0000 XXX" />
                  </div>
                  <div>
                    <Label>Dirección</Label>
                    <Input value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} />
                  </div>
                  <div>
                    <Label>IBAN</Label>
                    <Input value={editForm.iban} onChange={e => setEditForm({...editForm, iban: e.target.value})} placeholder="ES00 0000 0000 0000 0000 0000" />
                  </div>
                  <div>
                    <Label>Titular Cuenta</Label>
                    <Input value={editForm.account_holder} onChange={e => setEditForm({...editForm, account_holder: e.target.value})} />
                  </div>
                </div>

                {/* Jobs This Month */}
                {driverProfile.jobs?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Trabajos del mes ({driverProfile.monthly_summary?.period})</h4>
                    <div className="max-h-48 overflow-y-auto border rounded">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Ref</TableHead>
                            <TableHead>Fecha</TableHead>
                            <TableHead>Ruta</TableHead>
                            <TableHead>Estado</TableHead>
                            <TableHead className="text-right">Pago</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {driverProfile.jobs.slice(0, 10).map(j => (
                            <TableRow key={j.id}>
                              <TableCell className="font-mono text-xs">{j.reference}</TableCell>
                              <TableCell className="text-sm">{j.date}</TableCell>
                              <TableCell className="text-xs truncate max-w-[150px]">{j.pickup_address} → {j.dropoff_address}</TableCell>
                              <TableCell><span className="text-xs">{j.status}</span></TableCell>
                              <TableCell className="text-right text-sm text-orange-600">€{(j.driver_total_vat_incl || 0).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedDriver(null)}>Cancelar</Button>
              <LoadingButton onClick={saveProfile} loading={saving} style={{ backgroundColor: '#B4A798' }}>
                Guardar Cambios
              </LoadingButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// ==================== ADMIN BILLING (MONTHLY) ====================
const AdminBilling = () => {
  const [activeTab, setActiveTab] = useState("companies");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [companiesData, setCompaniesData] = useState(null);
  const [driversData, setDriversData] = useState(null);
  const [payments, setPayments] = useState({ companies: {}, drivers: {} });
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showConditions, setShowConditions] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [paymentNote, setPaymentNote] = useState("");
  const { addToast } = useToast();

  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

  // Calculate due dates
  const getCompanyDueDate = (year, month) => {
    // Due: 5th of next month
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    return new Date(nextYear, nextMonth - 1, 5);
  };

  const getDriverDueDate = (year, month) => {
    // Due: 10th of next month
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    return new Date(nextYear, nextMonth - 1, 10);
  };

  const isOverdue = (dueDate, isPaid) => {
    if (isPaid) return false;
    return new Date() > dueDate;
  };

  const formatDate = (date) => {
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [companiesRes, driversRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/billing/companies?year=${selectedYear}&month=${selectedMonth}`),
        axios.get(`${API}/admin/billing/drivers?year=${selectedYear}&month=${selectedMonth}`),
        axios.get(`${API}/admin/billing/payments?year=${selectedYear}&month=${selectedMonth}`)
      ]);
      setCompaniesData(companiesRes.data);
      setDriversData(driversRes.data);
      setPayments(paymentsRes.data);
    } catch (err) {
      console.error('Error loading billing data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleMarkPaid = async (entityType, entityId, markAsPaid) => {
    try {
      const endpoint = entityType === 'company' 
        ? (markAsPaid ? '/admin/billing/companies/mark-paid' : '/admin/billing/companies/unmark-paid')
        : (markAsPaid ? '/admin/billing/drivers/mark-paid' : '/admin/billing/drivers/unmark-paid');
      
      await axios.post(`${API}${endpoint}`, {
        year: selectedYear,
        month: selectedMonth,
        entity_id: entityId,
        note: paymentNote
      });
      addToast(markAsPaid ? (entityType === 'company' ? 'Cobro registrado' : 'Pago registrado') : 'Desmarcado', 'success');
      setConfirmModal(null);
      setPaymentNote("");
      loadData();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Error', 'error');
    }
  };

  const companyDueDate = getCompanyDueDate(selectedYear, selectedMonth);
  const driverDueDate = getDriverDueDate(selectedYear, selectedMonth);

  return (
    <DashboardLayout role="ADMIN">
      <div className="space-y-6">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Facturación Mensual</h1>
            <button onClick={() => setShowConditions(true)} className="text-sm text-karga-dark hover:underline">
              Ver condiciones de facturación
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Select value={selectedMonth.toString()} onValueChange={v => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-32"><SelectValue/></SelectTrigger>
              <SelectContent>
                {monthNames.map((m, i) => (
                  <SelectItem key={i} value={(i+1).toString()}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="number" className="w-24" value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))} min={2024} max={2030}/>
            <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-blue-50">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500">Total Entregas</p>
              <p className="text-2xl font-bold text-karga-carbon">{companiesData?.total_delivered || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500">Total Facturar</p>
              <p className="text-2xl font-bold text-green-700">€{companiesData?.grand_total_vat_incl?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-slate-500">Vence: {formatDate(companyDueDate)}</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-50">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500">Total a Pagar</p>
              <p className="text-2xl font-bold text-orange-700">€{driversData?.grand_total_pagar_vat_incl?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-slate-500">Antes de: {formatDate(driverDueDate)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button className={`px-4 py-2 font-medium transition-colors ${activeTab === 'companies' ? 'text-karga-dark border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('companies')}>
            Empresas
          </button>
          <button className={`px-4 py-2 font-medium transition-colors ${activeTab === 'drivers' ? 'text-karga-dark border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('drivers')}>
            Conductores
          </button>
        </div>

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Facturación por Empresa - {monthNames[selectedMonth - 1]} {selectedYear}</CardTitle>
                <span className="text-sm text-slate-500">Vence: {formatDate(companyDueDate)}</span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
              ) : companiesData?.companies?.length === 0 ? (
                <div className="p-8 text-center text-slate-500"><Receipt className="h-12 w-12 mx-auto mb-4 text-slate-300"/><p>No hay entregas en este período</p></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empresa</TableHead>
                      <TableHead className="text-center">Entregados</TableHead>
                      <TableHead className="text-right">Total (sin IVA)</TableHead>
                      <TableHead className="text-center">Estado</TableHead>
                      <TableHead>Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companiesData?.companies?.map(c => {
                      const payment = payments.companies[c.company_id];
                      const isPaid = payment?.paid;
                      const overdue = isOverdue(companyDueDate, isPaid);
                      return (
                        <TableRow key={c.company_id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedCompany(c)}>
                          <TableCell><span className="font-medium text-karga-dark hover:underline">{c.company_name}</span></TableCell>
                          <TableCell className="text-center">{c.delivered_count}</TableCell>
                          <TableCell className="text-right font-bold text-green-600">€{c.total_vat_incl.toFixed(2)}</TableCell>
                          <TableCell className="text-center">
                            {isPaid ? (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">✓ Cobrado</span>
                            ) : overdue ? (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">Vencido</span>
                            ) : (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700">Pendiente</span>
                            )}
                          </TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            {isPaid ? (
                              <Button size="sm" variant="outline" className="text-xs" onClick={() => setConfirmModal({ type: 'company', id: c.company_id, name: c.company_name, action: 'unmark' })}>
                                Desmarcar
                              </Button>
                            ) : (
                              <Button size="sm" className="text-xs bg-green-600 hover:bg-green-700" onClick={() => setConfirmModal({ type: 'company', id: c.company_id, name: c.company_name, action: 'mark' })}>
                                Marcar cobrado
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Drivers Tab */}
        {activeTab === 'drivers' && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Pagos a Conductores - {monthNames[selectedMonth - 1]} {selectedYear}</CardTitle>
                <span className="text-sm text-slate-500">Pagar antes de: {formatDate(driverDueDate)}</span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
              ) : driversData?.drivers?.length === 0 ? (
                <div className="p-8 text-center text-slate-500"><Receipt className="h-12 w-12 mx-auto mb-4 text-slate-300"/><p>No hay entregas en este período</p></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Conductor</TableHead>
                      <TableHead className="text-center">Entregados</TableHead>
                      <TableHead className="text-right">Total pagar (sin IVA)</TableHead>
                      <TableHead className="text-center">Estado</TableHead>
                      <TableHead>Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {driversData?.drivers?.map(d => {
                      const payment = payments.drivers[d.driver_id];
                      const isPaid = payment?.paid;
                      const overdue = isOverdue(driverDueDate, isPaid);
                      return (
                        <TableRow key={d.driver_id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedDriver(d)}>
                          <TableCell><span className="font-medium text-karga-dark hover:underline">{d.driver_name}</span></TableCell>
                          <TableCell className="text-center">{d.delivered_count}</TableCell>
                          <TableCell className="text-right font-bold text-orange-600">€{d.total_pagar_vat_incl.toFixed(2)}</TableCell>
                          <TableCell className="text-center">
                            {isPaid ? (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">✓ Pagado</span>
                            ) : overdue ? (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">Vencido</span>
                            ) : (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700">Pendiente</span>
                            )}
                          </TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            {isPaid ? (
                              <Button size="sm" variant="outline" className="text-xs" onClick={() => setConfirmModal({ type: 'driver', id: d.driver_id, name: d.driver_name, action: 'unmark' })}>
                                Desmarcar
                              </Button>
                            ) : (
                              <Button size="sm" className="text-xs bg-orange-600 hover:bg-orange-700" onClick={() => setConfirmModal({ type: 'driver', id: d.driver_id, name: d.driver_name, action: 'mark' })}>
                                Marcar pagado
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Company Detail Modal */}
        <Dialog open={!!selectedCompany} onOpenChange={() => setSelectedCompany(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedCompany?.company_name} - {monthNames[selectedMonth - 1]} {selectedYear}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                <div>
                  <p className="text-slate-600">Total mes</p>
                  <p className="text-xs text-slate-500">Vence: {formatDate(companyDueDate)}</p>
                </div>
                <span className="text-2xl font-bold text-green-700">€{selectedCompany?.total_vat_incl?.toFixed(2)}</span>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Ref</TableHead><TableHead>Fecha</TableHead><TableHead>Ruta</TableHead><TableHead className="text-right">Importe</TableHead></TableRow></TableHeader>
                <TableBody>
                  {selectedCompany?.shipments?.map(s => (
                    <TableRow key={s.id}>
                      <TableCell className="font-mono text-xs">{s.reference}</TableCell>
                      <TableCell className="text-sm">{s.date}</TableCell>
                      <TableCell className="text-sm truncate max-w-[200px]">{s.pickup_address}... → {s.dropoff_address}...</TableCell>
                      <TableCell className="text-right font-medium">€{(s.total_vat_incl || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setSelectedCompany(null)}>Cerrar</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Driver Detail Modal */}
        <Dialog open={!!selectedDriver} onOpenChange={() => setSelectedDriver(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedDriver?.driver_name} - {monthNames[selectedMonth - 1]} {selectedYear}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-orange-50 rounded-lg">
                <div>
                  <p className="text-slate-600">Total mes</p>
                  <p className="text-xs text-slate-500">Pagar antes de: {formatDate(driverDueDate)}</p>
                </div>
                <span className="text-2xl font-bold text-orange-700">€{selectedDriver?.total_pagar_vat_incl?.toFixed(2)}</span>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Ref</TableHead><TableHead>Fecha</TableHead><TableHead>Ruta</TableHead><TableHead className="text-right">Pago</TableHead></TableRow></TableHeader>
                <TableBody>
                  {selectedDriver?.jobs?.map(j => (
                    <TableRow key={j.id}>
                      <TableCell className="font-mono text-xs">{j.reference}</TableCell>
                      <TableCell className="text-sm">{j.date}</TableCell>
                      <TableCell className="text-sm truncate max-w-[200px]">{j.pickup_address}... → {j.dropoff_address}...</TableCell>
                      <TableCell className="text-right font-medium">€{(j.total_vat_incl || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setSelectedDriver(null)}>Cerrar</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Payment Modal */}
        <Dialog open={!!confirmModal} onOpenChange={() => { setConfirmModal(null); setPaymentNote(""); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {confirmModal?.action === 'mark' 
                  ? (confirmModal?.type === 'company' ? 'Confirmar Cobro Recibido' : 'Confirmar Pago Realizado')
                  : 'Desmarcar Pago'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                {confirmModal?.action === 'mark' 
                  ? `¿Confirmar ${confirmModal?.type === 'company' ? 'cobro de' : 'pago a'} "${confirmModal?.name}" para ${monthNames[selectedMonth - 1]} ${selectedYear}?`
                  : `¿Desmarcar el ${confirmModal?.type === 'company' ? 'cobro' : 'pago'} de "${confirmModal?.name}"?`}
              </p>
              {confirmModal?.action === 'mark' && (
                <div>
                  <Label>Nota (opcional)</Label>
                  <Input value={paymentNote} onChange={e => setPaymentNote(e.target.value)} placeholder="Ej: Transferencia ref. 12345"/>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setConfirmModal(null); setPaymentNote(""); }}>Cancelar</Button>
              <Button 
                onClick={() => handleMarkPaid(confirmModal?.type, confirmModal?.id, confirmModal?.action === 'mark')}
                style={{ backgroundColor: confirmModal?.action === 'mark' ? '#16A34A' : '#DC2626' }}
              >
                {confirmModal?.action === 'mark' ? 'Confirmar' : 'Desmarcar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Billing Conditions Modal */}
        <Dialog open={showConditions} onOpenChange={setShowConditions}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Condiciones de Facturación</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm text-slate-700">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-blue-800 mb-2">Reglas Generales</p>
                <ul className="list-disc list-inside space-y-1 text-karga-carbon">
                  <li>Facturación mensual basada únicamente en entregas completadas (DELIVERED).</li>
                  <li>Todos los precios mostrados son sin IVA.</li>
                </ul>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="font-medium text-green-800 mb-2">Empresas</p>
                <ul className="list-disc list-inside space-y-1 text-green-700">
                  <li>El total del mes debe abonarse antes del <strong>día 5 del mes siguiente</strong>.</li>
                  <li>Ejemplo: facturación de enero se paga antes del 5 de febrero.</li>
                </ul>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="font-medium text-orange-800 mb-2">Conductores / Autónomos</p>
                <ul className="list-disc list-inside space-y-1 text-orange-700">
                  <li>El pago se realizará antes del <strong>día 10 del mes siguiente</strong>.</li>
                  <li>Se deja margen operativo para fines de semana y festivos.</li>
                </ul>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="font-medium text-red-800 mb-2">Impago</p>
                <ul className="list-disc list-inside space-y-1 text-red-700">
                  <li>En caso de impago, KargaExpress podrá suspender el servicio.</li>
                </ul>
              </div>
              <p className="text-xs text-slate-500 text-center">Legislación y jurisdicción española.</p>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowConditions(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// ==================== ADMIN FINANCE ====================
const AdminFinance = () => {
  const [finance, setFinance] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/finance`).then(r => setFinance(r.data)); }, []);

  return (
    <DashboardLayout role="ADMIN">
      <div className="space-y-6">
        <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Finanzas</h1>
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-orange-500"/>Por Cobrar (Empresas)</CardTitle></CardHeader>
            <CardContent>
              {finance?.receivables_by_company?.length ? (
                <Table>
                  <TableBody>
                    {finance.receivables_by_company.map(r => (
                      <TableRow key={r._id}>
                        <TableCell className="font-medium">{r.company_name}</TableCell>
                        <TableCell className="text-right text-orange-600 font-bold">€{r.total.toFixed(2)}</TableCell>
                        <TableCell className="text-right text-sm text-slate-500">{r.count} envíos</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <p className="text-slate-500 text-center py-4">Sin pendientes</p>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-red-500"/>Por Pagar (Conductores)</CardTitle></CardHeader>
            <CardContent>
              {finance?.payables_by_driver?.length ? (
                <Table>
                  <TableBody>
                    {finance.payables_by_driver.map(p => (
                      <TableRow key={p._id}>
                        <TableCell className="font-medium">{p.driver_name}</TableCell>
                        <TableCell className="text-right text-red-600 font-bold">€{p.total.toFixed(2)}</TableCell>
                        <TableCell className="text-right text-sm text-slate-500">{p.count} envíos</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <p className="text-slate-500 text-center py-4">Sin pendientes</p>}
            </CardContent>
          </Card>
        </div>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Margen Mensual</p>
            <p className="text-3xl font-bold text-green-600">€{(finance?.weekly_margin || 0).toFixed(2)}</p>
            <p className="text-sm text-slate-500">{finance?.weekly_deliveries || 0} entregas</p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

// ==================== COMPANY RECURRING ====================
const CompanyRecurring = () => {
  const [templates, setTemplates] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();
  const navigate = useNavigate();
  
  const [form, setForm] = useState({
    pickup_address: "", pickup_contact_name: "", pickup_phone: "",
    dropoff_address: "", dropoff_contact_name: "", dropoff_phone: "",
    cargo_description: "", notes: "",
    days_of_week: [],
    pickup_time: "09:00",
    end_date: "",
    service_type: "SCHEDULED",
    vehicle_category: "MEDIUM",
    allow_bigger_vehicle: true,
    requires_isothermal: false, requires_freezer: false, requires_liftgate: false
  });
  
  const daysLabels = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
  
  useEffect(() => { loadTemplates(); }, []);
  
  const loadTemplates = async () => {
    try {
      const r = await axios.get(`${API}/recurring-templates`);
      setTemplates(r.data);
    } catch {} finally { setLoading(false); }
  };
  
  const toggleDay = (dayIndex) => {
    setForm(f => ({
      ...f,
      days_of_week: f.days_of_week.includes(dayIndex) 
        ? f.days_of_week.filter(d => d !== dayIndex)
        : [...f.days_of_week, dayIndex].sort()
    }));
  };
  
  const handleCreate = async () => {
    if (!form.pickup_address || !form.dropoff_address) {
      addToast("Direcciones de recogida y entrega son obligatorias", "error");
      return;
    }
    if (form.days_of_week.length === 0) {
      addToast("Selecciona al menos un día de la semana", "error");
      return;
    }
    try {
      await axios.post(`${API}/recurring-templates`, {
        ...form,
        end_date: form.end_date || null
      });
      addToast("Plantilla recurrente creada", "success");
      setShowCreate(false);
      setForm({
        pickup_address: "", pickup_contact_name: "", pickup_phone: "",
        dropoff_address: "", dropoff_contact_name: "", dropoff_phone: "",
        cargo_description: "", notes: "", days_of_week: [], pickup_time: "09:00",
        end_date: "", service_type: "SCHEDULED", vehicle_category: "MEDIUM",
        allow_bigger_vehicle: true, requires_isothermal: false, requires_freezer: false, requires_liftgate: false
      });
      loadTemplates();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al crear plantilla", "error");
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm("¿Cancelar esta plantilla recurrente?")) return;
    try {
      await axios.delete(`${API}/recurring-templates/${id}`);
      addToast("Plantilla cancelada", "success");
      loadTemplates();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    }
  };
  
  const formatDays = (days) => days.map(d => daysLabels[d]).join(", ");
  
  const statusColors = {
    ACTIVE: "bg-green-100 text-green-700",
    PAUSED: "bg-yellow-100 text-yellow-700",
    CANCELLED: "bg-red-100 text-red-700"
  };
  
  return (
    <DashboardLayout role="COMPANY">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Envíos Recurrentes</h1>
          <Button onClick={() => setShowCreate(true)} style={{ backgroundColor: '#B4A798' }}>
            <Plus className="h-4 w-4 mr-2"/>Nueva Plantilla
          </Button>
        </div>
        
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Cargando...</div>
            ) : templates.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <RefreshCw className="h-12 w-12 mx-auto mb-4 text-slate-300"/>
                <p>No tienes plantillas recurrentes</p>
                <p className="text-sm mt-2">Crea una para automatizar tus envíos regulares</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ruta</TableHead>
                    <TableHead>Días</TableHead>
                    <TableHead>Hora</TableHead>
                    <TableHead>Precio Fijo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Próximo</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.filter(t => t.status !== 'CANCELLED').map(t => (
                    <TableRow key={t.id}>
                      <TableCell>
                        <div className="text-sm">
                          <p className="font-medium">{t.pickup_address?.substring(0, 25)}...</p>
                          <p className="text-slate-500">→ {t.dropoff_address?.substring(0, 25)}...</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{formatDays(t.days_of_week)}</TableCell>
                      <TableCell className="text-sm">{t.pickup_time}</TableCell>
                      <TableCell>
                        {t.client_total_vat_incl ? (
                          <div className="text-sm">
                            <p className="font-medium text-karga-dark">€{t.client_total_vat_incl.toFixed(2)}</p>
                            <p className="text-xs text-slate-500">(sin IVA)</p>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Pendiente</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${statusColors[t.status]}`}>
                          {t.status === 'ACTIVE' ? 'Activo' : t.status === 'PAUSED' ? 'Pausado' : 'Cancelado'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">
                        {t.next_generation_date ? new Date(t.next_generation_date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) : '-'}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(t.id)}>
                          <XCircle className="h-4 w-4"/>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        
        {/* Create Modal */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nueva Plantilla Recurrente</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Pickup */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2"><MapPin className="h-4 w-4 text-green-600"/>Recogida</h3>
                <Input placeholder="Dirección de recogida *" value={form.pickup_address} onChange={e => setForm({...form, pickup_address: e.target.value})}/>
                <div className="grid grid-cols-2 gap-4">
                  <Input placeholder="Persona de contacto" value={form.pickup_contact_name} onChange={e => setForm({...form, pickup_contact_name: e.target.value})}/>
                  <Input placeholder="Teléfono" value={form.pickup_phone} onChange={e => setForm({...form, pickup_phone: e.target.value})}/>
                </div>
              </div>
              
              {/* Dropoff */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2"><MapPin className="h-4 w-4 text-red-600"/>Entrega</h3>
                <Input placeholder="Dirección de entrega *" value={form.dropoff_address} onChange={e => setForm({...form, dropoff_address: e.target.value})}/>
                <div className="grid grid-cols-2 gap-4">
                  <Input placeholder="Persona de contacto" value={form.dropoff_contact_name} onChange={e => setForm({...form, dropoff_contact_name: e.target.value})}/>
                  <Input placeholder="Teléfono" value={form.dropoff_phone} onChange={e => setForm({...form, dropoff_phone: e.target.value})}/>
                </div>
              </div>
              
              {/* Schedule */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2"><Calendar className="h-4 w-4"/>Programación</h3>
                <div>
                  <Label className="mb-2 block">Días de la semana *</Label>
                  <div className="flex gap-2 flex-wrap">
                    {daysLabels.map((day, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => toggleDay(idx)}
                        className={`px-3 py-2 rounded border text-sm transition-colors ${
                          form.days_of_week.includes(idx) 
                            ? 'bg-karga-carbon text-white border-blue-600' 
                            : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Hora de recogida *</Label>
                    <Input type="time" value={form.pickup_time} onChange={e => setForm({...form, pickup_time: e.target.value})}/>
                  </div>
                  <div>
                    <Label>Fecha fin (opcional)</Label>
                    <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})}/>
                  </div>
                </div>
              </div>
              
              {/* Cargo & Vehicle */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2"><Truck className="h-4 w-4"/>Mercancía y Vehículo</h3>
                <Textarea placeholder="Descripción de la mercancía" value={form.cargo_description} onChange={e => setForm({...form, cargo_description: e.target.value})} rows={2}/>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo de vehículo</Label>
                    <Select value={form.vehicle_category} onValueChange={v => setForm({...form, vehicle_category: v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SMALL">Furgoneta pequeña</SelectItem>
                        <SelectItem value="MEDIUM">Furgón mediano</SelectItem>
                        <SelectItem value="LARGE">Furgón grande</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tipo de servicio</Label>
                    <Select value={form.service_type} onValueChange={v => setForm({...form, service_type: v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SCHEDULED">Programado</SelectItem>
                        <SelectItem value="URGENT">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={form.requires_isothermal} onCheckedChange={c => setForm({...form, requires_isothermal: c})}/>
                    <Thermometer className="h-4 w-4 text-blue-500"/><span className="text-sm">Isotermo</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={form.requires_freezer} onCheckedChange={c => setForm({...form, requires_freezer: c})}/>
                    <Snowflake className="h-4 w-4 text-cyan-500"/><span className="text-sm">Congelado</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={form.requires_liftgate} onCheckedChange={c => setForm({...form, requires_liftgate: c})}/>
                    <ArrowUp className="h-4 w-4 text-orange-500"/><span className="text-sm">Plataforma elevadora</span>
                  </label>
                </div>
                <Textarea placeholder="Notas adicionales" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2}/>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button onClick={handleCreate} style={{ backgroundColor: '#B4A798' }}>Crear Plantilla</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// ==================== ADMIN RECURRING ====================
const AdminRecurring = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState("");
  const [autoAssign, setAutoAssign] = useState(false);
  const [saving, setSaving] = useState(false);
  const [priceModal, setPriceModal] = useState(null);
  const [priceForm, setPriceForm] = useState({ client_total: "", driver_total: "" });
  const { addToast } = useToast();
  
  const daysLabels = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
  
  useEffect(() => { 
    loadTemplates(); 
    loadDrivers();
  }, []);
  
  const loadTemplates = async () => {
    try {
      const r = await axios.get(`${API}/recurring-templates`);
      setTemplates(r.data);
    } catch {} finally { setLoading(false); }
  };

  const loadDrivers = async () => {
    try {
      const r = await axios.get(`${API}/drivers?page=1&limit=100`);
      setDrivers(r.data.items || r.data);
    } catch {}
  };
  
  // Generate occurrences modal state
  const [generateModal, setGenerateModal] = useState(null);
  const [generateForm, setGenerateForm] = useState({ date_from: "", date_to: "" });
  const [generating, setGenerating] = useState(false);
  
  // Monthly billing modal state
  const [billingModal, setBillingModal] = useState(null);
  const [billingForm, setBillingForm] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, completed_only: true });
  const [billingResult, setBillingResult] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  
  const openPriceModal = (template) => {
    setPriceModal(template);
    setPriceForm({
      client_total: template.client_daily_total_vat_incl?.toString() || template.client_total_vat_incl?.toString() || "",
      driver_total: template.driver_daily_total_vat_incl?.toString() || template.driver_total_vat_incl?.toString() || ""
    });
  };
  
  const handleSetPrice = async () => {
    if (!priceForm.client_total || !priceForm.driver_total) {
      addToast("Ambos precios son obligatorios", "error");
      return;
    }
    const clientTotal = parseFloat(priceForm.client_total);
    const driverTotal = parseFloat(priceForm.driver_total);
    if (driverTotal >= clientTotal) {
      addToast("El pago al conductor debe ser menor que el precio cliente", "error");
      return;
    }
    try {
      setSaving(true);
      await axios.patch(`${API}/recurring-templates/${priceModal.id}/daily-price`, {
        client_daily_total_vat_incl: clientTotal,
        driver_daily_total_vat_incl: driverTotal
      });
      addToast("Precio diario actualizado.", "success");
      setPriceModal(null);
      loadTemplates();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    } finally {
      setSaving(false);
    }
  };
  
  const openGenerateModal = (template) => {
    if (!template.client_daily_total_vat_incl) {
      addToast("Debe fijar el precio diario antes de generar envíos", "error");
      return;
    }
    // Default: from today to end of month
    const today = new Date();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    setGenerateForm({
      date_from: today.toISOString().split('T')[0],
      date_to: lastDay.toISOString().split('T')[0]
    });
    setGenerateModal(template);
  };
  
  const handleGenerateOccurrences = async () => {
    if (!generateForm.date_from || !generateForm.date_to) {
      addToast("Seleccione las fechas", "error");
      return;
    }
    try {
      setGenerating(true);
      const result = await axios.post(`${API}/recurring-templates/generate-occurrences`, {
        template_id: generateModal.id,
        date_from: generateForm.date_from,
        date_to: generateForm.date_to
      });
      addToast(`Generados ${result.data.generated_count} envíos (${result.data.skipped_count} omitidos)`, "success");
      setGenerateModal(null);
      loadTemplates();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al generar", "error");
    } finally {
      setGenerating(false);
    }
  };
  
  const openBillingModal = (template) => {
    setBillingModal(template);
    setBillingResult(null);
    setBillingForm({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, completed_only: true });
  };
  
  const handleGetBilling = async () => {
    try {
      setLoadingBilling(true);
      const result = await axios.post(`${API}/recurring-templates/monthly-billing`, {
        template_id: billingModal.id,
        year: billingForm.year,
        month: billingForm.month,
        count_only_completed: billingForm.completed_only
      });
      setBillingResult(result.data);
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al obtener facturación", "error");
    } finally {
      setLoadingBilling(false);
    }
  };
  
  const handlePause = async (id) => {
    try {
      await axios.post(`${API}/recurring-templates/${id}/pause`);
      addToast("Plantilla pausada", "success");
      loadTemplates();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    }
  };
  
  const handleResume = async (id) => {
    try {
      await axios.post(`${API}/recurring-templates/${id}/resume`);
      addToast("Plantilla reanudada", "success");
      loadTemplates();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm("¿Cancelar esta plantilla recurrente?")) return;
    try {
      await axios.delete(`${API}/recurring-templates/${id}`);
      addToast("Plantilla cancelada", "success");
      loadTemplates();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    }
  };

  const openPreferredDriverModal = (template) => {
    setEditingTemplate(template);
    setSelectedDriver(template.preferred_driver_id || "");
    setAutoAssign(template.auto_assign_preferred || false);
  };

  const savePreferredDriver = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/recurring-templates/${editingTemplate.id}`, {
        preferred_driver_id: selectedDriver || null,
        auto_assign_preferred: selectedDriver ? autoAssign : false
      });
      addToast("Conductor preferido actualizado", "success");
      loadTemplates();
      setEditingTemplate(null);
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    } finally {
      setSaving(false);
    }
  };
  
  const formatDays = (days) => days.map(d => daysLabels[d]).join(", ");
  
  // Applicants modal state (NEW for v2)
  const [applicantsModal, setApplicantsModal] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  
  const statusColors = {
    OPEN: "bg-blue-100 text-karga-carbon",
    ACTIVE: "bg-green-100 text-green-700",
    PAUSED: "bg-yellow-100 text-yellow-700",
    ENDED: "bg-slate-100 text-slate-700",
    CANCELLED: "bg-red-100 text-red-700"
  };
  
  const statusLabels = {
    OPEN: "Abierto",
    ACTIVE: "Activo",
    PAUSED: "Pausado",
    ENDED: "Finalizado",
    CANCELLED: "Cancelado"
  };
  
  // Load applicants for a template
  const loadApplicants = async (template) => {
    setApplicantsModal(template);
    setLoadingApplicants(true);
    try {
      const r = await axios.get(`${API}/recurring/${template.id}/applicants`);
      setApplicants(r.data.applicants || []);
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al cargar aplicantes", "error");
      setApplicants([]);
    } finally {
      setLoadingApplicants(false);
    }
  };
  
  // Assign driver to recurring service
  const handleAssignDriver = async (templateId, driverId) => {
    setAssigning(true);
    try {
      const r = await axios.post(`${API}/recurring/${templateId}/assign`, { driver_id: driverId });
      addToast(`Conductor asignado. ${r.data.instances_created} instancias creadas.`, "success");
      setApplicantsModal(null);
      loadTemplates();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al asignar", "error");
    } finally {
      setAssigning(false);
    }
  };
  
  // Reassign driver
  const handleReassignDriver = async (templateId) => {
    if (!selectedDriver) {
      addToast("Seleccione un conductor", "error");
      return;
    }
    setAssigning(true);
    try {
      const r = await axios.post(`${API}/recurring/${templateId}/reassign`, { driver_id: selectedDriver });
      addToast(`Conductor reasignado. ${r.data.updated_instances} instancias actualizadas.`, "success");
      setEditingTemplate(null);
      loadTemplates();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al reasignar", "error");
    } finally {
      setAssigning(false);
    }
  };
  
  // Finalize recurring service
  const handleFinalize = async (templateId) => {
    if (!window.confirm("¿Finalizar este servicio recurrente? Se cancelarán todos los envíos futuros pendientes.")) return;
    setFinalizing(true);
    try {
      const r = await axios.post(`${API}/recurring/${templateId}/finalize`);
      addToast(`Servicio finalizado. ${r.data.cancelled_instances} instancias canceladas.`, "success");
      loadTemplates();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al finalizar", "error");
    } finally {
      setFinalizing(false);
    }
  };
  
  // Refresh instances manually
  const handleRefreshInstances = async (templateId) => {
    try {
      const r = await axios.post(`${API}/recurring/${templateId}/refresh-instances`);
      addToast(`${r.data.new_instances_created} nuevas instancias creadas`, "success");
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    }
  };
  
  return (
    <DashboardLayout role="ADMIN">
      <div className="space-y-6">
        <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Plantillas Recurrentes</h1>
        
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Cargando...</div>
            ) : templates.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <RefreshCw className="h-12 w-12 mx-auto mb-4 text-slate-300"/>
                <p>No hay plantillas recurrentes</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Ruta</TableHead>
                    <TableHead>Días</TableHead>
                    <TableHead>Hora</TableHead>
                    <TableHead>Precio/Envío</TableHead>
                    <TableHead>Est. Mensual</TableHead>
                    <TableHead>Conductor</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map(t => (
                    <TableRow key={t.id}>
                      <TableCell className="font-medium">{t.company_name || 'N/A'}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{t.pickup_address?.substring(0, 20)}...</p>
                          <p className="text-slate-500">→ {t.dropoff_address?.substring(0, 20)}...</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{formatDays(t.days_of_week)}</TableCell>
                      <TableCell className="text-sm">{t.pickup_time}</TableCell>
                      <TableCell>
                        {(t.client_daily_total_vat_incl || t.client_total_vat_incl) ? (
                          <div className="text-xs">
                            <p className="font-medium text-karga-dark">€{(t.client_daily_total_vat_incl || t.client_total_vat_incl)?.toFixed(2)}</p>
                            <p className="text-orange-600">D: €{(t.driver_daily_total_vat_incl || t.driver_total_vat_incl)?.toFixed(2)}</p>
                            <p className="text-green-600">M: €{(t.margin_daily_total || t.margin_total)?.toFixed(2)}</p>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => openPriceModal(t)}>
                            <Euro className="h-3 w-3 mr-1"/>Fijar precio
                          </Button>
                        )}
                      </TableCell>
                      {/* Est. Mensual = precio × días/semana × 4 */}
                      <TableCell>
                        {(t.client_daily_total_vat_incl || t.client_total_vat_incl) ? (
                          <div className="text-xs">
                            <p className="font-semibold text-slate-700">
                              €{((t.client_daily_total_vat_incl || t.client_total_vat_incl) * (t.days_of_week?.length || 0) * 4).toFixed(2)}
                            </p>
                            <p className="text-green-600">
                              Margen: €{((t.margin_daily_total || t.margin_total || 0) * (t.days_of_week?.length || 0) * 4).toFixed(2)}
                            </p>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* V2: Show assigned driver for ACTIVE, or applicant count for OPEN */}
                        {t.assigned_driver_name ? (
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium text-green-700">{t.assigned_driver_name}</span>
                            <span className="px-1 py-0.5 bg-green-100 text-green-700 rounded text-xs">Asignado</span>
                          </div>
                        ) : t.status === 'OPEN' ? (
                          <div className="flex items-center gap-1">
                            <span className="text-sm text-karga-dark font-medium">
                              {t.applicants?.length || 0} aplicante{(t.applicants?.length || 0) !== 1 ? 's' : ''}
                            </span>
                            {(t.applicants?.length || 0) > 0 && (
                              <Button size="sm" variant="ghost" onClick={() => loadApplicants(t)} className="h-6 px-2">
                                <Eye className="h-3 w-3"/>
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${statusColors[t.status] || 'bg-slate-100 text-slate-700'}`}>
                          {statusLabels[t.status] || t.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {/* V2: View applicants for OPEN services */}
                          {t.status === 'OPEN' && (t.applicants?.length || 0) > 0 && (
                            <Button size="sm" onClick={() => loadApplicants(t)} style={{ backgroundColor: '#B4A798' }} title="Ver aplicantes">
                              <Users className="h-3 w-3 mr-1"/>{t.applicants?.length}
                            </Button>
                          )}
                          {/* V2: Refresh instances for ACTIVE */}
                          {t.status === 'ACTIVE' && (
                            <Button size="sm" variant="outline" onClick={() => handleRefreshInstances(t.id)} title="Generar próximos 30 días">
                              <RefreshCw className="h-3 w-3 mr-1"/>Generar
                            </Button>
                          )}
                          {/* Monthly Billing */}
                          <Button variant="outline" size="sm" onClick={() => openBillingModal(t)} title="Ver facturación mensual">
                            <Receipt className="h-3 w-3"/>
                          </Button>
                          {/* Set Price - only for OPEN or ACTIVE without price */}
                          {(t.status === 'OPEN' || t.status === 'ACTIVE') && (
                            <Button variant="outline" size="sm" onClick={() => openPriceModal(t)} title="Editar precio diario">
                              <Euro className="h-3 w-3"/>
                            </Button>
                          )}
                          {/* V2: Reassign Driver - for ACTIVE */}
                          {t.status === 'ACTIVE' && (
                            <Button variant="ghost" size="sm" onClick={() => openPreferredDriverModal(t)} title="Reasignar conductor">
                              <UserCheck className="h-4 w-4 text-karga-dark"/>
                            </Button>
                          )}
                          {/* Pause/Resume */}
                          {t.status === 'ACTIVE' && (
                            <Button variant="ghost" size="sm" onClick={() => handlePause(t.id)} title="Pausar">
                              <Pause className="h-4 w-4 text-yellow-600"/>
                            </Button>
                          )}
                          {t.status === 'PAUSED' && (
                            <Button variant="ghost" size="sm" onClick={() => handleResume(t.id)} title="Reanudar">
                              <Play className="h-4 w-4 text-green-600"/>
                            </Button>
                          )}
                          {/* V2: Finalize - for ACTIVE or PAUSED */}
                          {(t.status === 'ACTIVE' || t.status === 'PAUSED') && (
                            <Button variant="ghost" size="sm" onClick={() => handleFinalize(t.id)} disabled={finalizing} title="Finalizar servicio">
                              <StopCircle className="h-4 w-4 text-slate-600"/>
                            </Button>
                          )}
                          {/* Cancel - for OPEN */}
                          {t.status === 'OPEN' && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} title="Cancelar">
                              <XCircle className="h-4 w-4 text-red-600"/>
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Reassign Driver Modal (V2) */}
        <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reasignar Conductor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">
                  {editingTemplate?.pickup_address?.substring(0, 30)}... → {editingTemplate?.dropoff_address?.substring(0, 30)}...
                </p>
                {editingTemplate?.assigned_driver_name && (
                  <p className="text-sm mt-2">
                    <span className="text-slate-500">Conductor actual:</span>{' '}
                    <span className="font-medium text-green-700">{editingTemplate.assigned_driver_name}</span>
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Nuevo Conductor</Label>
                <Select value={selectedDriver || "none"} onValueChange={v => setSelectedDriver(v === "none" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar conductor"/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar conductor</SelectItem>
                    {drivers.filter(d => d.profile?.approval_status === 'APPROVED' && d.id !== editingTemplate?.assigned_driver_id).map(d => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name} - {d.profile?.vehicle_category || 'N/A'}
                        {d.has_payment_info && " ✓ IBAN"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                <AlertCircle className="h-4 w-4 inline mr-1"/>
                Al reasignar, todos los envíos futuros pendientes serán actualizados al nuevo conductor.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingTemplate(null)}>Cancelar</Button>
              <LoadingButton 
                onClick={() => handleReassignDriver(editingTemplate?.id)} 
                loading={assigning} 
                disabled={!selectedDriver || selectedDriver === "none"}
                style={{ backgroundColor: '#B4A798' }}
              >
                Reasignar Conductor
              </LoadingButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Applicants Modal (V2) */}
        <Dialog open={!!applicantsModal} onOpenChange={() => setApplicantsModal(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Aplicantes para Servicio Recurrente</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium">{applicantsModal?.company_name}</p>
                <p className="text-slate-500">{applicantsModal?.pickup_address} → {applicantsModal?.dropoff_address}</p>
                <p className="text-slate-500 mt-1">Días: {applicantsModal?.days_of_week?.map(d => daysLabels[d]).join(", ")} • Hora: {applicantsModal?.pickup_time}</p>
                {applicantsModal?.client_daily_total_vat_incl && (
                  <p className="text-green-600 font-medium mt-1">€{applicantsModal.client_daily_total_vat_incl.toFixed(2)}/día</p>
                )}
              </div>
              
              {!applicantsModal?.client_daily_total_vat_incl && (
                <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                  <AlertCircle className="h-4 w-4 inline mr-1"/>
                  Debe fijar el precio diario antes de asignar un conductor.
                </div>
              )}
              
              {loadingApplicants ? (
                <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto"/></div>
              ) : applicants.length === 0 ? (
                <div className="p-8 text-center text-slate-500">No hay aplicantes todavía</div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {applicants.map((app) => (
                    <div key={app.driver_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                      <div className="flex-1">
                        <p className="font-medium">{app.driver_name}</p>
                        <div className="flex flex-wrap gap-2 mt-1 text-xs text-slate-600">
                          {app.vehicle_category && <span className="px-2 py-0.5 bg-slate-100 rounded">{app.vehicle_category}</span>}
                          {app.license_plate && <span className="px-2 py-0.5 bg-slate-100 rounded">{app.license_plate}</span>}
                          {app.has_isothermal && <span className="px-2 py-0.5 bg-blue-100 text-karga-carbon rounded">Isotermo</span>}
                          {app.has_freezer && <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded">Congelador</span>}
                          {app.has_liftgate && <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded">Plataforma</span>}
                          {app.has_payment_info ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">✓ IBAN</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">⚠ Sin IBAN</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          Aplicó: {new Date(app.applied_at).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                      <LoadingButton
                        size="sm"
                        onClick={() => handleAssignDriver(applicantsModal.id, app.driver_id)}
                        loading={assigning}
                        disabled={!applicantsModal?.client_daily_total_vat_incl}
                        style={{ backgroundColor: '#B4A798' }}
                      >
                        <Check className="h-4 w-4 mr-1"/>Asignar
                      </LoadingButton>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApplicantsModal(null)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Price Modal for Recurring Templates */}
        <Dialog open={!!priceModal} onOpenChange={() => setPriceModal(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Fijar Precio de Contrato</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium">{priceModal?.company_name}</p>
                <p className="text-slate-500">{priceModal?.pickup_address?.substring(0, 40)}... → {priceModal?.dropoff_address?.substring(0, 40)}...</p>
              </div>
              <p className="text-sm text-slate-600">
                Este precio se aplicará automáticamente a todos los envíos futuros generados desde esta plantilla. No requerirá aceptación por parte de la empresa.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Precio Cliente (€, sin IVA)</Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={priceForm.client_total} 
                    onChange={e => setPriceForm({...priceForm, client_total: e.target.value})}
                    placeholder="100.00"
                  />
                </div>
                <div>
                  <Label>Pago Conductor (€, sin IVA)</Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={priceForm.driver_total} 
                    onChange={e => setPriceForm({...priceForm, driver_total: e.target.value})}
                    placeholder="70.00"
                  />
                </div>
              </div>
              {priceForm.client_total && priceForm.driver_total && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800">
                    Margen (sin IVA): <strong>€{(parseFloat(priceForm.client_total || 0) - parseFloat(priceForm.driver_total || 0)).toFixed(2)}</strong>
                  </p>
                </div>
              )}
              <div className="p-2 bg-yellow-50 rounded text-xs text-yellow-800">
                <AlertCircle className="h-3 w-3 inline mr-1"/>
                Los cambios solo aplican a envíos FUTUROS. Los envíos ya generados mantienen su precio.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPriceModal(null)}>Cancelar</Button>
              <LoadingButton onClick={handleSetPrice} loading={saving} style={{ backgroundColor: '#B4A798' }}>
                Guardar Precio Fijo
              </LoadingButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Generate Occurrences Modal */}
        <Dialog open={!!generateModal} onOpenChange={() => setGenerateModal(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generar Envíos</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium">{generateModal?.company_name}</p>
                <p className="text-slate-500">{generateModal?.pickup_address?.substring(0, 40)}... → {generateModal?.dropoff_address?.substring(0, 40)}...</p>
                <p className="text-karga-dark font-medium mt-1">€{generateModal?.client_daily_total_vat_incl?.toFixed(2)}/día</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <Calendar className="h-4 w-4 inline mr-1"/>
                  Se generará un envío por cada día laborable dentro del rango seleccionado.
                </p>
                <p className="text-xs text-karga-dark mt-1">
                  Días activos: {generateModal?.days_of_week?.map(d => daysLabels[d]).join(", ")}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha Inicio</Label>
                  <Input 
                    type="date" 
                    value={generateForm.date_from} 
                    onChange={e => setGenerateForm({...generateForm, date_from: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Fecha Fin</Label>
                  <Input 
                    type="date" 
                    value={generateForm.date_to} 
                    onChange={e => setGenerateForm({...generateForm, date_to: e.target.value})}
                  />
                </div>
              </div>
              {generateModal?.excluded_dates?.length > 0 && (
                <div className="p-2 bg-yellow-50 rounded text-xs text-yellow-800">
                  <AlertCircle className="h-3 w-3 inline mr-1"/>
                  Fechas excluidas: {generateModal.excluded_dates.slice(0, 5).join(", ")}{generateModal.excluded_dates.length > 5 && "..."}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setGenerateModal(null)}>Cancelar</Button>
              <LoadingButton onClick={handleGenerateOccurrences} loading={generating} style={{ backgroundColor: '#B4A798' }}>
                <Calendar className="h-4 w-4 mr-2"/>Generar Envíos
              </LoadingButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Monthly Billing Modal */}
        <Dialog open={!!billingModal} onOpenChange={() => { setBillingModal(null); setBillingResult(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Facturación Mensual</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium">{billingModal?.company_name}</p>
                <p className="text-slate-500">{billingModal?.pickup_address?.substring(0, 40)}... → {billingModal?.dropoff_address?.substring(0, 40)}...</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Año</Label>
                  <Input 
                    type="number" 
                    min="2024" 
                    max="2030" 
                    value={billingForm.year} 
                    onChange={e => setBillingForm({...billingForm, year: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Mes</Label>
                  <Select value={billingForm.month.toString()} onValueChange={v => setBillingForm({...billingForm, month: parseInt(v)})}>
                    <SelectTrigger>
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                      {["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"].map((m, i) => (
                        <SelectItem key={i} value={(i+1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="completed-only" 
                  checked={billingForm.completed_only} 
                  onCheckedChange={v => setBillingForm({...billingForm, completed_only: v})}
                />
                <Label htmlFor="completed-only" className="text-sm cursor-pointer">
                  Solo contar envíos completados (DELIVERED)
                </Label>
              </div>
              
              {!billingResult && (
                <LoadingButton onClick={handleGetBilling} loading={loadingBilling} className="w-full" style={{ backgroundColor: '#B4A798' }}>
                  <Receipt className="h-4 w-4 mr-2"/>Calcular Facturación
                </LoadingButton>
              )}
              
              {billingResult && (
                <div className="space-y-3 pt-2 border-t">
                  <h4 className="font-semibold text-lg">
                    Resumen: {billingResult.period}
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-2 bg-slate-100 rounded">
                      <p className="text-slate-500">Total Generados</p>
                      <p className="font-bold text-lg">{billingResult.counts.total_generated}</p>
                    </div>
                    <div className="p-2 bg-green-100 rounded">
                      <p className="text-slate-500">Completados</p>
                      <p className="font-bold text-lg text-green-700">{billingResult.counts.completed}</p>
                    </div>
                    <div className="p-2 bg-red-100 rounded">
                      <p className="text-slate-500">Cancelados</p>
                      <p className="font-bold text-lg text-red-700">{billingResult.counts.cancelled}</p>
                    </div>
                    <div className="p-2 bg-blue-100 rounded">
                      <p className="text-slate-500">Facturables</p>
                      <p className="font-bold text-lg text-karga-carbon">{billingResult.counts.billable}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Total Cliente (sin IVA)</span>
                      <span className="font-bold text-xl text-karga-carbon">€{billingResult.totals.client_total_vat_incl?.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Total Conductor (sin IVA)</span>
                      <span className="font-bold text-xl text-orange-700">€{billingResult.totals.driver_total_vat_incl?.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 font-medium">Margen Karga (sin IVA)</span>
                      <span className="font-bold text-xl text-green-700">€{billingResult.totals.margin_total?.toFixed(2)}</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 text-center">
                    Modo: {billingResult.billing_mode === 'completed_only' ? 'Solo completados' : 'Todos excepto cancelados'}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setBillingModal(null); setBillingResult(null); }}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// ==================== DRIVER DASHBOARD ====================
const DriverDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();
  const { addToast } = useToast();

  useEffect(() => {
    axios.get(`${API}/drivers/me/summary`).then(r => setSummary(r.data));
    axios.get(`${API}/drivers/me/profile`).then(r => setProfile(r.data));
  }, []);

  const toggleAvailability = async () => {
    // Don't allow if not ACTIVE
    if (profile?.approval_status !== 'ACTIVE' && profile?.approval_status !== 'APPROVED') {
      addToast("Tu cuenta no está activa. Espera aprobación del administrador.", "error");
      return;
    }
    try {
      const r = await axios.patch(`${API}/drivers/me/availability`, { is_available: !profile.is_available });
      setProfile({...profile, is_available: r.data.is_available});
      addToast(r.data.is_available ? "Ahora estás disponible" : "Has desactivado disponibilidad", "success");
    } catch { addToast("Error", "error"); }
  };

  const isPending = profile?.approval_status === 'PENDING' || profile?.approval_status === 'PENDING_APPROVAL';
  const isBlocked = profile?.approval_status === 'BLOCKED' || profile?.approval_status === 'REJECTED';
  const isActive = profile?.approval_status === 'ACTIVE' || profile?.approval_status === 'APPROVED';

  return (
    <DashboardLayout role="DRIVER">
      <div className="space-y-6">
        {/* Pending/Blocked Banner */}
        {isPending && (
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Clock className="h-6 w-6 text-yellow-600"/>
              <div>
                <p className="font-medium text-yellow-800">Cuenta pendiente de activación</p>
                <p className="text-sm text-yellow-700">Tu solicitud está siendo revisada. Te notificaremos cuando puedas empezar a trabajar.</p>
              </div>
            </CardContent>
          </Card>
        )}
        {isBlocked && (
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4 flex items-center gap-3">
              <XCircle className="h-6 w-6 text-red-600"/>
              <div>
                <p className="font-medium text-red-800">Cuenta bloqueada</p>
                <p className="text-sm text-red-700">Tu cuenta ha sido bloqueada. Contacta con soporte para más información.</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Panel de Conductor</h1>
          {isActive && (
            <Button onClick={toggleAvailability} variant={profile?.is_available ? "default" : "outline"}
              style={profile?.is_available ? { backgroundColor: '#16A34A' } : {}}>
              {profile?.is_available ? "✓ Disponible" : "No Disponible"}
            </Button>
          )}
        </div>

        {isActive && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Completados Hoy</p>
                  <p className="text-2xl font-bold">{summary?.today_completed || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Ganado Hoy</p>
                  <p className="text-2xl font-bold text-green-600">€{(summary?.today_earnings || 0).toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Trabajos Activos</p>
                  <p className="text-2xl font-bold">{summary?.pending_jobs || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Por Cobrar</p>
                  <p className="text-2xl font-bold text-orange-600">€{(summary?.pending_payout || 0).toFixed(2)}</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate("/driver/available")}>
                <CardContent className="p-6 text-center">
                  <Inbox className="h-12 w-12 mx-auto mb-4 text-purple-600"/>
                  <h3 className="font-semibold text-lg">Trabajos Disponibles</h3>
                  <p className="text-sm text-slate-500">Ver trabajos que puedes aceptar</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate("/driver/jobs")}>
                <CardContent className="p-6 text-center">
                  <Package className="h-12 w-12 mx-auto mb-4 text-karga-dark"/>
                  <h3 className="font-semibold text-lg">Mis Trabajos</h3>
                  <p className="text-sm text-slate-500">Trabajos asignados y activos</p>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

// ==================== DRIVER AVAILABLE JOBS ====================
const DriverAvailableJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applyingId, setApplyingId] = useState(null);
  const { addToast } = useToast();
  
  // V2: Open recurring services
  const [recurringServices, setRecurringServices] = useState([]);
  const [loadingRecurring, setLoadingRecurring] = useState(true);
  const [applyingRecurringId, setApplyingRecurringId] = useState(null);
  
  const daysLabels = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/drivers/available-jobs`);
      const data = r.data?.items || r.data || [];
      setJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[DriverAvailableJobs] Error:', err);
      setError(err.userMessage || 'Error al cargar los trabajos');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // V2: Load open recurring services
  const loadRecurringServices = useCallback(async () => {
    setLoadingRecurring(true);
    try {
      const r = await axios.get(`${API}/recurring/open`);
      setRecurringServices(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      console.error('[DriverAvailableJobs] Recurring error:', err);
      setRecurringServices([]);
    } finally {
      setLoadingRecurring(false);
    }
  }, []);
  
  useEffect(() => { 
    loadJobs(); 
    loadRecurringServices();
  }, [loadJobs, loadRecurringServices]);

  const applyForJob = async (id) => {
    setApplyingId(id);
    try {
      await axios.post(`${API}/shipments/${id}/apply`);
      addToast("Aplicación enviada. Espera aprobación del admin.", "success");
      loadJobs(); // Refresh to show "Applied" state
    } catch (err) { 
      addToast(err.response?.data?.detail || "Error al aplicar", "error"); 
    } finally {
      setApplyingId(null);
    }
  };

  const withdrawApplication = async (id) => {
    setApplyingId(id);
    try {
      await axios.delete(`${API}/shipments/${id}/apply`);
      addToast("Aplicación retirada", "info");
      loadJobs();
    } catch (err) { 
      addToast(err.response?.data?.detail || "Error", "error"); 
    } finally {
      setApplyingId(null);
    }
  };
  
  // V2: Apply for recurring service
  const applyForRecurring = async (templateId) => {
    setApplyingRecurringId(templateId);
    try {
      await axios.post(`${API}/recurring/${templateId}/apply`);
      addToast("Aplicación enviada para servicio recurrente", "success");
      loadRecurringServices();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al aplicar", "error");
    } finally {
      setApplyingRecurringId(null);
    }
  };
  
  // V2: Withdraw recurring application
  const withdrawRecurringApplication = async (templateId) => {
    setApplyingRecurringId(templateId);
    try {
      await axios.delete(`${API}/recurring/${templateId}/apply`);
      addToast("Aplicación retirada", "info");
      loadRecurringServices();
    } catch (err) {
      addToast(err.response?.data?.detail || "Error", "error");
    } finally {
      setApplyingRecurringId(null);
    }
  };

  return (
    <DashboardLayout role="DRIVER">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Trabajos Disponibles</h1>
          <Button size="sm" variant="outline" onClick={() => { loadJobs(); loadRecurringServices(); }} disabled={loading || loadingRecurring}>
            <RefreshCw className={`h-4 w-4 ${(loading || loadingRecurring) ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {/* Error State */}
        {error && (
          <Card className="border-red-200"><CardContent className="p-6 text-center">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-700 mb-4">{error}</p>
            <Button onClick={loadJobs} variant="outline"><RefreshCw className="h-4 w-4 mr-2" />Reintentar</Button>
          </CardContent></Card>
        )}
        
        {/* Info banner */}
        {!error && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-karga-dark flex-shrink-0 mt-0.5"/>
            <div className="text-sm text-blue-800">
              <p className="font-medium">¿Cómo funciona?</p>
              <p>Aplica a los trabajos que te interesen. El administrador revisará las aplicaciones y te asignará si eres seleccionado.</p>
            </div>
          </CardContent>
        </Card>
        )}
        
        {/* V2: Open Recurring Services Section */}
        {recurringServices.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-karga-dark"/>
              Servicios Recurrentes Disponibles
            </h2>
            <p className="text-sm text-slate-500">Estos son contratos de trabajo continuo. Si eres seleccionado, tendrás trabajo asegurado según el horario establecido.</p>
            <div className="space-y-3">
              {recurringServices.map(service => (
                <Card key={service.id} className={`border-blue-200 ${service.has_applied ? 'bg-green-50/30 border-green-300' : 'bg-blue-50/30'}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-blue-100 text-karga-carbon rounded text-xs font-medium">
                            <RefreshCw className="h-3 w-3 inline mr-1"/>RECURRENTE
                          </span>
                          <VehicleBadge category={service.vehicle_category}/>
                          <RequirementBadges isothermal={service.requires_isothermal} freezer={service.requires_freezer} liftgate={service.requires_liftgate}/>
                        </div>
                        <p className="font-medium">{service.pickup_address}</p>
                        <p className="text-sm text-slate-500">→ {service.dropoff_address}</p>
                        <div className="flex flex-wrap gap-2 mt-2 text-xs text-slate-600">
                          <span className="px-2 py-1 bg-slate-100 rounded">
                            <Calendar className="h-3 w-3 inline mr-1"/>
                            {service.days_of_week?.map(d => daysLabels[d]).join(", ")}
                          </span>
                          <span className="px-2 py-1 bg-slate-100 rounded">
                            <Clock className="h-3 w-3 inline mr-1"/>
                            {service.pickup_time}
                          </span>
                          {service.applicant_count > 0 && (
                            <span className="px-2 py-1 bg-slate-100 rounded">
                              {service.applicant_count} aplicante{service.applicant_count !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-2">{service.company_name}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Pago por día</p>
                          {service.driver_daily_total_vat_incl ? (
                            <>
                              <p className="text-2xl font-bold text-green-600">€{service.driver_daily_total_vat_incl.toFixed(2)}/día</p>
                              <p className="text-xs text-slate-500">(sin IVA)</p>
                            </>
                          ) : (
                            <p className="text-lg font-medium text-slate-400">Por definir</p>
                          )}
                        </div>
                        {service.has_applied ? (
                          <div className="flex flex-col items-end gap-2">
                            <span className="flex items-center gap-1 text-green-600 font-medium">
                              <CheckCircle className="h-4 w-4"/>
                              Aplicación enviada
                            </span>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => withdrawRecurringApplication(service.id)}
                              disabled={applyingRecurringId === service.id}
                            >
                              {applyingRecurringId === service.id ? <Loader2 className="h-4 w-4 animate-spin"/> : "Retirar aplicación"}
                            </Button>
                          </div>
                        ) : (
                          <LoadingButton 
                            onClick={() => applyForRecurring(service.id)} 
                            loading={applyingRecurringId === service.id}
                            style={{ backgroundColor: '#B4A798' }}
                          >
                            <Send className="h-4 w-4 mr-1"/>Aplicar
                          </LoadingButton>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        
        {/* One-off jobs section */}
        <div className="space-y-3">
          {recurringServices.length > 0 && (
            <h2 className="text-lg font-semibold">Trabajos Puntuales</h2>
          )}
          
          {!error && loading && jobs.length === 0 ? (
            <Card><CardContent className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400"/></CardContent></Card>
          ) : !error && jobs.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500">No hay trabajos puntuales disponibles ahora</CardContent></Card>
          ) : !error && (
            <div className="space-y-4">
              {(Array.isArray(jobs) ? jobs : []).map(j => (
                <Card key={j.id} className={j.has_applied ? "border-green-200 bg-green-50/30" : ""}>
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <VehicleBadge category={j.vehicle_category}/>
                          <RequirementBadges isothermal={j.requires_isothermal} freezer={j.requires_freezer} liftgate={j.requires_liftgate}/>
                          {j.service_type === "URGENT" && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">URGENTE</span>}
                          {j.applicant_count > 0 && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                              {j.applicant_count} aplicante{j.applicant_count > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <ScheduleInfo shipment={j} showRecurringPattern={true}/>
                        <p className="font-medium mt-1">{j.pickup_address}</p>
                        <p className="text-sm text-slate-500">→ {j.dropoff_address}</p>
                        {j.cargo_description && <p className="text-sm text-slate-600 mt-2">{j.cargo_description}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Pago del servicio</p>
                          {j.mi_pago_total > 0 ? (
                            <>
                              <p className="text-3xl font-bold text-green-600">€{j.mi_pago_total?.toFixed(2)}</p>
                              <p className="text-xs text-slate-500">(sin IVA)</p>
                            </>
                          ) : (
                            <p className="text-lg font-medium text-slate-400">Pendiente</p>
                          )}
                        </div>
                        {j.has_applied ? (
                          <div className="flex flex-col items-end gap-2">
                            <span className="flex items-center gap-1 text-green-600 font-medium">
                              <CheckCircle className="h-4 w-4"/>
                              Aplicación enviada
                            </span>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => withdrawApplication(j.id)}
                              disabled={applyingId === j.id}
                            >
                              {applyingId === j.id ? <Loader2 className="h-4 w-4 animate-spin"/> : "Retirar aplicación"}
                            </Button>
                          </div>
                        ) : (
                          <LoadingButton 
                            onClick={() => applyForJob(j.id)} 
                            loading={applyingId === j.id}
                            style={{ backgroundColor: '#B4A798' }}
                          >
                            <Send className="h-4 w-4 mr-1"/>Aplicar
                          </LoadingButton>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

// ==================== DRIVER MY JOBS (with POD Upload) ====================
const DriverJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [receiverName, setReceiverName] = useState("");
  const [podStatus, setPodStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadType, setUploadType] = useState(null);
  const { addToast } = useToast();
  
  // Firma eliminada en v1.0.1
  
  // Incidence modal state
  const [incidenceModal, setIncidenceModal] = useState(null); // { job: shipment object }
  const [incidenceType, setIncidenceType] = useState("");
  const [incidenceNotes, setIncidenceNotes] = useState("");
  const [incidencePhotos, setIncidencePhotos] = useState([]);
  const [submittingIncidence, setSubmittingIncidence] = useState(false);
  const incidenceFileRef = useRef(null);

  const loadJobs = () => { 
    axios.get(`${API}/shipments`)
      .then(r => setJobs(r.data.items || []))
      .catch(() => setJobs([]));
  };
  useEffect(() => { loadJobs(); }, []);

  const loadPodStatus = async (shipmentId) => {
    try {
      const r = await axios.get(`${API}/shipments/${shipmentId}/pod-status`);
      setPodStatus(r.data);
    } catch { setPodStatus(null); }
  };

  // Incidence handling functions
  const openIncidenceModal = (job) => {
    setIncidenceModal({ job });
    setIncidenceType("");
    setIncidenceNotes("");
    setIncidencePhotos([]);
  };
  
  const closeIncidenceModal = () => {
    setIncidenceModal(null);
    setIncidenceType("");
    setIncidenceNotes("");
    setIncidencePhotos([]);
  };
  
  const handleIncidencePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length + incidencePhotos.length > 3) {
      addToast("Máximo 3 fotos permitidas", "error");
      return;
    }
    const validFiles = files.filter(f => ['image/jpeg', 'image/png', 'image/webp'].includes(f.type));
    if (validFiles.length !== files.length) {
      addToast("Solo se permiten imágenes JPG, PNG o WebP", "error");
    }
    setIncidencePhotos(prev => [...prev, ...validFiles].slice(0, 3));
  };
  
  const removeIncidencePhoto = (idx) => {
    setIncidencePhotos(prev => prev.filter((_, i) => i !== idx));
  };
  
  const submitIncidence = async () => {
    if (!incidenceType) {
      addToast("Seleccione un tipo de incidencia", "error");
      return;
    }
    
    setSubmittingIncidence(true);
    try {
      // First create the incidence
      const res = await axios.post(`${API}/shipments/${incidenceModal.job.id}/incidence`, {
        incidence_type: incidenceType,
        incidence_notes: incidenceNotes || null
      });
      
      // Then upload photos if any
      for (const photo of incidencePhotos) {
        const formData = new FormData();
        formData.append('file', photo);
        await axios.post(`${API}/shipments/${incidenceModal.job.id}/incidence/photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      addToast(res.data.message || "Incidencia registrada. El equipo de KargaExpress la revisará.", "success");
      closeIncidenceModal();
      loadJobs(); // Refresh jobs to show updated status
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al registrar incidencia", "error");
    } finally {
      setSubmittingIncidence(false);
    }
  };

  const handleSelectJob = (job, nextAction) => {
    setSelectedJob({ ...job, nextAction });
    setReceiverName(job.receiver_name || "");
    loadPodStatus(job.id);
  };

  const handleFileUpload = async (e, proofType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    if (!allowedTypes.includes(file.type)) {
      addToast("Tipo de archivo no permitido. Use JPG, PNG o WebP", "error");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      addToast("Archivo demasiado grande. Máximo 10MB", "error");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('proof_type', proofType);

      await axios.post(`${API}/shipments/${selectedJob.id}/upload-proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      addToast("Foto subida correctamente", "success");
      loadPodStatus(selectedJob.id);
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al subir foto", "error");
    } finally {
      setUploading(false);
      setUploadType(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerUpload = async (type) => {
    setUploadType(type);
    
    try {
      // Use Capacitor Camera to open camera directly
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        direction: CameraDirection.Rear,
        saveToGallery: false,
        correctOrientation: true
      });
      
      // Convert URI to File for upload
      if (image.webPath) {
        const response = await fetch(image.webPath);
        const blob = await response.blob();
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        
        // Call the upload handler with the file
        await handleCameraUpload(file, type);
      }
    } catch (error) {
      // User cancelled or camera error
      if (error.message?.includes('cancelled') || error.message?.includes('canceled') || error.message?.includes('User cancelled')) {
        addToast("Foto cancelada", "info");
      } else if (error.message?.includes('Camera is not available')) {
        // Fallback to file input on web/desktop
        fileInputRef.current?.click();
      } else {
        console.error('Camera error:', error);
        // Fallback to file input
        fileInputRef.current?.click();
      }
    }
  };

  // Handler for camera photo upload
  const handleCameraUpload = async (file, proofType) => {
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('proof_type', proofType);

      await axios.post(`${API}/shipments/${selectedJob.id}/upload-proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      addToast("Foto subida correctamente", "success");
      loadPodStatus(selectedJob.id);
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al subir foto", "error");
    } finally {
      setUploading(false);
      setUploadType(null);
    }
  };

  const updateStatus = async (id, action) => {
    try {
      if (action === "deliver") {
        // Firma eliminada en v1.0.1 - Solo se requiere receptor (opcional)
        
        // Get GPS location if available
        let gpsLat = null, gpsLng = null;
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          gpsLat = pos.coords.latitude;
          gpsLng = pos.coords.longitude;
        } catch (e) { console.log("GPS not available"); }
        
        await axios.post(`${API}/shipments/${id}/deliver`, { 
          receiver_name: receiverName || "No especificado",
          gps_lat: gpsLat,
          gps_lng: gpsLng
        });
        addToast("Entrega completada", "success");
      } else if (action === "pickup") {
        // v1.0.2: Pickup ahora pasa automáticamente a IN_TRANSIT
        await axios.post(`${API}/shipments/${id}/pickup`);
        addToast("Recogida completada. En tránsito.", "success");
      } else {
        await axios.post(`${API}/shipments/${id}/${action}`);
        addToast("Estado actualizado", "success");
      }
      setSelectedJob(null);
      setReceiverName("");
      setPodStatus(null);
      loadJobs();
    } catch (err) { 
      // v1.0.2: Mensajes en español, evitar spam de toasts
      const errorMsg = err.response?.data?.detail || "Error al actualizar estado";
      addToast(errorMsg, "error"); 
    }
  };
  
  // Firma eliminada en v1.0.1 - POD válido: foto albarán + foto entrega + receptor (opcional)

  const getNextAction = (status) => {
    // v1.0.2: PICKED_UP ya no existe como estado intermedio
    // Después del pickup, el estado es IN_TRANSIT directamente
    const actions = {
      DRIVER_ASSIGNED: { label: "Confirmar Recogida", action: "pickup", color: "#B4A798" },
      PICKED_UP: { label: "Entregar", action: "deliver", color: "#16A34A" }, // Fallback por si hay datos antiguos
      IN_TRANSIT: { label: "Entregar", action: "deliver", color: "#16A34A" },
    };
    return actions[status];
  };

  const PodUploadButton = ({ type, label, isComplete, icon: Icon }) => (
    <button
      onClick={() => triggerUpload(type)}
      disabled={uploading}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        isComplete 
          ? "bg-green-100 text-green-700 border border-green-300" 
          : "bg-slate-100 text-slate-700 border border-slate-300 hover:bg-slate-200"
      }`}
    >
      {isComplete ? <CheckCircle className="h-4 w-4"/> : <Icon className="h-4 w-4"/>}
      {label}
      {isComplete && <span className="text-xs">✓</span>}
    </button>
  );

  return (
    <DashboardLayout role="DRIVER">
      <div className="space-y-6">
        <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Mis Trabajos</h1>
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic"
          className="hidden"
          onChange={(e) => uploadType && handleFileUpload(e, uploadType)}
        />

        {jobs.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-slate-500">No tienes trabajos asignados</CardContent></Card>
        ) : (
          <div className="space-y-4">
            {jobs.map(j => {
              const nextAction = getNextAction(j.status);
              const isActiveJob = ['DRIVER_ASSIGNED', 'PICKED_UP', 'IN_TRANSIT'].includes(j.status);
              return (
                <Card key={j.id} className={j.has_incidence ? "border-red-300" : ""}>
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <StatusPill status={j.status} role="DRIVER"/>
                          {/* Pago del servicio - siempre visible */}
                          <div className="flex items-center gap-1">
                            <span className="text-green-600 font-bold text-lg">€{(j.mi_pago_total || j.driver_total_vat_incl || j.price)?.toFixed(2)}</span>
                            <span className="text-xs text-slate-500">(sin IVA)</span>
                          </div>
                          {j.has_incidence && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs flex items-center gap-1">
                              <AlertCircle className="h-3 w-3"/>Incidencia
                            </span>
                          )}
                        </div>
                        {/* Fecha y franja horaria */}
                        <ScheduleInfo shipment={j}/>
                        <p className="font-medium mt-1">{j.pickup_address}</p>
                        <p className="text-sm text-slate-500">→ {j.dropoff_address}</p>
                        {j.pickup_contact_name && <p className="text-xs text-slate-400 mt-1">Recogida: {j.pickup_contact_name} {j.pickup_phone}</p>}
                        {j.dropoff_contact_name && <p className="text-xs text-slate-400">Entrega: {j.dropoff_contact_name} {j.dropoff_phone}</p>}
                        {/* Indicador de servicio recurrente */}
                        {j.service_type === 'SCHEDULED' && (
                          <p className="text-xs text-karga-dark mt-1 flex items-center gap-1">
                            <RefreshCw className="h-3 w-3"/>Servicio recurrente - precio fijo acordado
                          </p>
                        )}
                        {/* POD indicators */}
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {j.has_pickup_proof && <span className="text-xs text-green-600">✓ Foto recogida</span>}
                          {j.has_delivery_proof && <span className="text-xs text-green-600">✓ Foto entrega</span>}
                          {j.has_delivery_note && <span className="text-xs text-green-600">✓ Albarán</span>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        {nextAction && (
                          <Button onClick={() => handleSelectJob(j, nextAction)} style={{ backgroundColor: nextAction.color }}>
                            {nextAction.label}
                          </Button>
                        )}
                        {/* v1.0.2: Report Incidence button - DESACTIVADO */}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Action Dialog with POD Upload */}
        <Dialog open={!!selectedJob} onOpenChange={() => { setSelectedJob(null); setPodStatus(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{selectedJob?.nextAction?.label}</DialogTitle></DialogHeader>
            {selectedJob && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <p className="font-medium">{selectedJob.pickup_address}</p>
                  <p className="text-slate-500">→ {selectedJob.dropoff_address}</p>
                </div>
                
                {/* Pago del servicio - siempre visible */}
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700">Pago del servicio</p>
                  <p className="text-2xl font-bold text-green-700">€{(selectedJob.mi_pago_total || selectedJob.driver_total_vat_incl || selectedJob.price)?.toFixed(2)}</p>
                  <p className="text-xs text-green-600">(sin IVA)</p>
                </div>

                {/* POD Section for Pickup */}
                {selectedJob.nextAction?.action === "pickup" && (
                  <div className="space-y-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-800 mb-2">Foto de Recogida (obligatoria)</p>
                      <PodUploadButton 
                        type="PICKUP_PHOTO" 
                        label="Subir foto de recogida" 
                        isComplete={podStatus?.pickup_photo} 
                        icon={CameraIcon}
                      />
                    </div>
                    {!podStatus?.pickup_photo && (
                      <div className="p-2 bg-yellow-50 rounded text-xs text-yellow-800">
                        <AlertCircle className="h-3 w-3 inline mr-1"/>
                        Debe subir la foto de recogida antes de marcar como recogido
                      </div>
                    )}
                  </div>
                )}

                {/* POD Section for Delivery */}
                {selectedJob.nextAction?.action === "deliver" && (
                  <div className="space-y-3">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-sm font-medium text-green-800 mb-3">Pruebas de Entrega (obligatorias: 3 fotos)</p>
                      <div className="flex flex-wrap gap-2">
                        <PodUploadButton 
                          type="DELIVERY_PHOTO" 
                          label="Foto entrega" 
                          isComplete={podStatus?.delivery_photo} 
                          icon={CameraIcon}
                        />
                        <PodUploadButton 
                          type="DELIVERY_NOTE" 
                          label="Foto albarán" 
                          isComplete={podStatus?.delivery_note} 
                          icon={FileText}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Nombre del receptor (opcional)</Label>
                      <Input 
                        value={receiverName} 
                        onChange={e => setReceiverName(e.target.value)} 
                        placeholder="Nombre de quien recibe la mercancía"
                      />
                    </div>
                    
                    {/* POD Status Summary - Firma eliminada en v1.0.1 */}
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs font-medium text-slate-600 mb-2">Estado POD:</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className={podStatus?.pickup_photo ? "text-green-600" : "text-red-600"}>
                          {podStatus?.pickup_photo ? "✓" : "✗"} Foto recogida
                        </div>
                        <div className={podStatus?.delivery_photo ? "text-green-600" : "text-red-600"}>
                          {podStatus?.delivery_photo ? "✓" : "✗"} Foto entrega
                        </div>
                        <div className={podStatus?.delivery_note ? "text-green-600" : "text-red-600"}>
                          {podStatus?.delivery_note ? "✓" : "✗"} Foto albarán
                        </div>
                        <div className={receiverName ? "text-green-600" : "text-slate-400"}>
                          {receiverName ? "✓" : "○"} Receptor (opcional)
                        </div>
                      </div>
                    </div>

                    {(!podStatus?.pickup_photo || !podStatus?.delivery_photo || !podStatus?.delivery_note) && (
                      <div className="p-2 bg-yellow-50 rounded text-xs text-yellow-800">
                        <AlertCircle className="h-3 w-3 inline mr-1"/>
                        Debe completar las 3 fotos (recogida, entrega, albarán) para entregar
                      </div>
                    )}
                  </div>
                )}

                {/* v1.0.2: El paso manual de "En tránsito" ha sido eliminado */}

                {uploading && (
                  <div className="p-3 bg-slate-50 rounded-lg text-sm text-blue-800 text-center">
                    Subiendo foto...
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setSelectedJob(null); setPodStatus(null); }}>Cancelar</Button>
              <Button 
                onClick={() => updateStatus(selectedJob.id, selectedJob.nextAction.action)} 
                style={{ backgroundColor: selectedJob?.nextAction?.color }}
                disabled={
                  uploading ||
                  (selectedJob?.nextAction?.action === "pickup" && !podStatus?.pickup_photo) ||
                  (selectedJob?.nextAction?.action === "deliver" && (!podStatus?.pickup_photo || !podStatus?.delivery_photo || !podStatus?.delivery_note))
                }
              >
                Confirmar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Incidence Report Modal */}
        <Dialog open={!!incidenceModal} onOpenChange={() => closeIncidenceModal()}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertCircle className="h-5 w-5"/>
                Reportar Incidencia
              </DialogTitle>
            </DialogHeader>
            {incidenceModal && (
              <div className="space-y-4">
                {/* Shipment info */}
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <p className="font-medium">{incidenceModal.job.pickup_address}</p>
                  <p className="text-slate-500">→ {incidenceModal.job.dropoff_address}</p>
                </div>

                {/* Incidence Type Select */}
                <div>
                  <Label className="text-slate-700">Tipo de incidencia *</Label>
                  <Select value={incidenceType} onValueChange={setIncidenceType}>
                    <SelectTrigger data-testid="incidence-type-select">
                      <SelectValue placeholder="Seleccionar tipo de incidencia"/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DELAY">Retraso</SelectItem>
                      <SelectItem value="NO_ONE_AT_PICKUP">No hay nadie en recogida</SelectItem>
                      <SelectItem value="NO_ONE_AT_DELIVERY">No hay nadie en entrega</SelectItem>
                      <SelectItem value="CARGO_ISSUE">Problema con mercancía</SelectItem>
                      <SelectItem value="WRONG_ADDRESS">Dirección incorrecta</SelectItem>
                      <SelectItem value="OTHER">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-slate-700">Descripción (opcional)</Label>
                  <Textarea 
                    value={incidenceNotes}
                    onChange={(e) => setIncidenceNotes(e.target.value)}
                    placeholder="Descripción breve de la incidencia..."
                    rows={2}
                    maxLength={500}
                    data-testid="incidence-notes-input"
                  />
                </div>

                {/* Photos */}
                <div>
                  <Label className="text-slate-700">Fotos (opcional, máx. 3)</Label>
                  <input
                    ref={incidenceFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={handleIncidencePhotoSelect}
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {incidencePhotos.map((photo, idx) => (
                      <div key={idx} className="relative">
                        <img 
                          src={URL.createObjectURL(photo)} 
                          alt={`Incidencia ${idx+1}`} 
                          className="w-16 h-16 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => removeIncidencePhoto(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {incidencePhotos.length < 3 && (
                      <button
                        type="button"
                        onClick={() => incidenceFileRef.current?.click()}
                        className="w-16 h-16 border-2 border-dashed border-slate-300 rounded flex items-center justify-center text-slate-400 hover:border-slate-400 hover:text-slate-500"
                        data-testid="add-incidence-photo-btn"
                      >
                        <CameraIcon className="h-6 w-6"/>
                      </button>
                    )}
                  </div>
                </div>

                {submittingIncidence && (
                  <div className="p-3 bg-slate-50 rounded-lg text-sm text-blue-800 text-center">
                    Registrando incidencia...
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={closeIncidenceModal} disabled={submittingIncidence}>
                Cancelar
              </Button>
              <Button 
                onClick={submitIncidence}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={!incidenceType || submittingIncidence}
                data-testid="submit-incidence-btn"
              >
                Enviar incidencia
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// ==================== DRIVER EARNINGS ====================
const DriverEarnings = () => {
  const [summary, setSummary] = useState(null);
  useEffect(() => { axios.get(`${API}/drivers/me/summary`).then(r => setSummary(r.data)); }, []);

  return (
    <DashboardLayout role="DRIVER">
      <div className="space-y-6">
        <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Mis Ganancias</h1>
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-slate-500">Ganado Hoy</p>
              <p className="text-3xl font-bold text-green-600">€{(summary?.today_earnings || 0).toFixed(2)}</p>
              <p className="text-sm text-slate-500 mt-1">{summary?.today_completed || 0} entregas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-slate-500">Pendiente de Pago</p>
              <p className="text-3xl font-bold text-orange-600">€{(summary?.pending_payout || 0).toFixed(2)}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

// ==================== DRIVER PROFILE ====================
// ==================== PUSH NOTIFICATION SETTINGS ====================
const PushNotificationSettings = () => {
  const [pushStatus, setPushStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const { addToast } = useToast();

  const checkPushSupport = () => {
    return 'Notification' in window && 'serviceWorker' in navigator;
  };

  useEffect(() => {
    loadPushStatus();
  }, []);

  const loadPushStatus = async () => {
    try {
      const r = await axios.get(`${API}/push/status`);
      setPushStatus(r.data);
    } catch (err) {
      console.error("Error loading push status:", err);
    } finally {
      setLoading(false);
    }
  };

  const requestPermission = async () => {
    if (!checkPushSupport()) {
      addToast("Tu navegador no soporta notificaciones push", "error");
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  };

  const togglePush = async () => {
    setToggling(true);
    try {
      // If enabling, request permission first
      if (!pushStatus?.push_enabled) {
        const granted = await requestPermission();
        if (!granted) {
          addToast("Debes permitir notificaciones en tu navegador", "error");
          setToggling(false);
          return;
        }
      }

      const r = await axios.post(`${API}/push/toggle`, { 
        enabled: !pushStatus?.push_enabled 
      });
      
      setPushStatus(prev => ({ ...prev, push_enabled: r.data.push_enabled }));
      addToast(
        r.data.push_enabled 
          ? "Notificaciones push activadas" 
          : "Notificaciones push desactivadas", 
        "success"
      );
    } catch (err) {
      addToast(err.response?.data?.detail || "Error al cambiar configuración", "error");
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin"/>
            Cargando configuración...
          </div>
        </CardContent>
      </Card>
    );
  }

  const browserSupported = checkPushSupport();
  const permissionStatus = browserSupported ? Notification.permission : 'unsupported';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5"/>
          Notificaciones Push
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!browserSupported ? (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              Tu navegador no soporta notificaciones push. Usa Chrome, Firefox o Edge para activarlas.
            </p>
          </div>
        ) : permissionStatus === 'denied' ? (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">
              Has bloqueado las notificaciones. Para activarlas, cambia los permisos en la configuración de tu navegador.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Recibir notificaciones push</p>
                <p className="text-sm text-slate-500">
                  Te avisaremos cuando tengas nuevos trabajos, asignaciones y actualizaciones.
                </p>
              </div>
              <Button
                variant={pushStatus?.push_enabled ? "outline" : "default"}
                onClick={togglePush}
                disabled={toggling}
                className="min-w-[100px]"
                style={!pushStatus?.push_enabled ? { backgroundColor: '#B4A798' } : {}}
              >
                {toggling ? (
                  <Loader2 className="h-4 w-4 animate-spin"/>
                ) : pushStatus?.push_enabled ? (
                  "Desactivar"
                ) : (
                  "Activar"
                )}
              </Button>
            </div>

            {pushStatus?.push_enabled && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <CheckCircle className="h-4 w-4"/>
                Notificaciones push activas
              </div>
            )}

            {permissionStatus === 'default' && !pushStatus?.push_enabled && (
              <p className="text-xs text-slate-500">
                Al activar, tu navegador te pedirá permiso para recibir notificaciones.
              </p>
            )}
          </>
        )}

        <div className="border-t pt-4">
          <p className="text-sm font-medium text-slate-700 mb-2">Recibirás notificaciones sobre:</p>
          <ul className="text-sm text-slate-500 space-y-1">
            <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-green-500"/>Nuevos trabajos disponibles</li>
            <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-green-500"/>Cuando te asignen un trabajo</li>
            <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-green-500"/>Actualizaciones de tus envíos</li>
            <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-green-500"/>Confirmaciones de pago</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== DRIVER CALENDAR (v1.0) ====================
const DriverCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayDeliveries, setDayDeliveries] = useState([]);

  // Get month data
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay();

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  const loadDeliveries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get all shipments for this driver
      const r = await axios.get(`${API}/shipments?limit=100`);
      const data = r.data?.items || r.data || [];
      // Filter to only delivered shipments in the current month
      const filtered = (Array.isArray(data) ? data : []).filter(s => {
        if (s.status !== 'DELIVERED') return false;
        const deliveredDate = new Date(s.delivered_at || s.updated_at || s.created_at);
        return deliveredDate.getMonth() === month && deliveredDate.getFullYear() === year;
      });
      setDeliveries(filtered);
    } catch (err) {
      console.error('[DriverCalendar] Error:', err);
      setError(err.userMessage || 'Error al cargar el calendario');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { loadDeliveries(); }, [loadDeliveries]);

  // Group deliveries by day
  const deliveriesByDay = useMemo(() => {
    const byDay = {};
    deliveries.forEach(d => {
      const date = new Date(d.delivered_at || d.updated_at || d.created_at);
      const day = date.getDate();
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(d);
    });
    return byDay;
  }, [deliveries]);

  const handleDayClick = (day) => {
    if (deliveriesByDay[day]) {
      setSelectedDate(day);
      setDayDeliveries(deliveriesByDay[day]);
    }
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  // Generate calendar grid
  const calendarDays = [];
  for (let i = 0; i < startingDay; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="h-12"></div>);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const hasDeliveries = deliveriesByDay[day]?.length > 0;
    const count = deliveriesByDay[day]?.length || 0;
    const isToday = new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;
    
    calendarDays.push(
      <div 
        key={day}
        onClick={() => handleDayClick(day)}
        className={`h-12 flex flex-col items-center justify-center rounded-lg cursor-pointer transition-colors
          ${hasDeliveries ? 'bg-green-100 hover:bg-green-200 border-2 border-green-500' : 'hover:bg-slate-100'}
          ${isToday ? 'ring-2 ring-blue-500' : ''}
          ${selectedDate === day ? 'bg-blue-100' : ''}`}
      >
        <span className={`text-sm ${hasDeliveries ? 'font-bold text-green-700' : 'text-slate-700'}`}>{day}</span>
        {hasDeliveries && <span className="text-xs text-green-600">{count} entrega{count > 1 ? 's' : ''}</span>}
      </div>
    );
  }

  return (
    <DashboardLayout role="DRIVER">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Mi Calendario</h1>
          <Button size="sm" variant="outline" onClick={loadDeliveries} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {error && (
          <Card className="border-red-200"><CardContent className="p-6 text-center">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-700 mb-4">{error}</p>
            <Button onClick={loadDeliveries} variant="outline"><RefreshCw className="h-4 w-4 mr-2" />Reintentar</Button>
          </CardContent></Card>
        )}

        {!error && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <Button variant="ghost" size="sm" onClick={prevMonth}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <CardTitle className="text-lg">{monthNames[month]} {year}</CardTitle>
                <Button variant="ghost" size="sm" onClick={nextMonth}>
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(d => (
                  <div key={d} className="text-center text-xs font-medium text-slate-500 py-2">{d}</div>
                ))}
              </div>
              {/* Calendar grid */}
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
              ) : (
                <div className="grid grid-cols-7 gap-1">{calendarDays}</div>
              )}
              
              {/* Legend */}
              <div className="mt-4 flex gap-4 text-xs text-slate-600">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-100 border-2 border-green-500 rounded"></div>
                  <span>Días trabajados</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 ring-2 ring-blue-500 rounded"></div>
                  <span>Hoy</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Selected day deliveries */}
        {selectedDate && dayDeliveries.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Entregas del día {selectedDate}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dayDeliveries.map(d => (
                  <div key={d.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{d.dropoff_address?.substring(0, 40)}...</p>
                        <p className="text-sm text-slate-500">{d.company_name}</p>
                      </div>
                      <span className="text-green-600 font-bold">€{d.mi_pago_total?.toFixed(2) || d.driver_total_vat_incl?.toFixed(2) || d.driver_payout?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                ))}
                <div className="pt-3 border-t">
                  <p className="text-right font-bold">
                    Total del día: €{dayDeliveries.reduce((sum, d) => sum + (d.mi_pago_total || d.driver_total_vat_incl || d.driver_payout || 0), 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary card */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-green-600">{deliveries.length}</p>
                <p className="text-sm text-slate-500">Entregas este mes</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-karga-dark">
                  €{deliveries.reduce((sum, d) => sum + (d.mi_pago_total || d.driver_total_vat_incl || d.driver_payout || 0), 0).toFixed(2)}
                </p>
                <p className="text-sm text-slate-500">Total ganado</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info for invoicing */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-karga-dark flex-shrink-0 mt-0.5"/>
              <div className="text-sm text-blue-800">
                <p className="font-medium">Referencia para facturación</p>
                <p>Usa este calendario como referencia para tus facturas mensuales. 
                   Los días marcados en verde son días en los que completaste entregas.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

// Sound Notification Settings for Drivers
const SoundNotificationSettings = () => {
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('karga_sound_enabled') !== 'false';
  });
  const [testPlaying, setTestPlaying] = useState(false);
  const audioContextRef = useRef(null);
  const { addToast } = useToast();

  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('karga_sound_enabled', newValue.toString());
    addToast(newValue ? "Sonido de notificaciones activado" : "Sonido de notificaciones desactivado", "success");
  };

  const playTestSound = (type) => {
    setTestPlaying(true);
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      if (type === 'job') {
        oscillator.frequency.setValueAtTime(880, ctx.currentTime);
        oscillator.frequency.setValueAtTime(1174.66, ctx.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
      } else if (type === 'assigned') {
        oscillator.frequency.setValueAtTime(523.25, ctx.currentTime);
        oscillator.frequency.setValueAtTime(659.25, ctx.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(783.99, ctx.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.35);
      } else {
        oscillator.frequency.setValueAtTime(880, ctx.currentTime);
        oscillator.frequency.setValueAtTime(659.25, ctx.currentTime + 0.15);
        gainNode.gain.setValueAtTime(0.35, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
      }
      
      setTimeout(() => setTestPlaying(false), 400);
    } catch (e) {
      addToast("Audio no soportado en este navegador", "error");
      setTestPlaying(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="h-5 w-5 text-karga-dark"/>
          Sonido de Notificaciones
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Activar sonido</p>
            <p className="text-xs text-slate-500">Sonará cuando haya nuevos trabajos o asignaciones</p>
          </div>
          <button
            onClick={toggleSound}
            className={`relative w-12 h-6 rounded-full transition-colors ${soundEnabled ? 'bg-karga-carbon' : 'bg-slate-300'}`}
            data-testid="sound-toggle"
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${soundEnabled ? 'translate-x-7' : 'translate-x-1'}`}/>
          </button>
        </div>

        {soundEnabled && (
          <div className="border-t pt-4 space-y-3">
            <p className="text-sm text-slate-600 font-medium">Probar sonidos:</p>
            <div className="flex flex-wrap gap-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => playTestSound('job')}
                disabled={testPlaying}
                className="text-xs"
              >
                <Truck className="h-3 w-3 mr-1"/>
                Nuevo trabajo
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => playTestSound('assigned')}
                disabled={testPlaying}
                className="text-xs"
              >
                <UserCheck className="h-3 w-3 mr-1"/>
                Asignado
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => playTestSound('alert')}
                disabled={testPlaying}
                className="text-xs"
              >
                <AlertCircle className="h-3 w-3 mr-1"/>
                Alerta
              </Button>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-3 mt-3">
              <p className="text-xs text-slate-600">
                <strong>Eventos con sonido:</strong>
              </p>
              <ul className="text-xs text-slate-500 mt-1 space-y-0.5">
                <li>• Nuevo trabajo disponible (agrupado si hay varios)</li>
                <li>• Trabajo asignado a ti</li>
                <li>• Cancelación o incidencia</li>
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DriverProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const { addToast } = useToast();

  useEffect(() => {
    axios.get(`${API}/drivers/me/profile`).then(r => { setProfile(r.data); setForm(r.data || {}); });
  }, []);

  const handleSave = async () => {
    try {
      const r = await axios.patch(`${API}/drivers/me/profile`, form);
      setProfile(r.data);
      setEditing(false);
      addToast("Perfil actualizado", "success");
    } catch { addToast("Error al guardar", "error"); }
  };
  
  const hasMissingPaymentInfo = !profile?.iban || !profile?.account_holder;

  return (
    <DashboardLayout role="DRIVER">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: '#171717' }}>Mi Perfil</h1>
          {!editing && <Button onClick={() => setEditing(true)} variant="outline">Editar</Button>}
        </div>
        
        {/* Payment Warning */}
        {hasMissingPaymentInfo && !editing && (
          <Card className="border-yellow-300 bg-yellow-50">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5"/>
              <div>
                <p className="font-medium text-yellow-800">Datos de pago incompletos</p>
                <p className="text-sm text-yellow-700 mt-1">Para recibir tus pagos, debes completar tu IBAN y titular de cuenta.</p>
                <Button size="sm" className="mt-2" onClick={() => setEditing(true)} style={{ backgroundColor: '#F59E0B' }}>
                  Completar datos
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        <Card>
          <CardContent className="p-6 space-y-4">
            {editing ? (
              <>
                {/* Vehicle Section */}
                <h3 className="font-medium text-slate-700 border-b pb-2">Vehículo</h3>
                <div>
                  <Label>Tamaño de vehículo</Label>
                  <Select value={form.vehicle_category} onValueChange={v => setForm({...form, vehicle_category: v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SMALL">Pequeño</SelectItem>
                      <SelectItem value="MEDIUM">Mediano</SelectItem>
                      <SelectItem value="LARGE">Grande</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Matrícula</Label><Input value={form.license_plate || ""} onChange={e => setForm({...form, license_plate: e.target.value})}/></div>
                <div className="border-t pt-4">
                  <Label className="mb-3 block">Capacidades del vehículo</Label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Checkbox id="iso" checked={form.is_isothermal} onCheckedChange={c => setForm({...form, is_isothermal: c})}/>
                      <Label htmlFor="iso">Frío (+2°C a +8°C)</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox id="freeze" checked={form.is_freezer} onCheckedChange={c => setForm({...form, is_freezer: c})}/>
                      <Label htmlFor="freeze">Congelación (-18°C)</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox id="lift" checked={form.has_liftgate} onCheckedChange={c => setForm({...form, has_liftgate: c})}/>
                      <Label htmlFor="lift">Plataforma elevadora</Label>
                    </div>
                  </div>
                </div>
                
                {/* Payment Section */}
                <h3 className="font-medium text-slate-700 border-b pb-2 mt-6">Datos de Pago</h3>
                <div>
                  <Label>IBAN</Label>
                  <Input 
                    placeholder="ES00 0000 0000 0000 0000 0000" 
                    value={form.iban || ""} 
                    onChange={e => setForm({...form, iban: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">Cuenta donde recibirás tus pagos</p>
                </div>
                <div>
                  <Label>Titular de la cuenta</Label>
                  <Input 
                    placeholder="Nombre completo del titular" 
                    value={form.account_holder || ""} 
                    onChange={e => setForm({...form, account_holder: e.target.value})}
                  />
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
                  <Button onClick={handleSave} style={{ backgroundColor: '#B4A798' }}>Guardar</Button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <h3 className="font-medium text-slate-700 border-b pb-2">Vehículo</h3>
                <div><span className="text-sm text-slate-500">Vehículo:</span><p className="font-medium"><VehicleBadge category={profile?.vehicle_category}/></p></div>
                <div><span className="text-sm text-slate-500">Matrícula:</span><p className="font-medium">{profile?.license_plate || "-"}</p></div>
                <div><span className="text-sm text-slate-500">Capacidades:</span><div className="mt-1"><RequirementBadges isothermal={profile?.is_isothermal} freezer={profile?.is_freezer} liftgate={profile?.has_liftgate}/></div></div>
                <div><span className="text-sm text-slate-500">Estado:</span><p className="font-medium">{profile?.is_available ? <span className="text-green-600">Disponible</span> : <span className="text-slate-400">No disponible</span>}</p></div>
                
                <h3 className="font-medium text-slate-700 border-b pb-2 mt-6">Datos de Pago</h3>
                <div>
                  <span className="text-sm text-slate-500">IBAN:</span>
                  <p className="font-medium">{profile?.iban ? `****${profile.iban.slice(-4)}` : <span className="text-red-500">No configurado</span>}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Titular:</span>
                  <p className="font-medium">{profile?.account_holder || <span className="text-red-500">No configurado</span>}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Push Notifications Settings */}
        <PushNotificationSettings />
        
        {/* Sound Notification Settings */}
        <SoundNotificationSettings />
      </div>
    </DashboardLayout>
  );
};

// ==================== PASSWORD RECOVERY PAGES ====================
const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al enviar el email");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F2ED' }}>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <KargaLogo size="md" variant="light" />
            </div>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#DCFCE7' }}>
              <CheckCircle className="h-8 w-8 text-green-600"/>
            </div>
            <CardTitle>Revisa tu email</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-600">
              Si el email <strong>{email}</strong> está registrado, recibirás instrucciones para restablecer tu contraseña.
            </p>
            <p className="text-sm text-slate-500">
              El enlace expirará en 30 minutos.
            </p>
            <div className="pt-4 space-y-2">
              <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>
                Volver al inicio de sesión
              </Button>
              <button 
                onClick={() => { setSent(false); setEmail(""); }} 
                className="text-sm hover:underline" 
                style={{ color: '#B4A798' }}
              >
                ¿No recibiste el email? Intentar de nuevo
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F2ED' }}>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <KargaLogo size="md" variant="light" />
          </div>
          <CardTitle>Recuperar contraseña</CardTitle>
          <p className="text-sm text-slate-500 mt-2">
            Introduce tu email y te enviaremos instrucciones para restablecer tu contraseña.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="tu@email.com"
                required 
                data-testid="forgot-email" 
              />
            </div>
            <LoadingButton 
              type="submit" 
              className="w-full" 
              loading={loading}
              style={{ backgroundColor: '#B4A798' }} 
              data-testid="forgot-submit"
            >
              Enviar instrucciones
            </LoadingButton>
          </form>
          <div className="mt-6 text-center">
            <button onClick={() => navigate("/login")} className="text-sm hover:underline" style={{ color: '#B4A798' }}>
              ← Volver al inicio de sesión
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
        confirm_password: confirmPassword
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al restablecer la contraseña");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F2ED' }}>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEE2E2' }}>
              <XCircle className="h-8 w-8 text-red-600"/>
            </div>
            <CardTitle>Enlace inválido</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-600">
              Este enlace de recuperación no es válido o ha expirado.
            </p>
            <Button className="w-full" onClick={() => navigate("/forgot-password")} style={{ backgroundColor: '#B4A798' }}>
              Solicitar nuevo enlace
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F2ED' }}>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <KargaLogo size="md" variant="light" />
            </div>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#DCFCE7' }}>
              <CheckCircle className="h-8 w-8 text-green-600"/>
            </div>
            <CardTitle>¡Contraseña actualizada!</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-600">
              Tu contraseña ha sido actualizada correctamente. Ya puedes iniciar sesión con tu nueva contraseña.
            </p>
            <Button className="w-full" onClick={() => navigate("/login")} style={{ backgroundColor: '#B4A798' }}>
              Iniciar sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F2ED' }}>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <KargaLogo size="md" variant="light" />
          </div>
          <CardTitle>Nueva contraseña</CardTitle>
          <p className="text-sm text-slate-500 mt-2">
            Introduce tu nueva contraseña.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
            <div>
              <Label htmlFor="password">Nueva contraseña</Label>
              <Input 
                id="password" 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                placeholder="Mínimo 6 caracteres"
                required 
                data-testid="reset-password" 
              />
            </div>
            <div>
              <Label htmlFor="confirm">Confirmar contraseña</Label>
              <Input 
                id="confirm" 
                type="password" 
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
                placeholder="Repite la contraseña"
                required 
                data-testid="reset-confirm" 
              />
            </div>
            <LoadingButton 
              type="submit" 
              className="w-full" 
              loading={loading}
              style={{ backgroundColor: '#B4A798' }} 
              data-testid="reset-submit"
            >
              Guardar contraseña
            </LoadingButton>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== ROUTES ====================
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" />;
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/splash" element={<SplashScreen />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/register/company" element={<CompanyRegisterPage />} />
              <Route path="/register/driver" element={<DriverRegisterPage />} />
              
              {/* Legal Pages - Required for Google Play */}
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/legal" element={<LegalNoticePage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              
              {/* Company Routes */}
              <Route path="/company" element={<ProtectedRoute allowedRoles={["COMPANY"]}><CompanyDashboard /></ProtectedRoute>} />
              <Route path="/company/new" element={<ProtectedRoute allowedRoles={["COMPANY"]}><CompanyNewShipment /></ProtectedRoute>} />
              <Route path="/company/recurring" element={<ProtectedRoute allowedRoles={["COMPANY"]}><CompanyRecurring /></ProtectedRoute>} />
              <Route path="/company/quotes" element={<ProtectedRoute allowedRoles={["COMPANY"]}><CompanyQuotes /></ProtectedRoute>} />
              <Route path="/company/shipments" element={<ProtectedRoute allowedRoles={["COMPANY"]}><CompanyShipments /></ProtectedRoute>} />
              <Route path="/company/shipments/:id" element={<ProtectedRoute allowedRoles={["COMPANY"]}><ShipmentDetail role="COMPANY" /></ProtectedRoute>} />
              <Route path="/company/billing" element={<ProtectedRoute allowedRoles={["COMPANY"]}><CompanyBilling /></ProtectedRoute>} />
              <Route path="/company/profile" element={<ProtectedRoute allowedRoles={["COMPANY"]}><CompanyProfile /></ProtectedRoute>} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={<ProtectedRoute allowedRoles={["ADMIN"]}><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/operations" element={<ProtectedRoute allowedRoles={["ADMIN"]}><AdminOperations /></ProtectedRoute>} />
              <Route path="/admin/requests" element={<ProtectedRoute allowedRoles={["ADMIN"]}><AdminRequests /></ProtectedRoute>} />
              <Route path="/admin/shipments" element={<ProtectedRoute allowedRoles={["ADMIN"]}><AdminShipments /></ProtectedRoute>} />
              <Route path="/admin/shipments/:id" element={<ProtectedRoute allowedRoles={["ADMIN"]}><ShipmentDetail role="ADMIN" /></ProtectedRoute>} />
              <Route path="/admin/recurring" element={<ProtectedRoute allowedRoles={["ADMIN"]}><AdminRecurring /></ProtectedRoute>} />
              <Route path="/admin/companies" element={<ProtectedRoute allowedRoles={["ADMIN"]}><AdminCompanies /></ProtectedRoute>} />
              <Route path="/admin/drivers" element={<ProtectedRoute allowedRoles={["ADMIN"]}><AdminDrivers /></ProtectedRoute>} />
              <Route path="/admin/finance" element={<ProtectedRoute allowedRoles={["ADMIN"]}><AdminFinance /></ProtectedRoute>} />
              <Route path="/admin/billing" element={<ProtectedRoute allowedRoles={["ADMIN"]}><AdminBilling /></ProtectedRoute>} />
              
              {/* Driver Routes */}
              <Route path="/driver" element={<ProtectedRoute allowedRoles={["DRIVER"]}><DriverDashboard /></ProtectedRoute>} />
              <Route path="/driver/available" element={<ProtectedRoute allowedRoles={["DRIVER"]}><DriverAvailableJobs /></ProtectedRoute>} />
              <Route path="/driver/jobs" element={<ProtectedRoute allowedRoles={["DRIVER"]}><DriverJobs /></ProtectedRoute>} />
              <Route path="/driver/calendar" element={<ProtectedRoute allowedRoles={["DRIVER"]}><DriverCalendar /></ProtectedRoute>} />
              <Route path="/driver/earnings" element={<ProtectedRoute allowedRoles={["DRIVER"]}><DriverEarnings /></ProtectedRoute>} />
              <Route path="/driver/profile" element={<ProtectedRoute allowedRoles={["DRIVER"]}><DriverProfilePage /></ProtectedRoute>} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
